import {
  Button,
  Select,
  Tag,
  message,
  Spin,
  Form,
  Table,
  InputNumber,
  Dropdown,
  Space,
  Typography,
  Modal,
  Input,
  DatePicker,
  Popover,
  Timeline,
  Badge,
  Popconfirm,
  Descriptions,
  Tooltip,
  Drawer,
} from "antd";
import {
  AddAccess,
  ViewAccess,
  EditAccess,
  CancelAccess,
  ApproveAccess,
  RejectAccess,
} from "../../../components/accessControl/AccessControl";
import { useAccessControl } from "../../../utils/useAccessControl";
import React, {
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import dayjs from "dayjs";
import {
  Alert,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import FormDatePicker from "../../form-components/DatePicker";
import { ApiResponse } from "../../../api/master/axiosIndex";
import { projectsApis } from "../../../api/projects";
import { useSelector, useDispatch } from "react-redux";
import { Icon } from "../../../components/Component";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import Swal from "sweetalert2";
import {
  DownOutlined,
  LoadingOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { RootState } from "../../../store/store";
import SubTaskList from "./SubTaskList";
import ReminderPopper from "./ReminderPopper";
import RecurringFrequencyPopper from "./RecurringFrequencyPopper";
import { tasksApis } from "../../../api/NewTasks";
import { ReactTyped } from "react-typed";
import { BoqLines } from "../activity/ActivityCreation";
import { updateTaskListDetailsModalSlice } from "../../../store/slices/projectManagement/task-list-view/TaskListDetailModalSlice";
import { tagRender } from "../projects/projectCreation";
import TaskDependencies from "../tasks/taskDependenciesAnt";
import { OwnerChangeReason } from "../phases/PhaseFieldDetailsUpdate";
import { getDirtyFieldsOnly } from "../../../utils/use-form/GetDirtyFieldsOnly";
import { CommentsTab } from "../../../components/comments/Comments";
import { IssuesTable } from "../issues/IssueTableView";
import TaskCheckListView from "./TaskCheckListView";
import TaskCheckListCreateModal from "./TaskCheckListCreateModal";
import type { SweetAlertIcon } from "sweetalert2";
import { useLocation } from "react-router-dom";
import { TimeLogTable } from "../timeSheets/TimeLogTable";
import { workApprovalApis } from "../../../api/WorkApproval";
import DocumentsTabWrapper from "../projectDocument/DocumentsTabWrapper";
import TaskStatusTimeLine from "./TaskStatusTimeLine";
import { getColorByLabel } from "../projects/ProjectInformationById";
import TaskActivityStreamLine from "./TaskActivityStreamLine";
import IssueApprovalDrawer from "../issues/IssueApprovalDrawer";
import sitebreakdownMasterApis from "../../../api/master/sitebreakdown";
import contractorsApis from "../../../api/master/contractors";
import {
  ToolMaterialsManager,
  MAX_TOOL_MATERIALS,
  type ToolMaterialItem,
} from "./TaskCreation";
import {
  computeTaskPriceBreakdown,
  DEFAULT_CESS_PERCENT,
  DEFAULT_GST_PERCENT,
} from "../shared/taskPrice";
import TaskPriceBreakdownPanel, {
  rateInclGstTooltip,
} from "../shared/TaskPriceBreakdownPanel";

// Consistent style for label-value pairs with modern card styling
const labelValueStyle = {
  display: "flex",
  alignItems: "flex-start",
  minHeight: "32px",
  marginBottom: "4px",
  gap: "8px",
  fontFamily: '"Roboto", sans-serif',
  width: "100%",
  flexDirection: "column" as const,
};
const labelStyle = {
  width: "100%",
  margin: 0,
  fontWeight: 500,
  color: "#64748b",
  flexShrink: 0,
  fontFamily: '"Roboto", sans-serif',
  fontSize: "12px",
  lineHeight: "1.4",
  display: "flex",
  alignItems: "center",
  minHeight: "auto",
  paddingTop: "0px",
  textTransform: "uppercase" as const,
  letterSpacing: "0.03em",
};
const valueStyle = {
  flex: 1,
  minWidth: 0,
  wordBreak: "break-word" as const,
  whiteSpace: "normal" as const,
  overflowWrap: "break-word" as const,
  fontWeight: 500,
  color: "#1e293b",
  fontFamily: '"Roboto", sans-serif',
  fontSize: "14px",
  lineHeight: "1.5",
  display: "flex",
  alignItems: "center",
  minHeight: "auto",
  paddingTop: "0px",
};

// Edit mode form group styles for better spacing
const editModeFormGroupClassName = "edit-mode-form-group";

const { Text } = Typography;
interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  dataIndex: string;
  title: any;
  inputType: "number" | "text";
  record: any;
  index: number;
  children: React.ReactNode;
  form: any;
  // Optional — only used by the rate-analysis BOQ cells.
  workUomsList?: SelectOption[];
  outputFactor?: number;
}

// Max digits allowed in any numeric input field
const MAX_NUMBER_DIGITS = 10;

// Restrict numeric inputs to digits only (no letters/symbols/emojis), a single
// decimal point, and at most MAX_NUMBER_DIGITS digits.
const handleNumberKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  const controlKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "Escape",
    "Enter",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
  ];
  if (controlKeys.includes(e.key)) return;
  if (e.key === ".") {
    if ((e.currentTarget.value || "").includes(".")) e.preventDefault();
    return;
  }
  if (!/^[0-9]$/.test(e.key)) {
    e.preventDefault();
    return;
  }
  const digitCount = (e.currentTarget.value || "").replace(/\D/g, "").length;
  const hasSelection =
    e.currentTarget.selectionStart !== e.currentTarget.selectionEnd;
  if (digitCount >= MAX_NUMBER_DIGITS && !hasSelection) {
    e.preventDefault();
  }
};

// Keep alphanumeric characters plus - / ( ) _ (strips spaces, other symbols, emojis)
const sanitizeAlphaNumeric = (value: string) =>
  (value || "").replace(/[^a-zA-Z0-9()_\/-]/g, "");

// Isolated live total for the tools add-row. Using useWatch here means only this
// tiny component re-renders as the user types rate/quantity — the parent form
// (and its inputs) don't re-render, so the cursor/focus stays put.
const ToolTotalPreview: React.FC<{ control: any }> = ({ control }) => {
  const quantity = useWatch({ control, name: "toolQuantity" });
  const rate = useWatch({ control, name: "toolRate" });
  const total = (Number(quantity) || 0) * (Number(rate) || 0);
  return (
    <input
      type="text"
      className="form-control"
      readOnly
      disabled
      style={{ height: "38px" }}
      value={total ? total.toFixed(2) : ""}
    />
  );
};

// Live total for the tools rate-analysis add-row (RA qty * RA rate).
const ToolRateAnalysisTotalPreview: React.FC<{ control: any }> = ({
  control,
}) => {
  const quantity = useWatch({ control, name: "toolRateAnalysisQty" });
  const rate = useWatch({ control, name: "toolRateAnalysisRate" });
  const total = (Number(quantity) || 0) * (Number(rate) || 0);
  return (
    <input
      type="text"
      className="form-control"
      readOnly
      disabled
      style={{ height: "38px" }}
      value={total ? total.toFixed(2) : ""}
    />
  );
};

// Isolated serial-number input for the tools add-row. Controlling the value via
// useWatch (instead of methods.watch in the parent render) keeps re-renders local
// so the cursor/focus doesn't jump while typing.
const ToolSerialNumberInput: React.FC<{
  control: any;
  setValue: (name: any, value: any) => void;
  disabled?: boolean;
}> = ({ control, setValue, disabled }) => {
  const value = useWatch({ control, name: "toolSerialNumber" }) || "";
  return (
    <input
      type="text"
      className="form-control"
      placeholder="Enter serial number"
      maxLength={50}
      disabled={disabled}
      style={{ height: "38px" }}
      value={value}
      onChange={(e) =>
        setValue("toolSerialNumber", sanitizeAlphaNumeric(e.target.value))
      }
    />
  );
};

const TaskInformationEditableCell: React.FC<EditableCellProps> = ({
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  form,
  workUomsList,
  outputFactor,
  ...restProps
}) => {
  // Unit-basis Rate Analysis cells for a BOQ material line. RA qty pre-fills the
  // overall Allocate Qty via outputFactor; RA rate pre-fills the overall Rate.
  if (
    dataIndex === "boqRateAnalysisQty" ||
    dataIndex === "boqRateAnalysisRate"
  ) {
    const id = record?.activityBoqId;
    const fieldName = `${dataIndex}_${id}`;
    const initial =
      dataIndex === "boqRateAnalysisQty"
        ? record?.taskBoqRateAnalysisQty
        : record?.taskBoqRateAnalysisRate;
    return (
      <td {...restProps}>
        <Form.Item
          name={fieldName}
          style={{ margin: 0 }}
          initialValue={
            initial !== undefined && initial !== null
              ? Number(initial)
              : undefined
          }
        >
          <InputNumber
            min={0}
            max={9999999999}
            onKeyDown={handleNumberKeyDown}
            style={{ width: "100%" }}
            onChange={(value) => {
              if (dataIndex === "boqRateAnalysisQty") {
                const f = Number(outputFactor) || 0;
                if (f > 0 && value != null) {
                  form.setFieldsValue({
                    [`allocateBOQQuantity_${id}`]: Number(
                      (f * Number(value)).toFixed(2),
                    ),
                  });
                  // Re-validate the auto-filled Allocate Qty so the
                  // availability error surfaces immediately (antd does not
                  // re-validate a programmatically set field on its own).
                  form
                    .validateFields([`allocateBOQQuantity_${id}`])
                    .catch(() => {});
                }
              } else {
                form.setFieldsValue({ [`boqRate_${id}`]: value });
              }
            }}
          />
        </Form.Item>
      </td>
    );
  }
  if (dataIndex === "boqRateAnalysisUomId") {
    const fieldName = `boqRateAnalysisUomId_${record?.activityBoqId}`;
    return (
      <td {...restProps}>
        <Form.Item
          name={fieldName}
          style={{ margin: 0 }}
          initialValue={record?.taskBoqRateAnalysisUomId ?? undefined}
        >
          <Select
            style={{ width: "100%" }}
            placeholder="UOM"
            options={workUomsList}
            showSearch
            optionFilterProp="label"
            allowClear
          />
        </Form.Item>
      </td>
    );
  }
  // Only render input for allocateBOQQuantity column
  if (dataIndex === "allocateBOQQuantity") {
    const fieldName = `${dataIndex}_${record?.activityBoqId}`;
    const initialQuantity =
      record.taskAllocatedQuantity !== undefined &&
      record.taskAllocatedQuantity !== null
        ? Number(record.taskAllocatedQuantity)
        : undefined;
    const availableQuantity =
      record.taskAvailableQuantity ??
      record.availableQuantity ??
      record.allotedQuantity ??
      0;
    // Max user can allocate on edit = the row's existing allocation
    // (which isn't double-counted) + the task's still-available pool.
    // Min = > 0 (cannot give 0).
    const existingAllocated = Number(initialQuantity ?? 0);
    const stillAvailable = Number(availableQuantity ?? 0);
    const maxAllowed = existingAllocated + stillAvailable;
    const isDisabled = stillAvailable === 0;
    return (
      <td {...restProps}>
        <Form.Item
          name={fieldName}
          style={{ margin: 0 }}
          initialValue={initialQuantity}
          rules={[
            {
              required: !isDisabled,
              message: `Please Input ${title}!`,
            },
            () => ({
              validator(_, value) {
                if (value === null || value === undefined || value === "") {
                  return Promise.resolve();
                }
                const numericValue = Number(value);
                if (Number.isNaN(numericValue)) {
                  return Promise.reject(new Error("Invalid quantity"));
                }
                if (numericValue <= 0) {
                  return Promise.reject(
                    new Error("Quantity must be greater than 0"),
                  );
                }
                if (numericValue > maxAllowed) {
                  return Promise.reject(
                    new Error(
                      `Cannot exceed ${maxAllowed} (existing ${existingAllocated} + available ${stillAvailable})`,
                    ),
                  );
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          {isDisabled ? (
            <Tooltip title="Task available quantity is 0. You cannot allocate any quantity.">
              <span style={{ display: "inline-block", width: "100%" }}>
                <InputNumber
                  min={0}
                  style={{ width: "100%", pointerEvents: "none" }}
                  disabled
                />
              </span>
            </Tooltip>
          ) : (
            <InputNumber min={0} max={9999999999}
          onKeyDown={handleNumberKeyDown} style={{ width: "100%" }} />
          )}
        </Form.Item>
      </td>
    );
  }

  if (dataIndex === "boqRate") {
    const fieldName = `boqRate_${record?.activityBoqId}`;
    const initialRate =
      record?.taskBoqRate !== undefined && record?.taskBoqRate !== null
        ? Number(record.taskBoqRate)
        : undefined;
    return (
      <td {...restProps}>
        <Form.Item
          name={fieldName}
          style={{ margin: 0 }}
          initialValue={initialRate}
          rules={[
            { required: true, message: `Please Input ${title}!` },
            {
              validator(_, value) {
                if (value === null || value === undefined || value === "") {
                  return Promise.resolve();
                }
                const numericValue = Number(value);
                if (Number.isNaN(numericValue)) {
                  return Promise.reject(new Error("Invalid rate"));
                }
                if (numericValue < 0) {
                  return Promise.reject(new Error("Rate cannot be negative"));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber min={0} max={9999999999}
          onKeyDown={handleNumberKeyDown} style={{ width: "100%" }} />
        </Form.Item>
      </td>
    );
  }

  if (dataIndex === "boqTotalAmount") {
    const id = record?.activityBoqId;
    return (
      <td {...restProps}>
        <Form.Item shouldUpdate noStyle>
          {() => {
            const qty =
              Number(form.getFieldValue(`allocateBOQQuantity_${id}`)) || 0;
            const rate = Number(form.getFieldValue(`boqRate_${id}`)) || 0;
            const total = qty * rate;
            return <span>{total ? total.toFixed(2) : ""}</span>;
          }}
        </Form.Item>
      </td>
    );
  }

  // Live total for the BOQ rate-analysis row (RA qty * RA rate).
  if (dataIndex === "boqRateAnalysisTotalAmount") {
    const id = record?.activityBoqId;
    return (
      <td {...restProps}>
        <Form.Item shouldUpdate noStyle>
          {() => {
            const qty =
              Number(form.getFieldValue(`boqRateAnalysisQty_${id}`)) || 0;
            const rate =
              Number(form.getFieldValue(`boqRateAnalysisRate_${id}`)) || 0;
            const total = qty * rate;
            return <span>{total ? total.toFixed(2) : ""}</span>;
          }}
        </Form.Item>
      </td>
    );
  }

  // For all other columns, render children normally
  return <td {...restProps}>{children}</td>;
};

const ResourceEditableCell: React.FC<any> = ({
  dataIndex,
  title,
  record,
  children,
  form,
  editing,
  workUomsList,
  outputFactor,
  ...restProps
}) => {
  // Unit-basis Rate Analysis cells. RA qty pre-fills the overall quantity via
  // outputFactor, RA rate pre-fills the overall rate, RA UOM mirrors the UOM.
  if (
    dataIndex === "resourceRateAnalysisQty" ||
    dataIndex === "resourceRateAnalysisRate"
  ) {
    const id = record?.activityResourceId;
    const key =
      dataIndex === "resourceRateAnalysisQty"
        ? "rateAnalysisQty"
        : "rateAnalysisRate";
    const fieldName = `${key}_${id}`;
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item name={fieldName} style={{ margin: 0 }}>
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              max={9999999999}
              onKeyDown={handleNumberKeyDown}
              placeholder={key === "rateAnalysisQty" ? "RA Qty" : "RA Rate"}
              onChange={(value) => {
                if (key === "rateAnalysisQty") {
                  const f = Number(outputFactor) || 0;
                  if (f > 0 && value != null) {
                    form.setFieldsValue({
                      [`quantity_${id}`]: Number((f * Number(value)).toFixed(2)),
                    });
                  }
                } else {
                  form.setFieldsValue({ [`rate_${id}`]: value });
                }
              }}
            />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
  if (dataIndex === "resourceRateAnalysisUomId") {
    const fieldName = `rateAnalysisUomId_${record?.activityResourceId}`;
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item name={fieldName} style={{ margin: 0 }}>
            <Select
              style={{ width: "100%" }}
              placeholder="UOM"
              options={workUomsList}
              showSearch
              optionFilterProp="label"
              allowClear
              onChange={(value) =>
                form.setFieldsValue({
                  [`workUomId_${record?.activityResourceId}`]: value,
                })
              }
            />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
  if (dataIndex === "resourceCount") {
    const fieldName = `${dataIndex}_${record?.activityResourceId}`;

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={fieldName}
            style={{ margin: 0 }}
            initialValue={record?.resourceCount || undefined}
            rules={[
              {
                type: "number",
                min: 1,
                message: `${title} must be at least 1`,
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              max={9999999999}
              onKeyDown={handleNumberKeyDown}
              placeholder="Enter count"
            />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
  if (dataIndex === "resourceRate" || dataIndex === "resourceQuantity") {
    const key = dataIndex === "resourceRate" ? "rate" : "quantity";
    const fieldName = `${key}_${record?.activityResourceId}`;
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={fieldName}
            style={{ margin: 0 }}
            rules={[
              {
                type: "number",
                min: 0,
                max: 99999999,
                message: `${title} must be between 0 and 99999999`,
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              max={9999999999}
          onKeyDown={handleNumberKeyDown}
              placeholder={`Enter ${title.toLowerCase()}`}
            />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
  if (dataIndex === "workUomId") {
    const fieldName = `workUomId_${record?.activityResourceId}`;
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item name={fieldName} style={{ margin: 0 }}>
            <Select
              style={{ width: "100%" }}
              placeholder="Select UOM"
              options={workUomsList}
              showSearch
              optionFilterProp="label"
              allowClear
            />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
  if (dataIndex === "resourceTotalAmount") {
    const id = record?.activityResourceId;
    return (
      <td {...restProps}>
        <Form.Item shouldUpdate noStyle>
          {() => {
            const qty = Number(form.getFieldValue(`quantity_${id}`)) || 0;
            const rate = Number(form.getFieldValue(`rate_${id}`)) || 0;
            const count = Number(form.getFieldValue(`resourceCount_${id}`)) || 0;
            const total = qty * rate * count;
            return <span>{total ? total.toFixed(2) : ""}</span>;
          }}
        </Form.Item>
      </td>
    );
  }
  return <td {...restProps}>{children}</td>;
};

const ToolEditableCell: React.FC<any> = ({
  dataIndex,
  record,
  children,
  editing,
  onCountChange,
  onFieldChange,
  workUomsList,
  outputFactor,
  ...restProps
}) => {
  // Unit-basis Rate Analysis cells. RA qty pre-fills the overall quantity via
  // outputFactor, RA rate pre-fills the overall rate, RA UOM mirrors to overall
  // UOM — matching the create form. All remain user-editable afterwards.
  if (
    (dataIndex === "rateAnalysisQty" || dataIndex === "rateAnalysisRate") &&
    editing &&
    record &&
    onFieldChange
  ) {
    return (
      <td {...restProps}>
        <InputNumber
          style={{ width: "100%" }}
          min={0}
          max={9999999999}
          onKeyDown={handleNumberKeyDown}
          defaultValue={record[dataIndex]}
          onBlur={(e) => {
            const value =
              e.target.value === "" ? undefined : Number(e.target.value);
            if (value === record[dataIndex]) return;
            onFieldChange(record.toolId, dataIndex, value);
            if (dataIndex === "rateAnalysisQty") {
              const f = Number(outputFactor) || 0;
              if (f > 0 && value != null) {
                onFieldChange(
                  record.toolId,
                  "quantity",
                  Number((f * value).toFixed(2)),
                );
              }
            } else {
              onFieldChange(record.toolId, "rate", value);
            }
          }}
          placeholder={dataIndex === "rateAnalysisQty" ? "RA Qty" : "RA Rate"}
        />
      </td>
    );
  }
  if (dataIndex === "rateAnalysisUomId" && editing && record && onFieldChange) {
    return (
      <td {...restProps}>
        <Select
          style={{ width: "100%" }}
          placeholder="Select UOM"
          options={workUomsList}
          showSearch
          optionFilterProp="label"
          allowClear
          defaultValue={record.rateAnalysisUomId}
          onChange={(value) => {
            onFieldChange(record.toolId, "rateAnalysisUomId", value);
            onFieldChange(record.toolId, "workUomId", value);
          }}
        />
      </td>
    );
  }
  if (dataIndex === "count" && editing && record && onCountChange) {
    return (
      <td {...restProps}>
        <InputNumber
          style={{ width: "100%" }}
          min={1}
          defaultValue={record.count}
          onBlur={(e) => {
            const value = Number(e.target.value);
            if (value && value >= 1 && value !== record.count) {
              onCountChange(record.toolId, value);
            }
          }}
          placeholder="Enter count"
        />
      </td>
    );
  }
  if (
    (dataIndex === "rate" || dataIndex === "quantity") &&
    editing &&
    record &&
    onFieldChange
  ) {
    return (
      <td {...restProps}>
        <InputNumber
          style={{ width: "100%" }}
          min={0}
          max={9999999999}
          onKeyDown={handleNumberKeyDown}
          defaultValue={record[dataIndex]}
          onBlur={(e) => {
            const value = e.target.value === "" ? undefined : Number(e.target.value);
            if (value !== record[dataIndex]) {
              onFieldChange(record.toolId, dataIndex, value);
            }
          }}
          placeholder={`Enter ${dataIndex}`}
        />
      </td>
    );
  }
  if (dataIndex === "workUomId" && editing && record && onFieldChange) {
    return (
      <td {...restProps}>
        <Select
          style={{ width: "100%" }}
          placeholder="Select UOM"
          options={workUomsList}
          showSearch
          optionFilterProp="label"
          allowClear
          defaultValue={record.workUomId}
          onChange={(value) => onFieldChange(record.toolId, "workUomId", value)}
        />
      </td>
    );
  }
  if (dataIndex === "serialNumber" && editing && record && onFieldChange) {
    return (
      <td {...restProps}>
        <Input
          style={{ width: "100%" }}
          maxLength={50}
          defaultValue={record.serialNumber}
          onInput={(e) => {
            e.currentTarget.value = sanitizeAlphaNumeric(
              e.currentTarget.value,
            );
          }}
          onBlur={(e) => {
            const value =
              e.target.value === ""
                ? undefined
                : sanitizeAlphaNumeric(e.target.value);
            if (value !== record.serialNumber) {
              onFieldChange(record.toolId, "serialNumber", value);
            }
          }}
          placeholder="Enter serial number"
        />
      </td>
    );
  }
  return <td {...restProps}>{children}</td>;
};

// Flatten a tool material (local ToolMaterialItem) to the API line shape.
const toolMaterialToLine = (m: ToolMaterialItem) => ({
  materialId: m.materialId,
  ...(m.subMaterialId != null ? { subMaterialId: m.subMaterialId } : {}),
  ...(m.quantity != null ? { quantity: Number(m.quantity) } : {}),
  ...(m.workUomId != null ? { workUomId: Number(m.workUomId) } : {}),
  ...(m.rate != null ? { rate: Number(m.rate) } : {}),
  ...(m.rateAnalysisQty != null
    ? { rateAnalysisQty: Number(m.rateAnalysisQty) }
    : {}),
  ...(m.rateAnalysisUomId != null
    ? { rateAnalysisUomId: Number(m.rateAnalysisUomId) }
    : {}),
  ...(m.rateAnalysisRate != null
    ? { rateAnalysisRate: Number(m.rateAnalysisRate) }
    : {}),
});

// Diff a tool's current split-materials against the originally loaded set into
// the {newLines, updatedLines, deletedLines} shape the update API expects.
// New rows have no taskToolMaterialId; removed rows are existing ids no longer
// present; changed rows are matched by id with any differing field.
const buildToolMaterialsDiff = (
  current: ToolMaterialItem[] = [],
  existing: any[] = [],
) => {
  const newLines = (current || [])
    .filter((m) => m.taskToolMaterialId == null)
    .map(toolMaterialToLine);

  const currentIds = new Set(
    (current || [])
      .map((m) => m.taskToolMaterialId)
      .filter((id) => id != null),
  );
  const deletedLines = (existing || [])
    .filter((m: any) => !currentIds.has(m.taskToolMaterialId))
    .map((m: any) => ({ taskToolMaterialId: m.taskToolMaterialId }));

  const updatedLines = (current || [])
    .filter((m) => m.taskToolMaterialId != null)
    .filter((m) => {
      const prev = (existing || []).find(
        (e: any) => e.taskToolMaterialId === m.taskToolMaterialId,
      );
      if (!prev) return false;
      return (
        Number(prev.rate ?? "") !== Number(m.rate ?? "") ||
        Number(prev.quantity ?? "") !== Number(m.quantity ?? "") ||
        Number(prev.workUomId ?? "") !== Number(m.workUomId ?? "") ||
        Number(prev.subMaterialId ?? "") !== Number(m.subMaterialId ?? "") ||
        Number(prev.rateAnalysisQty ?? "") !==
          Number(m.rateAnalysisQty ?? "") ||
        Number(prev.rateAnalysisUomId ?? "") !==
          Number(m.rateAnalysisUomId ?? "") ||
        Number(prev.rateAnalysisRate ?? "") !==
          Number(m.rateAnalysisRate ?? "")
      );
    })
    .map((m) => ({
      taskToolMaterialId: m.taskToolMaterialId,
      ...toolMaterialToLine(m),
    }));

  return { newLines, updatedLines, deletedLines };
};

const toolMaterialsDiffHasChanges = (diff: {
  newLines: any[];
  updatedLines: any[];
  deletedLines: any[];
}) =>
  diff.newLines.length > 0 ||
  diff.updatedLines.length > 0 ||
  diff.deletedLines.length > 0;

// Read-only "Rate Analysis & Cost Breakdown" shown in the task detail view.
// Renders entirely from the fetched task data (selectedTaskDetails).
const raMoney = (v: any) =>
  `₹${(Number(v) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const RateAnalysisBreakdown: React.FC<{ task: any }> = ({ task }) => {
  const resources: any[] = task?.taskResources || [];
  const tools: any[] = task?.taskTools || [];
  const boqs: any[] = task?.taskBoqs || [];

  const [materialMap, setMaterialMap] = useState<Record<number, string>>({});
  const [subMaterialMap, setSubMaterialMap] = useState<Record<number, string>>(
    {},
  );

  useEffect(() => {
    let active = true;
    const loadNames = async () => {
      try {
        const matRes = await tasksApis.fetchMachineryMaterialDropDown();
        const mMap: Record<number, string> = {};
        (matRes?.data || []).forEach((m: any) => {
          mMap[m.id ?? m.materialId] = m.materialName ?? m.name ?? m.label;
        });
        const matIds = Array.from(
          new Set(
            tools
              .flatMap((t: any) =>
                (t.toolMaterials || []).map((mm: any) => mm.materialId),
              )
              .filter((id: any) => id != null),
          ),
        );
        const sMap: Record<number, string> = {};
        await Promise.all(
          matIds.map(async (id: any) => {
            try {
              const subRes =
                await tasksApis.fetchMachinerySubMaterialDropDown(id);
              (subRes?.data || []).forEach((s: any) => {
                sMap[s.id ?? s.subMaterialId] =
                  s.subMaterialName ?? s.name ?? s.label;
              });
            } catch {
              /* ignore */
            }
          }),
        );
        if (active) {
          setMaterialMap(mMap);
          setSubMaterialMap(sMap);
        }
      } catch {
        /* ignore */
      }
    };
    if (tools.some((t: any) => (t.toolMaterials || []).length)) loadNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.taskId]);

  if (!resources.length && !tools.length && !boqs.length) return null;

  const raHeader = {
    background: "#bae0ff",
    textAlign: "center" as const,
    fontWeight: 600,
  };
  const overallHeader = {
    background: "#d9f7be",
    textAlign: "center" as const,
    fontWeight: 600,
  };
  const num = (v: any) => (v == null || v === "" ? "" : Number(v));
  const lineRa = (r: any) =>
    (Number(r?.rateAnalysisQty) || 0) * (Number(r?.rateAnalysisRate) || 0);
  const sum = (arr: any[], fn: (x: any) => number) =>
    arr.reduce((s, x) => s + fn(x), 0);

  const resOverall = sum(resources, (r) => Number(r.totalAmount) || 0);
  const toolOverall = sum(tools, (t) => Number(t.totalAmount) || 0);
  const boqOverall = sum(boqs, (b) => Number(b.totalAmount) || 0);
  const resRa = sum(resources, lineRa);
  const toolRa = sum(tools, lineRa);
  const boqRa = sum(boqs, lineRa);

  const groupCols = (
    raChildren: any[],
    overallChildren: any[],
  ): any[] => [
    {
      title: "Rate Analysis (per unit)",
      align: "center" as const,
      onHeaderCell: () => ({ style: raHeader }),
      children: raChildren,
    },
    {
      title: "Overall",
      align: "center" as const,
      onHeaderCell: () => ({ style: overallHeader }),
      children: overallChildren,
    },
  ];

  return (
    <div className="col-12 mt-3">
      <div
        className="card"
        style={{ border: "1px solid #004293", borderRadius: "8px" }}
      >
        <div className="card-body" style={{ padding: "16px" }}>
          <h6
            className="title mb-3"
            style={{ color: "#004293", fontWeight: 600 }}
          >
            Rate Analysis &amp; Cost Breakdown
          </h6>

          {resources.length > 0 && (
            <div className="mb-3">
              <div className="mb-1" style={{ fontWeight: 600 }}>
                Manpower
              </div>
              <Table
                size="small"
                bordered
                pagination={false}
                rowKey={(_: any, i?: number) => `res-${i}`}
                dataSource={resources}
                scroll={{ x: "max-content" }}
                columns={[
                  { title: "Labour Type", dataIndex: "laborTypeName" },
                  { title: "Labour Category", dataIndex: "laborCategoryName" },
                  ...groupCols(
                    [
                      {
                        title: "Qty",
                        render: (_: any, r: any) => num(r.rateAnalysisQty),
                      },
                      { title: "UOM", dataIndex: "rateAnalysisUomName" },
                      {
                        title: "Rate",
                        render: (_: any, r: any) =>
                          raMoney(r.rateAnalysisRate),
                      },
                      { title: "Total", render: (_: any, r: any) => raMoney(lineRa(r)) },
                    ],
                    [
                      { title: "Count", dataIndex: "resourceCount" },
                      { title: "Qty", render: (_: any, r: any) => num(r.quantity) },
                      { title: "UOM", dataIndex: "workUomName" },
                      { title: "Rate", render: (_: any, r: any) => raMoney(r.rate) },
                      { title: "Total", render: (_: any, r: any) => raMoney(r.totalAmount) },
                    ],
                  ),
                ]}
              />
            </div>
          )}

          {tools.length > 0 && (
            <div className="mb-3">
              <div className="mb-1" style={{ fontWeight: 600 }}>
                Machinery
              </div>
              <Table
                size="small"
                bordered
                pagination={false}
                rowKey={(_: any, i?: number) => `tool-${i}`}
                dataSource={tools}
                scroll={{ x: "max-content" }}
                expandable={{
                  rowExpandable: (r: any) => (r.toolMaterials || []).length > 0,
                  expandedRowRender: (r: any) => (
                    <div style={{ padding: "4px 8px" }}>
                      <div className="mb-1" style={{ fontWeight: 600 }}>
                        Split Materials
                      </div>
                      <Table
                        size="small"
                        bordered
                        pagination={false}
                        rowKey={(_: any, i?: number) => `mat-${i}`}
                        dataSource={r.toolMaterials || []}
                        columns={[
                          {
                            title: "Material",
                            render: (_: any, m: any) =>
                              materialMap[m.materialId] ||
                              `Material #${m.materialId}`,
                          },
                          {
                            title: "Sub-material",
                            render: (_: any, m: any) =>
                              m.subMaterialId
                                ? subMaterialMap[m.subMaterialId] ||
                                  `#${m.subMaterialId}`
                                : "",
                          },
                          ...groupCols(
                            [
                              { title: "Qty", render: (_: any, m: any) => num(m.rateAnalysisQty) },
                              { title: "UOM", dataIndex: "rateAnalysisUomName" },
                              { title: "Rate", render: (_: any, m: any) => raMoney(m.rateAnalysisRate) },
                              { title: "Total", render: (_: any, m: any) => raMoney(lineRa(m)) },
                            ],
                            [
                              { title: "Qty", render: (_: any, m: any) => num(m.quantity) },
                              { title: "UOM", dataIndex: "workUomName" },
                              { title: "Rate", render: (_: any, m: any) => raMoney(m.rate) },
                              { title: "Total", render: (_: any, m: any) => raMoney(m.totalAmount) },
                            ],
                          ),
                        ]}
                      />
                    </div>
                  ),
                }}
                columns={[
                  { title: "Machinery", dataIndex: "toolName" },
                  { title: "Serial No.", dataIndex: "serialNumber" },
                  ...groupCols(
                    [
                      { title: "Qty", render: (_: any, t: any) => num(t.rateAnalysisQty) },
                      { title: "UOM", dataIndex: "rateAnalysisUomName" },
                      { title: "Rate", render: (_: any, t: any) => raMoney(t.rateAnalysisRate) },
                      { title: "Total", render: (_: any, t: any) => raMoney(lineRa(t)) },
                    ],
                    [
                      { title: "Qty", render: (_: any, t: any) => num(t.quantity) },
                      { title: "UOM", dataIndex: "workUomName" },
                      { title: "Rate", render: (_: any, t: any) => raMoney(t.rate) },
                      { title: "Total", render: (_: any, t: any) => raMoney(t.totalAmount) },
                    ],
                  ),
                ]}
              />
            </div>
          )}

          {boqs.length > 0 && (
            <div className="mb-3">
              <div className="mb-1" style={{ fontWeight: 600 }}>
                Materials (BOQ)
              </div>
              <Table
                size="small"
                bordered
                pagination={false}
                rowKey={(_: any, i?: number) => `boq-${i}`}
                dataSource={boqs}
                scroll={{ x: "max-content" }}
                columns={[
                  { title: "Material", dataIndex: "materialName" },
                  { title: "Category", dataIndex: "categoryName" },
                  ...groupCols(
                    [
                      { title: "Qty", render: (_: any, b: any) => num(b.rateAnalysisQty) },
                      { title: "UOM", dataIndex: "uomName" },
                      { title: "Rate", render: (_: any, b: any) => raMoney(b.rateAnalysisRate) },
                      { title: "Total", render: (_: any, b: any) => raMoney(lineRa(b)) },
                    ],
                    [
                      { title: "Qty", render: (_: any, b: any) => num(b.allotedQuantity) },
                      { title: "UOM", dataIndex: "uomName" },
                      { title: "Rate", render: (_: any, b: any) => raMoney(b.rate) },
                      { title: "Total", render: (_: any, b: any) => raMoney(b.totalAmount) },
                    ],
                  ),
                ]}
              />
            </div>
          )}

          {/* Cost summary */}
          <div className="row">
            <div className="col-md-6">
              <div className="p-2" style={{ background: "#e6f4ff", borderRadius: 6 }}>
                <div style={{ fontWeight: 600, color: "#004293" }}>
                  Rate Analysis Total (per unit)
                </div>
                <div className="d-flex justify-content-between"><span>Materials</span><strong>{raMoney(boqRa)}</strong></div>
                <div className="d-flex justify-content-between"><span>Manpower</span><strong>{raMoney(resRa)}</strong></div>
                <div className="d-flex justify-content-between"><span>Machinery</span><strong>{raMoney(toolRa)}</strong></div>
                <hr className="my-1" />
                <div className="d-flex justify-content-between"><span>Total</span><strong>{raMoney(boqRa + resRa + toolRa)}</strong></div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="p-2" style={{ background: "#f6ffed", borderRadius: 6 }}>
                <div style={{ fontWeight: 600, color: "#004293" }}>
                  Overall Total
                </div>
                <div className="d-flex justify-content-between"><span>Materials</span><strong>{raMoney(boqOverall)}</strong></div>
                <div className="d-flex justify-content-between"><span>Manpower</span><strong>{raMoney(resOverall)}</strong></div>
                <div className="d-flex justify-content-between"><span>Machinery</span><strong>{raMoney(toolOverall)}</strong></div>
                <hr className="my-1" />
                <div className="d-flex justify-content-between"><span>Overall Total</span><strong>{raMoney(boqOverall + resOverall + toolOverall)}</strong></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface TaskBoq {
  activityBoqId: number;
  allotedQuantity: number;
}

interface BudgetLine {
  lineId: number;
  headerId: number;
  budgetId: string;
  budgetLineName: string;
  budgetValue: number;
  allocatedLineValue: number;
}

type TaskFormFields = {
  taskName: string;
  workCategoryId: string;
  reminderFlag: boolean;
  taskDescription: string;
  taskOwnerId: string;
  contractorId: string;
  taskPlannedStartDate: any;
  taskPlannedEndDate: any;
  priorityId: string;
  isBillable: any;
  taskBoqs: string[];
  projectUserId: string[];
  boqLineIds: string[];
  taskWeightagePercentage?: number;
  rate?: number;
  workType: string;
  workQuantity: number;
  workUomId: string[];
  budgetLineId?: number | null;
  allocatedBudgetAmount?: number;
  toolType?: number | null;
  toolMachinery?: number | null;
  toolCount?: string;
  toolSerialNumber?: string;
  toolRate?: string;
  toolQuantity?: string;
  toolWorkUomId?: number | null;
  hyperlinkName?: string;
  hyperlinkUrl?: string;
  siteId?: string | number | null;
  workLevelSetting?: string;
  siteStructureUnitId?: string | number | null;
  floorNo?: string | null;
  siteStructureSubUnitId?: string | number | null;
  locationId?: string | number | null;
  [key: string]: any; // Allow dynamic BOQ quantity fields
};

export const formatRecurrence = ({
  recurrenceType,
  recurrenceInterval,
  maxRecurrenceCount,
}: any) => {
  if (recurrenceType === "None") {
    return "No recurrence";
  }
  const unit: any = {
    Daily: "day(s)",
    Weekly: "week(s)",
    Monthly: "month(s)",
    Yearly: "year(s)",
  };
  const intervalText = `Every ${recurrenceInterval} ${unit?.[recurrenceType]}`;
  const countText = `Ends after ${maxRecurrenceCount} occurrence${
    maxRecurrenceCount > 1 ? "s" : ""
  }`;
  return `${intervalText}, ${countText}`;
};

// Removed staticChecklistData - now using dynamic data from API

const TaskInformationById: FC<{
  refreshData?: () => void;
  handleClose: () => any;
  isFromWorkUI?: boolean;
  hideInternalTabs?: boolean;
  initialEditMode?: boolean;
  onBackToSummary?: () => void;
}> = ({
  refreshData = () => {},
  handleClose,
  isFromWorkUI,
  hideInternalTabs = false,
  initialEditMode = false,
  onBackToSummary,
}) => {
  // All hooks here, at the top level
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [selectedChecklistForModal, setSelectedChecklistForModal] =
    useState<any>(null);
  const [modalAction, setModalAction] = useState<string | null>(null);
  const [taskBoqModalOpen, setTaskBoqModalOpen] = useState(false);
  const pendingStatusRef = useRef<string | null>(null);
  const [pendingSwal, setPendingSwal] = useState<{
    icon: SweetAlertIcon;
    title: string;
    text: string;
  } | null>(null);

  const dispatch = useDispatch();
  const { data: TaskListInDetailModal } = useSelector(
    (state: RootState) => state.TaskListInDetailModal,
  );
  const [screenSize, setScreenSize] = useState(0);
  const viewChange = () => setScreenSize(window.innerWidth);
  const selectedTaskDetails = TaskListInDetailModal.selectedTaskDetails;
  const allowEdit = TaskListInDetailModal.allowEdit;
  const isSubtask = TaskListInDetailModal.isSubtask;
  const { activityResponse, projectResponse, phaseResponse } =
    TaskListInDetailModal;
  const {
    id,
    startDate: projectStartDate,
    endDate: projectEndDate,
  } = activityResponse || {};
  const projectId = projectResponse?.id;
  const phaseId = phaseResponse?.id;
  const [messageApi, contextHolder] = message.useMessage();
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const { canEdit: hasModuleEditPermission } = useAccessControl();
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(initialEditMode);
  const [currentUserId, setCurrentUserId] = useState<any>(null);
  const [remainingWeightage, setRemainingWeightage] = useState<number | null>(
    null
  );
  const [boqLines, setBoqLines] = useState<BoqLines[]>([]);
  const [errorData, setErrorData] = useState<
    | {
        error: string;
        message: string;
      }
    | undefined
  >(undefined);
  const [activityBoqList, setActivityBoqList] = useState<any>([]);
  const [allActivityBoqList, setAllActivityBoqList] = useState<any>([]);
  const [workCatgoriesList, setWorkCatgoriesList] = useState<any>([]);
  const [activityResourcesList, setActivityResourcesList] = useState<any>([]);
  const [selectedResourceKeys, setSelectedResourceKeys] = useState<React.Key[]>(
    [],
  );
  const [editingResourceKeys, setEditingResourceKeys] = useState<string[]>([]);
  const [projectTagsList, setProjectTagsList] = useState<any[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [projectUsers, setProjectUsers] = useState<SelectOption[]>([]);
  const [contractorOptions, setContractorOptions] = useState<any>([]);
  const [allSiteIds, setAllSiteIds] = useState<string>("");
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [remainders, setRemainders] = useState<any>(null);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [priorityList, setPriorityList] = useState<SelectOption[]>([]);
  const [boqLinesDataSource, setBoqLinesDataSource] = useState<any>([]);
  const materialAllocateErrorsRef = useRef<HTMLDivElement>(null);
  const [taskStatusList, setTaskStatusList] = useState<any[]>([]);
  const [boqMaterialsError, setBoqMaterialsError] = useState<
    string | undefined
  >(undefined);
  const [form] = Form.useForm();
  const [resourceForm] = Form.useForm();
  const location = useLocation();
  // Get location state safely
  const locationState = location.state as any;
  const locationExtraFields = locationState?.extra;
  const derivedProjectStatus = locationExtraFields?.screenName;

  const [isProcessing, setIsProcessing] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [workUomsList, setWorkUomsList] = useState<SelectOption[]>([]);

  // Tools & Machinery state
  const [toolsAndMachineryTypes, setToolsAndMachineryTypes] = useState<
    SelectOption[]
  >([]);
  const [toolsAndMachineryList, setToolsAndMachineryList] = useState<
    SelectOption[]
  >([]);
  const [selectedToolType, setSelectedToolType] = useState<number | null>(null);
  const [selectedTool, setSelectedTool] = useState<number | null>(null);
  const [taskTools, setTaskTools] = useState<
    Array<{
      taskToolId?: number;
      toolId: number;
      count?: number;
      serialNumber?: string;
      toolName?: string;
      typeName?: string;
      rate?: number;
      quantity?: number;
      workUomId?: number;
      totalAmount?: number;
      rateAnalysisQty?: number;
      rateAnalysisUomId?: number;
      rateAnalysisRate?: number;
      toolMaterials?: ToolMaterialItem[];
    }>
  >([]);

  // Machinery material options for the per-tool split-materials manager (edit).
  const [materialOptions, setMaterialOptions] = useState<SelectOption[]>([]);
  // Per-tool split-materials drawer (matches the create form: by row index).
  const [materialsDrawer, setMaterialsDrawer] = useState<{
    open: boolean;
    toolIndex: number | null;
  }>({ open: false, toolIndex: null });
  // Index of the tool row being edited in place via the add-row, or null.
  const [editingToolIndex, setEditingToolIndex] = useState<number | null>(null);
  // Bumped on BOQ / resource form changes so the live Cost Summary recomputes
  // (antd Form value changes don't re-render the parent on their own).
  const [, setCostTick] = useState(0);

  // Hyperlinks state
  const [hyperlinks, setHyperlinks] = useState<
    Array<{
      hyperlinkId?: number;
      name: string;
      link: string;
    }>
  >([]);
  const [currentHyperlinkName, setCurrentHyperlinkName] = useState("");
  const [currentHyperlinkUrl, setCurrentHyperlinkUrl] = useState("");

  // Approval hierarchy state - matching TaskCreation.tsx pattern
  const [approvals, setApprovals] = useState<{
    open: boolean;
    data: any[];
  }>({
    open: false,
    data: [],
  });
  const [isApprovalRequired, setIsApprovalRequired] = useState(false);
  const [autoApprovalRemarks, setAutoApprovalRemarks] = useState<string>("");

  // Handler to update tool rate / quantity / workUomId / serialNumber and the
  // unit-basis rate-analysis fields inline.
  const handleToolFieldChange = (
    toolId: number,
    field:
      | "rate"
      | "quantity"
      | "workUomId"
      | "serialNumber"
      | "rateAnalysisQty"
      | "rateAnalysisUomId"
      | "rateAnalysisRate",
    value: number | string | undefined,
  ) => {
    setTaskTools((prevTools) =>
      prevTools.map((tool) =>
        tool.toolId === toolId ? { ...tool, [field]: value } : tool,
      ),
    );
  };

  // Replace a tool's split-materials list (driven by ToolMaterialsManager).
  const handleToolMaterialsChange = (
    toolId: number,
    materials: ToolMaterialItem[],
  ) => {
    setTaskTools((prevTools) =>
      prevTools.map((tool) =>
        tool.toolId === toolId ? { ...tool, toolMaterials: materials } : tool,
      ),
    );
  };

  // Map a fetched task tool (selectedTaskDetails.taskTools) to the local
  // editable shape, carrying its id, rate-analysis and split-materials (with
  // their DB ids) so they can be edited inline and diffed on submit.
  const mapExistingTaskTool = (tool: any) => ({
    taskToolId: tool.taskToolId,
    toolId: tool.toolId,
    count: tool.count,
    serialNumber: tool.serialNumber ?? undefined,
    toolName: tool.toolName,
    typeName: tool.toolTypeName,
    rate: tool.rate != null ? Number(tool.rate) : undefined,
    quantity: tool.quantity != null ? Number(tool.quantity) : undefined,
    workUomId: tool.workUomId ?? undefined,
    rateAnalysisQty:
      tool.rateAnalysisQty != null ? Number(tool.rateAnalysisQty) : undefined,
    rateAnalysisUomId: tool.rateAnalysisUomId ?? undefined,
    rateAnalysisRate:
      tool.rateAnalysisRate != null ? Number(tool.rateAnalysisRate) : undefined,
    toolMaterials: (tool.toolMaterials || []).map(
      (m: any): ToolMaterialItem => ({
        taskToolMaterialId: m.taskToolMaterialId,
        materialId: m.materialId,
        materialName: m.materialName ?? undefined,
        subMaterialId: m.subMaterialId ?? undefined,
        subMaterialName: m.subMaterialName ?? undefined,
        quantity: m.quantity != null ? Number(m.quantity) : undefined,
        workUomId: m.workUomId ?? undefined,
        workUomName: m.workUomName ?? undefined,
        rate: m.rate != null ? Number(m.rate) : undefined,
        rateAnalysisQty:
          m.rateAnalysisQty != null ? Number(m.rateAnalysisQty) : undefined,
        rateAnalysisUomId: m.rateAnalysisUomId ?? undefined,
        rateAnalysisUomName: m.rateAnalysisUomName ?? undefined,
        rateAnalysisRate:
          m.rateAnalysisRate != null ? Number(m.rateAnalysisRate) : undefined,
      }),
    ),
  });

  // Handler for approval hierarchy submission
  const handleApprovalSubmit = (data: any[]) => {
    setApprovals({
      open: false,
      data: data,
    });
  };

  // State for executed rate modal
  const [executedRateModalVisible, setExecutedRateModalVisible] =
    useState(false);
  const [executedRate, setExecutedRate] = useState<number | null>(null);
  const [rateRemarks, setRateRemarks] = useState("");
  const [pendingClosureStatus, setPendingClosureStatus] = useState<
    string | null
  >(null);
  const [executedRateModalCancelled, setExecutedRateModalCancelled] =
    useState(false);

  // Budget line related state
  const [budgetLines, setBudgetLines] = useState<SelectOption[]>([]);
  const [hasProjectBudget, setHasProjectBudget] = useState(false);
  const [selectedBudgetValue, setSelectedBudgetValue] = useState<number | null>(
    null,
  );
  const [selectedBudgetLine, setSelectedBudgetLine] =
    useState<BudgetLine | null>(null);
  const [isLoadingBudgetLines, setIsLoadingBudgetLines] = useState(false);
  const budgetLinesFetchedRef = useRef<number | null>(null);

  // Ref to track previous site hierarchy values to detect actual changes
  const prevSiteHierarchyRef = useRef<{
    workLevelSetting?: string;
    unitId?: string | number | null;
    floorNo?: string | null;
    subUnitId?: string | number | null;
    locationId?: string | number | null;
  }>({});

  // Site Hierarchy related state
  const [towerOptions, setTowerOptions] = useState<SelectOption[]>([]);
  const [floorOptions, setFloorOptions] = useState<SelectOption[]>([]);
  const [flatOptions, setFlatOptions] = useState<SelectOption[]>([]);
  const [locationOptions, setLocationOptions] = useState<SelectOption[]>([]);
  const [projectSiteId, setProjectSiteId] = useState<number | null>(null);

  // Hierarchy constants
  const HIERARCHY_UNIT_TITLES: Record<string, string> = {
    TOWER_BLOCK: "Tower / Block",
    VILLA: "Villa",
    FLOOR: "Tower / Block",
    FLAT: "Tower / Block",
  };

  // Map workLevelSetting to segmentLevel format expected by backend
  const getSegmentLevel = (workLevelSetting?: string): string | undefined => {
    if (!workLevelSetting) return undefined;
    const segmentLevelMap: Record<string, string> = {
      TOWER_BLOCK: "Tower",
      VILLA: "Villa",
      FLOOR: "Floor",
      FLAT: "Flat",
    };
    return segmentLevelMap[workLevelSetting];
  };

  // Map segmentLevel from API to workLevelSetting for form
  const getWorkLevelSettingFromSegmentLevel = (
    segmentLevel?: string,
  ): string | undefined => {
    if (!segmentLevel) return undefined;
    const workLevelSettingMap: Record<string, string> = {
      Tower: "TOWER_BLOCK",
      Villa: "VILLA",
      Floor: "FLOOR",
      Flat: "FLAT",
    };
    return workLevelSettingMap[segmentLevel];
  };

  // Ref to prevent infinite loops in watch functions
  const isProcessingChangesRef = useRef(false);

  // Tracks the taskId we've populated form values for, so that when the
  // component is mounted directly in edit mode (initialEditMode=true) the
  // form still gets seeded once. Without this, the populate effect below
  // skips on edit mode and the user sees an empty form.
  const populatedForTaskIdRef = useRef<number | null>(null);

  // Store the original allowEdit value (based on project/phase/activity status)
  const [originalAllowEdit, setOriginalAllowEdit] = useState<
    boolean | undefined
  >(undefined);

  // Helper function to safely call refreshData
  const safeRefreshData = () => {
    if (typeof refreshData === "function") {
      try {
        refreshData();
      } catch (error) {
        console.warn("Error calling refreshData:", error);
      }
    }
  };

  // Helper function to check if reminders have meaningful changes
  const hasReminderMeaningfulChanges = () => {
    // If reminders were just loaded from API, don't consider it a change
    if (remainders?.isLoadedFromAPI) {
      return false;
    }

    if (!remainders && !selectedTaskDetails?.reminders) {
      return false;
    }

    if (!remainders && selectedTaskDetails?.reminders) {
      return true; // Reminder was removed
    }

    if (remainders && !selectedTaskDetails?.reminders) {
      return true; // New reminder was added
    }

    // Both exist, compare key fields
    const current = remainders;
    const existing = selectedTaskDetails?.reminders;

    // Compare essential reminder fields
    const keyFields = [
      "reminderType",
      "specificTime",
      "specificDate",
      "offsetFrom",
      "daysOffset",
      "recurrenceType",
      "recurrenceInterval",
      "maxRecurrenceCount",
    ];

    for (const field of keyFields) {
      if (field === "specificDate") {
        const currentDate = current[field]
          ? dayjs(current[field]).format("YYYY-MM-DD")
          : null;
        const existingDate = existing[field]
          ? dayjs(existing[field]).format("YYYY-MM-DD")
          : null;
        if (currentDate !== existingDate) {
          return true;
        }
      } else if (current[field] !== existing[field]) {
        return true;
      }
    }

    // Compare reminder users
    const currentUserIds =
      current.reminderUsers?.map((u: any) => u.userId) || [];
    const existingUserIds =
      existing.reminderUsers?.map((u: any) => u.userId) || [];

    if (currentUserIds.length !== existingUserIds.length) {
      return true;
    }

    for (const userId of currentUserIds) {
      if (!existingUserIds.includes(userId)) {
        return true;
      }
    }

    return false;
  };

  // Helper function to check if current task status allows editing
  const isTaskStatusEditable = (taskStatus?: string) => {
    if (!taskStatus) return false;
    return !["closed", "archived", "onhold"].includes(taskStatus.toLowerCase());
  };

  const [ownerChangeModalOpen, setOwnerChangeModalOpen] = useState<{
    open: boolean;
    extraData: any;
  }>({
    open: false,
    extraData: {},
  });
  const [statusChangeModalOpen, setStatusChangeModalOpen] = useState<{
    open: boolean;
    extraData: any;
  }>({
    open: false,
    extraData: {},
  });
  const [activeTab, setActiveTab] = useState("comments");
  // Initialize comments tab as loaded since it's the default active tab
  const [loadedTabs, setLoadedTabs] = useState<Set<string>>(
    new Set(["comments"]),
  );

  const TabsList = [
    {
      tabKey: "comments",
      label: "Comments",
      accessControl: "ViewAccess",
      moduleName: "Tasks",
    },
    {
      tabKey: "SubTasks",
      label: "Sub Tasks",
      accessControl: "ViewAccess",
      moduleName: "Tasks",
    },
    {
      tabKey: "Loghours",
      label: "Log Hours",
      accessControl: "ViewAccess",
      moduleName: "Tasks",
    },
    {
      tabKey: "issues",
      label: "Issues",
      accessControl: "ViewAccess",
      moduleName: "Tasks",
    },
    {
      tabKey: "Dependencies",
      label: "Dependencies",
      accessControl: "ViewAccess",
      moduleName: "Tasks",
    },
    {
      tabKey: "ChecklistResponse",
      label: "Checklist Response",
      accessControl: "ViewAccess",
      moduleName: "Tasks",
    },
    {
      tabKey: "Documents",
      label: "Documents",
      accessControl: "ViewAccess",
      moduleName: "Tasks",
    },
    {
      tabKey: "StatusTimeLine",
      label: "Status Timeline",
      accessControl: "ViewAccess",
      moduleName: "Tasks",
    },
    {
      tabKey: "ActivityStream",
      label: "Activity Stream",
      accessControl: "ViewAccess",
      moduleName: "Tasks",
    },
  ];

  const BILLABLE_OPTIONS: SelectOption[] = [
    { label: "Billable", value: "Billable" },
    { label: "Non Billable", value: "Non Billable" },
  ];

  const methods = useForm<TaskFormFields>({ mode: "onChange" });
  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { dirtyFields },
  } = methods;
  const startDateWatch = watch("taskPlannedStartDate");
  const selectedOwnerId = watch("taskOwnerId");
  const boqLineIdsWatch = watch("boqLineIds");

  // If the owner is changed to someone already chosen as an approver, drop
  // them from the saved approval hierarchy — a user can't approve their own
  // task. Handles the case where approvals were set before the owner.
  useEffect(() => {
    if (selectedOwnerId == null || selectedOwnerId === "") return;
    setApprovals((prev: any) => {
      if (!prev.data?.length) return prev;
      const filtered = prev.data.filter(
        (row: any) => Number(row.userId) !== Number(selectedOwnerId),
      );
      return filtered.length === prev.data.length
        ? prev
        : { ...prev, data: filtered };
    });
  }, [selectedOwnerId]);
  // Memoize BOQ options so the array reference is stable across re-renders.
  // Re-creating the options on every render makes antd Select treat them as
  // new and collapses the dropdown after each selection in multi-select mode.
  const boqTitleOptions = useMemo(() => {
    if (!boqLineIdsWatch || boqLineIdsWatch.length === 0) {
      return activityBoqList?.map((item: any) => ({
        label: `${item.lines?.[0]?.materialName} - ${item.lines?.[0]?.categoryName}`,
        value: item?.activityBoqId,
      }));
    }
    const firstBoq = activityBoqList.find(
      (item: any) => item.activityBoqId === boqLineIdsWatch[0],
    );
    if (firstBoq) {
      const targetNumber = firstBoq.boqTargetNumber;
      return activityBoqList
        ?.filter((item: any) => item.boqTargetNumber === targetNumber)
        ?.map((item: any) => ({
          label: `${item.lines?.[0]?.materialName} - ${item.lines?.[0]?.categoryName} `,
          value: item?.activityBoqId,
        }));
    }
    return activityBoqList?.map((item: any) => ({
      label: ` ${item.lines?.[0]?.materialName} - ${item.lines?.[0]?.categoryName} `,
      value: item?.activityBoqId,
    }));
  }, [activityBoqList, boqLineIdsWatch]);
  const workTypeWatch = watch("workType");
  // Task price split. While editing, preview from the form values; otherwise fall back
  // to what the server stored. Derived client-side with the same helper the backend
  // uses, so the preview can never disagree with what will be saved.
  const rateWatch = watch("rate");
  const gstPercentWatch = watch("gstPercent");
  const cessPercentWatch = watch("cessPercent");
  const taskGstPercent = Number(
    gstPercentWatch ?? selectedTaskDetails?.gstPercent ?? DEFAULT_GST_PERCENT,
  );
  const taskCessPercent = Number(
    cessPercentWatch ?? selectedTaskDetails?.cessPercent ?? DEFAULT_CESS_PERCENT,
  );
  const taskPriceBreakdown = useMemo(
    () =>
      computeTaskPriceBreakdown(
        rateWatch ?? selectedTaskDetails?.rate,
        taskGstPercent,
        taskCessPercent,
      ),
    [rateWatch, selectedTaskDetails?.rate, taskGstPercent, taskCessPercent],
  );
  const workQuantityWatch = watch("workQuantity");
  const outputQuantityWatch = watch("outputQuantity");
  const workUomIdWatch = watch("workUomId");
  const outputBasisUomIdWatch = watch("outputBasisUomId");
  // Rate-analysis is captured only for Quantity work type once an output qty is
  // given (mirrors the task creation screen). Otherwise only Overall is shown.
  const showRateAnalysis = useMemo(
    () =>
      workTypeWatch === "Quantity" &&
      !!outputQuantityWatch &&
      Number(outputQuantityWatch) > 0,
    [workTypeWatch, outputQuantityWatch],
  );
  const outputFactor = useMemo(() => {
    const wq = Number(workQuantityWatch);
    const oq = Number(outputQuantityWatch);
    if (!wq || !oq) return 0;
    return wq / oq;
  }, [workQuantityWatch, outputQuantityWatch]);
  const workUomLabel = useMemo(
    () =>
      workUomsList.find((u: SelectOption) => u.value === workUomIdWatch)
        ?.label || "",
    [workUomsList, workUomIdWatch],
  );
  const outputBasisUomLabel = useMemo(
    () =>
      workUomsList.find((u: SelectOption) => u.value === outputBasisUomIdWatch)
        ?.label || "",
    [workUomsList, outputBasisUomIdWatch],
  );
  const rateAnalysisLabel = useMemo(() => {
    if (!outputQuantityWatch) return "Rate Analysis";
    let label = `Rate Analysis per ${outputQuantityWatch}`;
    if (workUomLabel) label += ` ${workUomLabel}`;
    if (outputBasisUomLabel) label += ` per ${outputBasisUomLabel}`;
    return label;
  }, [outputQuantityWatch, workUomLabel, outputBasisUomLabel]);
  const overallLabel = useMemo(
    () =>
      workQuantityWatch
        ? `Overall (for ${workQuantityWatch}${
            workUomLabel ? ` ${workUomLabel}` : ""
          })`
        : "Overall",
    [workQuantityWatch, workUomLabel],
  );
  const budgetLineIdWatch = watch("budgetLineId");
  const allocatedBudgetAmountWatch = watch("allocatedBudgetAmount");
  const toolTypeWatch = watch("toolType");
  const toolMachineryWatch = watch("toolMachinery");
  const workLevelSetting = watch("workLevelSetting");
  const selectedUnitId = watch("siteStructureUnitId");
  const selectedFloorNo = watch("floorNo");
  const selectedSubUnitId = watch("siteStructureSubUnitId");
  const selectedLocationId = watch("locationId");

  const shouldShowHierarchy = useMemo(
    () => !!workLevelSetting && workLevelSetting !== "NONE",
    [workLevelSetting],
  );

  // Disable tower/block, floor, flat, location when they exist in API response (user cannot edit)
  const hasValue = (val: any) => val != null && val !== "";
  const isTowerBlockDisabledFromApi = hasValue(
    selectedTaskDetails?.siteStructureUnitId,
  );
  const isFloorDisabledFromApi = hasValue(selectedTaskDetails?.floorNo);
  const isFlatDisabledFromApi = hasValue(
    selectedTaskDetails?.siteStructureSubUnitId,
  );
  const isLocationDisabledFromApi = hasValue(selectedTaskDetails?.locationId);

  const hierarchyFieldLabel = useMemo(() => {
    if (!workLevelSetting) {
      return "Tower / Block";
    }
    return HIERARCHY_UNIT_TITLES[workLevelSetting] ?? "Tower / Block";
  }, [workLevelSetting]);

  const hierarchyFieldPlaceholder = useMemo(() => {
    switch (workLevelSetting) {
      case "VILLA":
        return "Select Villa";
      case "TOWER_BLOCK":
      case "FLOOR":
      case "FLAT":
        return "Select Tower / Block";
      default:
        return "Select Tower / Block / Villa";
    }
  }, [workLevelSetting]);

  // Merge the saved hierarchy values into the dropdown options so the dropdown
  // can render the saved label even when the corresponding option hasn't been
  // loaded yet (e.g., options API is still in flight, unit was archived, or
  // the loaded list is paginated/filtered and excludes this unit).
  const mergedTowerOptions = useMemo(() => {
    const savedId = selectedTaskDetails?.siteStructureUnitId;
    const savedName = selectedTaskDetails?.siteStructureUnitName;
    // Form value uses ID when present, else falls back to name string.
    const savedKey = savedId != null ? savedId.toString() : savedName;
    if (!savedKey) return towerOptions;
    if (towerOptions.some((opt) => opt.value === savedKey)) return towerOptions;
    return [
      { value: savedKey, label: savedName || savedKey },
      ...towerOptions,
    ];
  }, [towerOptions, selectedTaskDetails?.siteStructureUnitId, selectedTaskDetails?.siteStructureUnitName]);

  const mergedFloorOptions = useMemo(() => {
    const savedFloor = selectedTaskDetails?.floorNo;
    if (savedFloor == null || savedFloor === "") return floorOptions;
    const savedFloorStr = savedFloor.toString();
    if (floorOptions.some((opt) => opt.value === savedFloorStr)) return floorOptions;
    return [{ value: savedFloorStr, label: savedFloorStr }, ...floorOptions];
  }, [floorOptions, selectedTaskDetails?.floorNo]);

  const mergedFlatOptions = useMemo(() => {
    const savedId = selectedTaskDetails?.siteStructureSubUnitId;
    const savedName = selectedTaskDetails?.siteStructureSubUnitName;
    const savedKey = savedId != null ? savedId.toString() : savedName;
    if (!savedKey) return flatOptions;
    if (flatOptions.some((opt) => opt.value === savedKey)) return flatOptions;
    return [
      { value: savedKey, label: savedName || savedKey },
      ...flatOptions,
    ];
  }, [flatOptions, selectedTaskDetails?.siteStructureSubUnitId, selectedTaskDetails?.siteStructureSubUnitName]);

  const mergedLocationOptions = useMemo(() => {
    const savedId = selectedTaskDetails?.locationId;
    const savedName = selectedTaskDetails?.locationName;
    const savedKey = savedId != null ? savedId.toString() : savedName;
    if (!savedKey) return locationOptions;
    if (locationOptions.some((opt) => opt.value === savedKey)) return locationOptions;
    return [
      { value: savedKey, label: savedName || savedKey },
      ...locationOptions,
    ];
  }, [locationOptions, selectedTaskDetails?.locationId, selectedTaskDetails?.locationName]);

  // Auto-set isBillable to "Billable" when workType is "Quantity"
  useEffect(() => {
    if (workTypeWatch === "Quantity" && isEditMode) {
      setValue("isBillable", "Billable", {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  }, [workTypeWatch, isEditMode, setValue]);

  const fetchTaskWeightages = async () => {
    try {
      const response: any = await tasksApis.getActivityTaskWeightage(
        Number(projectId),
        id ? Number(id) : undefined,
        phaseId ? Number(phaseId) : undefined
      );
      const {
        data: list,
        status,
      }: {
        status: boolean;
        data: { activityId: number; overallTaskWeightage: number }[];
      } = response || {};
      if (status) {
        const activityIdNum = id ? Number(id) : undefined;
        const matched =
          (list || []).find(
            (item) => Number(item?.activityId) === activityIdNum
          ) || (list || [])[0];
        const used = Number(matched?.overallTaskWeightage) || 0;
        // overallTaskWeightage already includes this task's existing share.
        // Subtract it so the editor sees its current value as still available.
        const currentTaskWeightage =
          Number(selectedTaskDetails?.taskWeightagePercentage) || 0;
        const usedExcludingCurrent = Math.max(0, used - currentTaskWeightage);
        setRemainingWeightage(
          Math.max(0, Number((100 - usedExcludingCurrent).toFixed(2)))
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (isEditMode && !isSubtask && projectId) {
      fetchTaskWeightages();
    } else if (!isEditMode) {
      setRemainingWeightage(null);
    }
  }, [
    isEditMode,
    isSubtask,
    projectId,
    phaseId,
    id,
    selectedTaskDetails?.taskId,
    selectedTaskDetails?.taskWeightagePercentage,
  ]);

  // Hierarchy FormSelects mount only after workLevelSetting is set (via the
  // reset effect). For late-mounting Controllers, reset() values may not
  // reliably propagate. Re-seed hierarchy fields once shouldShowHierarchy
  // becomes true so the dropdowns display the saved labels in edit mode.
  const hierarchySeededTaskIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isEditMode || !shouldShowHierarchy || !selectedTaskDetails) return;
    const currentTaskId = selectedTaskDetails?.taskId ?? null;
    if (hierarchySeededTaskIdRef.current === currentTaskId) return;
    hierarchySeededTaskIdRef.current = currentTaskId;

    const unitValue =
      selectedTaskDetails?.siteStructureUnitId != null
        ? selectedTaskDetails.siteStructureUnitId.toString()
        : selectedTaskDetails?.siteStructureUnitName ?? null;
    const floorValue =
      selectedTaskDetails?.floorNo != null
        ? selectedTaskDetails.floorNo.toString()
        : null;
    const subUnitValue =
      selectedTaskDetails?.siteStructureSubUnitId != null
        ? selectedTaskDetails.siteStructureSubUnitId.toString()
        : selectedTaskDetails?.siteStructureSubUnitName ?? null;
    const locationValue =
      selectedTaskDetails?.locationId != null
        ? selectedTaskDetails.locationId.toString()
        : selectedTaskDetails?.locationName ?? null;

    if (unitValue != null) setValue("siteStructureUnitId", unitValue);
    if (floorValue != null) setValue("floorNo", floorValue);
    if (subUnitValue != null) setValue("siteStructureSubUnitId", subUnitValue);
    if (locationValue != null) setValue("locationId", locationValue);
  }, [isEditMode, shouldShowHierarchy, selectedTaskDetails, setValue]);

  // Calculate real-time remaining budget
  const getRealTimeRemainingBudget = () => {
    if (!selectedBudgetLine) return 0;

    const currentAllocation = allocatedBudgetAmountWatch || 0;
    const originalAllocation =
      selectedTaskDetails?.budgetValue ||
      selectedTaskDetails?.allocatedBudgetAmount ||
      0;

    // Calculate the difference between current and original allocation
    const allocationDifference = currentAllocation - originalAllocation;

    // Calculate remaining budget: original remaining - allocation difference
    const originalRemaining =
      selectedBudgetLine.budgetValue - selectedBudgetLine.allocatedLineValue;
    const realTimeRemaining = originalRemaining - allocationDifference;

    return Math.max(0, realTimeRemaining); // Ensure it doesn't go below 0
  };

  // Function to mark BOQ fields as dirty when they change
  const markBOQFieldsAsDirty = () => {
    const currentFormValues = form.getFieldsValue();
    Object.keys(currentFormValues)
      .filter((key) => key.startsWith("allocateBOQQuantity_"))
      .forEach((fieldName) => {
        const [, boqTargetId] = fieldName.split("_");
        const currentValue = currentFormValues[fieldName];
        const existingBoq = selectedTaskDetails?.taskBoqs?.find(
          (tb: any) => tb.activityBoqId === Number(boqTargetId),
        );

        if (
          existingBoq &&
          Number(existingBoq.allotedQuantity || 0) !== Number(currentValue || 0)
        ) {
          // Mark the field as dirty in the form
          form.setFieldValue(fieldName, currentValue);
          // Also update the react-hook-form state
          setValue(fieldName, currentValue, {
            shouldDirty: true,
            shouldTouch: true,
          });
        }
      });
  };

  // Function to mark all relevant fields as dirty before saving
  const markAllRelevantFieldsAsDirty = () => {
    // Set the processing flag to prevent infinite loops
    isProcessingChangesRef.current = true;

    try {
      // Mark BOQ fields as dirty
      markBOQFieldsAsDirty();

      // Mark BOQ line selection as dirty if changed
      const currentBoqLineIds = (boqLineIdsWatch || []).filter(
        (id): id is string => id !== undefined,
      );
      const existingBoqLineIds = (selectedTaskDetails?.taskBoqs || []).map(
        (boq: any) => boq.activityBoqId,
      );

      if (
        currentBoqLineIds.length !== existingBoqLineIds.length ||
        currentBoqLineIds.some((id: any) => !existingBoqLineIds.includes(id))
      ) {
        // Only set if value is actually different to avoid infinite loops
        const currentValue = form.getFieldValue("boqLineIds");
        if (
          JSON.stringify(currentValue) !== JSON.stringify(currentBoqLineIds)
        ) {
          setValue("boqLineIds", currentBoqLineIds, {
            shouldDirty: true,
            shouldTouch: false,
          });
        }
      }

      // Mark reminder flag as dirty only if reminders have actually changed
      if (hasReminderMeaningfulChanges()) {
        const hasReminders = remainders && Object.keys(remainders).length > 0;
        const currentReminderFlag = form.getFieldValue("reminderFlag");
        const expectedReminderFlag = hasReminders;

        if (currentReminderFlag !== expectedReminderFlag) {
          setValue("reminderFlag", expectedReminderFlag, {
            shouldDirty: true,
            shouldTouch: false,
          });
        }
      }
    } finally {
      // Reset the processing flag after a short delay
      setTimeout(() => {
        isProcessingChangesRef.current = false;
      }, 100);
    }
  };

  useEffect(() => {
    if (currentUser) {
      const { id } = currentUser;
      setCurrentUserId(id);
    }
  }, [currentUser]);

  // Fetch project siteId when projectId is available
  useEffect(() => {
    const fetchProjectSiteId = async () => {
      if (projectId) {
        try {
          const response: ApiResponse = await projectsApis.getProjectById(
            String(projectId),
            undefined,
          );
          if (response?.status && response?.data?.siteId) {
            setProjectSiteId(response.data.siteId);
            // Set siteId in form for submission
            setValue("siteId", response.data.siteId);
          }
        } catch (error) {
          console.error("Error fetching project siteId:", error);
        }
      }
    };
    fetchProjectSiteId();
  }, [projectId, setValue]);

  // Track if initial data has been loaded to prevent duplicate API calls
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [dropdownDataLoaded, setDropdownDataLoaded] = useState(false);

  useEffect(() => {
    if (!initialDataLoaded) {
      viewChange();
      setInitialDataLoaded(true);
    }
  }, [initialDataLoaded]);

  // Keep screen size in sync for responsive field layout
  useEffect(() => {
    viewChange();
    window.addEventListener("resize", viewChange);
    return () => window.removeEventListener("resize", viewChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (TaskListInDetailModal.id) {
      fetchTaskById();
      // Clear any existing error data when task changes
      setErrorData(undefined);
    }
  }, [TaskListInDetailModal.id]);

  // Load only status list when modal opens (needed to show status dropdown in view mode)
  useEffect(() => {
    if (TaskListInDetailModal.open && taskStatusList.length === 0) {
      fetchTaskStatusList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [TaskListInDetailModal.open]);

  // Reset edit mode when modal is opened/closed
  useEffect(() => {
    if (!TaskListInDetailModal.open) {
      setIsEditMode(false);
      setDisableSubmit(false);
      // Reset dropdown data loaded flag when modal is closed
      setDropdownDataLoaded(false);
    }
  }, [TaskListInDetailModal.open]);

  // Scroll to top and reset tab when task modal opens
  useEffect(() => {
    if (TaskListInDetailModal.open) {
      window.scrollTo(0, 0);
      // Reset to comments tab when modal opens
      setActiveTab("comments");
    }
  }, [TaskListInDetailModal.open]);

  // Load dropdown data when edit mode is activated
  const loadDropdownData = async () => {
    if (!dropdownDataLoaded) {
      await Promise.all([
        fetchPriority(),
        fetchActivityBoqs(),
        fetchWorkCatgoriesList(),
        fetchActivityResourcesList(),
        fetchProjectUsers(),
        fetchProjectTags(),
        fetchWorkUomsList(),
        fetchBudgetLines(), // Fetch budget lines initially
        fetchToolsAndMachineryTypes(),
        fetchToolsAndMachinery(),
        fetchMaterialOptions(),
        // Load task status list only when editing (not on modal open)
        ...(isEditMode && taskStatusList.length === 0
          ? [fetchTaskStatusList()]
          : []),
      ]);
      setDropdownDataLoaded(true);
    } else {
      // If data is already loaded, only fetch fresh budget lines when entering edit mode
      budgetLinesFetchedRef.current = null;
      await fetchBudgetLines();
    }
  };

  // Load Work UOM only when needed (when workType is "Quantity")
  const loadWorkUomIfNeeded = async () => {
    if (
      selectedTaskDetails?.workType === "Quantity" &&
      workUomsList.length === 0
    ) {
      await fetchWorkUomsList();
    }
  };

  // Handle tab activation and load data only when tab becomes active
  const handleTabChange = (tabKey: string) => {
    if (!loadedTabs.has(tabKey)) {
      const newLoadedTabs = new Set(loadedTabs);
      newLoadedTabs.add(tabKey);
      setLoadedTabs(newLoadedTabs);
    }
    setActiveTab(tabKey);
  };

  useEffect(() => {
    const currentTaskId = selectedTaskDetails?.taskId ?? null;
    const needsInitialEditPopulate =
      isEditMode && populatedForTaskIdRef.current !== currentTaskId;
    if (
      selectedTaskDetails &&
      boqLines &&
      (!isEditMode || needsInitialEditPopulate)
    ) {
      populatedForTaskIdRef.current = currentTaskId;
      const initialTags =
        selectedTaskDetails.taskTags?.map((tag: any) => ({
          label: tag.tagName,
          value: tag.tagId,
        })) || [];

      const initialBoqLineIds = selectedTaskDetails.taskBoqs
        ? selectedTaskDetails.taskBoqs
            .map((boq: TaskBoq) => {
              const boqLine = boqLines.find(
                (line: any) => line.boqTargetId === boq.activityBoqId,
              );
              return boqLine ? boqLine.boqTargetId : null;
            })
            .filter((id: any) => id !== null)
        : [];

      setTags(initialTags);
      // Reset remainders state to match the selected task details
      setRemainders(
        selectedTaskDetails?.reminders
          ? { ...selectedTaskDetails.reminders, isLoadedFromAPI: true }
          : null,
      );
      reset({
        taskName: selectedTaskDetails.taskName || "",
        internalTaskNumber: selectedTaskDetails.internalTaskNumber || "",
        workCategoryId: selectedTaskDetails.workCategoryId || "",
        reminderFlag: selectedTaskDetails.reminderFlag,
        taskDescription: selectedTaskDetails.taskDescription || "",
        taskOwnerId: selectedTaskDetails.taskOwnerId || "",
        contractorId: selectedTaskDetails.contractorId || "",
        taskPlannedStartDate: selectedTaskDetails?.taskPlannedStartDate
          ? dayjs(selectedTaskDetails.taskPlannedStartDate)
          : undefined,
        taskPlannedEndDate: selectedTaskDetails?.taskPlannedEndDate
          ? dayjs(selectedTaskDetails.taskPlannedEndDate)
          : undefined,
        priorityId: selectedTaskDetails.priorityId || "",
        isBillable:
          selectedTaskDetails.isBillable !== undefined
            ? selectedTaskDetails.isBillable === true
              ? "Billable"
              : "Non Billable"
            : "",
        taskBoqs: selectedTaskDetails.taskBoqs || [],
        projectUserId:
          selectedTaskDetails.taskUsers?.map(
            (user: any) => user.projectUserId,
          ) || [],
        boqLineIds: initialBoqLineIds,
        rate: selectedTaskDetails?.rate || undefined,
        gstPercent:
          selectedTaskDetails?.gstPercent != null
            ? Number(selectedTaskDetails.gstPercent)
            : undefined,
        cessPercent:
          selectedTaskDetails?.cessPercent != null
            ? Number(selectedTaskDetails.cessPercent)
            : undefined,
        taskWeightagePercentage: selectedTaskDetails.taskWeightagePercentage
          ? Number(selectedTaskDetails.taskWeightagePercentage)
          : undefined,
        workType: selectedTaskDetails?.workType || "Open",
        workQuantity: selectedTaskDetails?.workQuantity || undefined,
        workUomId: selectedTaskDetails?.workUomId || undefined,
        outputQuantity: selectedTaskDetails?.outputQuantity ?? undefined,
        outputBasisUomId: selectedTaskDetails?.outputBasisUomId ?? undefined,
        budgetLineId: selectedTaskDetails?.budgetLineId || null,
        allocatedBudgetAmount:
          selectedTaskDetails?.budgetValue ||
          selectedTaskDetails?.allocatedBudgetAmount ||
          undefined,
        siteId: selectedTaskDetails?.siteId || null,
        workLevelSetting:
          selectedTaskDetails?.workLevelSetting ||
          (selectedTaskDetails?.segmentLevel
            ? getWorkLevelSettingFromSegmentLevel(
                selectedTaskDetails.segmentLevel,
              )
            : undefined),
        siteStructureUnitId:
          selectedTaskDetails?.siteStructureUnitId != null
            ? selectedTaskDetails.siteStructureUnitId.toString()
            : selectedTaskDetails?.siteStructureUnitName ?? null,
        floorNo:
          selectedTaskDetails?.floorNo != null
            ? selectedTaskDetails.floorNo.toString()
            : null,
        siteStructureSubUnitId:
          selectedTaskDetails?.siteStructureSubUnitId != null
            ? selectedTaskDetails.siteStructureSubUnitId.toString()
            : selectedTaskDetails?.siteStructureSubUnitName ?? null,
        locationId:
          selectedTaskDetails?.locationId != null
            ? selectedTaskDetails.locationId.toString()
            : selectedTaskDetails?.locationName ?? null,
      });

      // Load Work UOM if needed after task details are loaded
      loadWorkUomIfNeeded();
    }
  }, [
    selectedTaskDetails,
    workCatgoriesList,
    boqLines,
    reset,
    setRemainders,
    isEditMode,
  ]);

  // When the component is mounted directly in edit mode (initialEditMode=true,
  // i.e. user clicked Edit on the TaskQuickViewCard summary), the in-form Edit
  // button's onClick is bypassed. That handler normally loads dropdown data
  // (work category, owner, priority, BOQs, tools, etc.) and seeds local state
  // for tools and hyperlinks. Replicate that setup here so the same fields
  // populate as when the user clicks Edit from inside the form.
  const initialEditSetupDoneRef = useRef(false);
  useEffect(() => {
    if (
      !initialEditMode ||
      initialEditSetupDoneRef.current ||
      !selectedTaskDetails
    ) {
      return;
    }
    initialEditSetupDoneRef.current = true;
    (async () => {
      await loadDropdownData();

      if (
        selectedTaskDetails?.taskTools &&
        selectedTaskDetails.taskTools.length > 0
      ) {
        setTaskTools(selectedTaskDetails.taskTools.map(mapExistingTaskTool));
      } else {
        setTaskTools([]);
      }

      if (
        selectedTaskDetails?.hyperlinks &&
        selectedTaskDetails.hyperlinks.length > 0
      ) {
        const existingHyperlinks = selectedTaskDetails.hyperlinks.map(
          (hyperlink: any) => ({
            hyperlinkId: hyperlink.hyperlinkId,
            name: hyperlink.name,
            link: hyperlink.link,
          }),
        );
        setHyperlinks(existingHyperlinks);
      } else {
        setHyperlinks([]);
      }

      setDisableSubmit(false);
    })();
  }, [initialEditMode, selectedTaskDetails]);

  // Separate effect to handle budget line initialization when budget lines are loaded
  useEffect(() => {
    if (
      selectedTaskDetails?.budgetLineId &&
      budgetLines.length > 0 &&
      !isEditMode
    ) {
      const selectedBudget = budgetLines.find(
        (budget) => budget.value === selectedTaskDetails.budgetLineId,
      );
      if (selectedBudget?.extra) {
        setSelectedBudgetValue(selectedBudget.extra.budgetValue);
        setSelectedBudgetLine({
          lineId: selectedBudget.value as number,
          headerId: 0,
          budgetId: selectedBudget.extra.budgetId,
          budgetLineName: selectedBudget.extra.budgetLineName,
          budgetValue: selectedBudget.extra.budgetValue,
          allocatedLineValue: selectedBudget.extra.allocatedLineValue,
        });
      }
    }
  }, [budgetLines, selectedTaskDetails?.budgetLineId, isEditMode]);

  // Initialize taskTools when selectedTaskDetails changes
  useEffect(() => {
    if (
      selectedTaskDetails?.taskTools &&
      selectedTaskDetails.taskTools.length > 0
    ) {
      setTaskTools(selectedTaskDetails.taskTools.map(mapExistingTaskTool));
    } else {
      setTaskTools([]);
    }
  }, [selectedTaskDetails?.taskTools]);

  // Re-sync the auto-approval remarks specifically whenever the task's
  // remarks field changes or the user enters edit mode. The broader
  // `[selectedTaskDetails]` effect below also covers this, but it can be
  // out-of-step in narrow cases (e.g. the task data arrives before the
  // user toggles into edit mode and a later state change clobbers the
  // local string), leaving the textarea blank even though the value
  // exists on the server.
  useEffect(() => {
    if (isEditMode) {
      setAutoApprovalRemarks(selectedTaskDetails?.autoApprovalRemarks || "");
      setIsApprovalRequired(selectedTaskDetails?.approvalRequired || false);
    }
  }, [
    isEditMode,
    selectedTaskDetails?.taskId,
    selectedTaskDetails?.autoApprovalRemarks,
    selectedTaskDetails?.approvalRequired,
  ]);

  // Initialize approval hierarchy when selectedTaskDetails changes
  useEffect(() => {
    if (selectedTaskDetails) {
      setIsApprovalRequired(selectedTaskDetails?.approvalRequired || false);
      setAutoApprovalRemarks(selectedTaskDetails?.autoApprovalRemarks || "");
      if (
        selectedTaskDetails?.stateApprovalHierarchy &&
        selectedTaskDetails.stateApprovalHierarchy.length > 0
      ) {
        const approvalData = selectedTaskDetails.stateApprovalHierarchy.map(
          (approval: any) => ({
            roleId: approval.roleId,
            level: approval.level,
            userId: approval.userId,
            approvalStage: approval.approvalStage,
            stateApprovalHierarchyId: approval.stateApprovalHierarchyId,
          }),
        );
        setApprovals({
          open: false,
          data: approvalData,
        });
      } else {
        setApprovals({
          open: false,
          data: [],
        });
      }
    }
  }, [selectedTaskDetails]);

  useEffect(() => {
    if (boqLineIdsWatch?.length) {
      const filteredData: any = [];
      // In edit mode use full list so task BOQs are always found (not restricted by activityBoqList filter)
      const listToUse =
        isEditMode && allActivityBoqList?.length > 0
          ? allActivityBoqList
          : activityBoqList;
      const firstBoq: any = listToUse?.find(
        (item: any) => item.activityBoqId == boqLineIdsWatch[0],
      );

      if (firstBoq) {
        const targetNumber = firstBoq.boqTargetNumber;
        // In edit mode include all selected BOQs (no same-target restriction); in view/add mode only same target
        boqLineIdsWatch.forEach((id: any) => {
          const boqLine: any = listToUse?.find(
            (item: any) => item.activityBoqId == id,
          );
          const includeLine =
            isEditMode || (boqLine && boqLine.boqTargetNumber === targetNumber);
          if (boqLine && includeLine) {
            const { lines, activityBoqId, boqTargetNumber, boqTitle } =
              boqLine || {};
            const boqLineName = (boqLine as any)?.boqLineName;
            if (lines) {
              const boqLines = lines?.map((line: any) => {
                const taskBoq = selectedTaskDetails?.taskBoqs?.find(
                  (tb: any) => tb.activityBoqId === activityBoqId,
                );
                return {
                  ...line,
                  boqTargetNumber,
                  boqTitle,
                  boqLineName: boqLineName || undefined,
                  activityBoqId,
                  allotedQuantity: line.allotedQuantity,
                  taskAllocatedQuantity: taskBoq
                    ? Number(taskBoq.allotedQuantity)
                    : undefined,
                  taskBoqRate:
                    taskBoq && taskBoq.rate !== undefined && taskBoq.rate !== null
                      ? Number(taskBoq.rate)
                      : undefined,
                  taskAvailableQuantity:
                    line.taskAvailableQuantity ??
                    taskBoq?.taskAvailableQuantity ??
                    line.allotedQuantity ??
                    0,
                  key: `${activityBoqId}_${line.activityBoqLineId}`,
                };
              });
              filteredData.push(...boqLines);
            }
          }
        });
      }
      setBoqLinesDataSource(filteredData);
    } else {
      setBoqLinesDataSource([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    boqLineIdsWatch,
    activityBoqList,
    allActivityBoqList,
    isEditMode,
    selectedTaskDetails?.taskBoqs,
  ]);

  const onResourceSelectChange = (
    newSelectedRowKeys: React.Key[],
    selectedRows?: any,
  ) => {
    setSelectedResourceKeys(newSelectedRowKeys);
    if (!newSelectedRowKeys) {
      setEditingResourceKeys(() => []);
    }
    const editableRowKeys: any = [];
    newSelectedRowKeys?.forEach((key) => {
      const selectedRow = selectedRows.find(
        (row: any) => `${row.activityResourceId}` === key,
      );
      editableRowKeys.push(key);
      if (selectedRow?.resourceCount) {
        resourceForm.setFieldsValue({
          [`resourceCount_${key}`]: selectedRow?.resourceCount,
        });
      }
    });
    setEditingResourceKeys(editableRowKeys);
  };

  const isResourceEditing = (record: any) =>
    editingResourceKeys.includes(`${record.activityResourceId}`);

  // Handle edit mode changes - pre-populate form fields with existing BOQ data and Resource data
  // Use full BOQ list (allActivityBoqList) when available so edit mode shows all task BOQs regardless of site hierarchy filter
  useEffect(() => {
    if (
      isEditMode &&
      selectedTaskDetails?.taskBoqs &&
      selectedTaskDetails.taskBoqs.length > 0
    ) {
      // Use full list so task's BOQs are always found (not restricted by activityBoqList site filter)
      const listToUse =
        allActivityBoqList?.length > 0 ? allActivityBoqList : activityBoqList;
      if (listToUse.length === 0) return;

      // Get all existing BOQ IDs from task - no same-target restriction
      const existingBoqIds = selectedTaskDetails.taskBoqs.map(
        (taskBoq: any) => taskBoq.activityBoqId,
      );

      // Include all task BOQs that exist in the list (no filtering by target)
      const filteredBoqIds = existingBoqIds.filter((boqId: any) =>
        listToUse.some((item: any) => item.activityBoqId === boqId),
      );

      if (filteredBoqIds.length > 0) {
        setValue("boqLineIds", filteredBoqIds);

        // Pre-populate form fields with existing BOQ data for edit mode
        selectedTaskDetails.taskBoqs.forEach((taskBoq: any) => {
          if (filteredBoqIds.includes(taskBoq.activityBoqId)) {
            setValue(
              `allocateBOQQuantity_${taskBoq.activityBoqId}`,
              taskBoq.allotedQuantity,
              {
                shouldDirty: false, // Don't mark as dirty initially
                shouldTouch: false,
              },
            );
            if (taskBoq.rate !== undefined && taskBoq.rate !== null) {
              setValue(`boqRate_${taskBoq.activityBoqId}`, Number(taskBoq.rate), {
                shouldDirty: false,
                shouldTouch: false,
              });
            }
          }
        });

        // Pre-populate table data with all existing BOQ items (no same-target restriction)
        const filteredData: any = [];
        filteredBoqIds.forEach((activityBoqId: any) => {
          const boqLine: any = listToUse.find(
            (item: any) => item.activityBoqId == activityBoqId,
          );
          const { lines, boqTargetNumber, boqTitle } = boqLine || {};
          const boqLineName = (boqLine as any)?.boqLineName;
          if (boqLine) {
            const boqLines = lines?.map((line: any) => {
              const taskBoq = selectedTaskDetails.taskBoqs.find(
                (tb: any) => tb.activityBoqId === activityBoqId,
              );
              return {
                ...line,
                boqTargetNumber,
                boqTitle,
                boqLineName: boqLineName || undefined,
                activityBoqId,
                allotedQuantity: line.allotedQuantity,
                taskAllocatedQuantity: taskBoq
                  ? Number(taskBoq.allotedQuantity)
                  : undefined,
                taskBoqRate:
                  taskBoq && taskBoq.rate !== undefined && taskBoq.rate !== null
                    ? Number(taskBoq.rate)
                    : undefined,
                taskBoqRateAnalysisQty:
                  taskBoq?.rateAnalysisQty != null
                    ? Number(taskBoq.rateAnalysisQty)
                    : undefined,
                taskBoqRateAnalysisUomId: taskBoq?.rateAnalysisUomId ?? undefined,
                taskBoqRateAnalysisRate:
                  taskBoq?.rateAnalysisRate != null
                    ? Number(taskBoq.rateAnalysisRate)
                    : undefined,
                taskAvailableQuantity:
                  line.taskAvailableQuantity ??
                  taskBoq?.taskAvailableQuantity ??
                  line.allotedQuantity ??
                  0,
                key: `${activityBoqId}_${line.activityBoqLineId}`,
              };
            });
            filteredData.push(...boqLines);
          }
        });
        setBoqLinesDataSource(filteredData);
      }
    }

    // Pre-populate resource selection when entering edit mode
    if (
      isEditMode &&
      selectedTaskDetails?.taskResources &&
      activityResourcesList.length > 0
    ) {
      const existingResourceIds = selectedTaskDetails.taskResources.map(
        (taskResource: any) => `${taskResource.activityResourceId}`,
      );
      setSelectedResourceKeys(existingResourceIds);
      setEditingResourceKeys(existingResourceIds);

      // Pre-populate resource counts and the new rate / quantity / UOM fields
      selectedTaskDetails.taskResources.forEach((taskResource: any) => {
        resourceForm.setFieldsValue({
          [`resourceCount_${taskResource.activityResourceId}`]:
            taskResource.resourceCount || 1,
          ...(taskResource.rate !== undefined && taskResource.rate !== null
            ? {
                [`rate_${taskResource.activityResourceId}`]: Number(
                  taskResource.rate,
                ),
              }
            : {}),
          ...(taskResource.quantity !== undefined &&
          taskResource.quantity !== null
            ? {
                [`quantity_${taskResource.activityResourceId}`]: Number(
                  taskResource.quantity,
                ),
              }
            : {}),
          ...(taskResource.workUomId !== undefined &&
          taskResource.workUomId !== null
            ? {
                [`workUomId_${taskResource.activityResourceId}`]:
                  taskResource.workUomId,
              }
            : {}),
          ...(taskResource.rateAnalysisQty != null
            ? {
                [`rateAnalysisQty_${taskResource.activityResourceId}`]: Number(
                  taskResource.rateAnalysisQty,
                ),
              }
            : {}),
          ...(taskResource.rateAnalysisUomId != null
            ? {
                [`rateAnalysisUomId_${taskResource.activityResourceId}`]:
                  taskResource.rateAnalysisUomId,
              }
            : {}),
          ...(taskResource.rateAnalysisRate != null
            ? {
                [`rateAnalysisRate_${taskResource.activityResourceId}`]: Number(
                  taskResource.rateAnalysisRate,
                ),
              }
            : {}),
        });
      });
    }
  }, [
    isEditMode,
    selectedTaskDetails,
    activityBoqList,
    allActivityBoqList,
    activityResourcesList,
    setValue,
    form,
    resourceForm,
  ]);

  // Watch for changes in BOQ fields and mark them as dirty
  useEffect(() => {
    if (isEditMode) {
      const subscription = watch((value, { name, type }) => {
        // Prevent infinite loops by checking if we're already processing
        if (isProcessingChangesRef.current) {
          return;
        }

        if (
          name &&
          name.startsWith("allocateBOQQuantity_") &&
          type === "change"
        ) {
          // Mark the field as dirty when it changes, but avoid infinite loop
          const currentValue = form.getFieldValue(name);
          if (currentValue !== value[name]) {
            // Use setTimeout to break the synchronous loop
            setTimeout(() => {
              isProcessingChangesRef.current = true;
              setValue(name, value[name], {
                shouldDirty: true,
                shouldTouch: false,
              });
              // Reset the flag after a short delay
              setTimeout(() => {
                isProcessingChangesRef.current = false;
              }, 100);
            }, 0);
          }
        }

        // Also watch for BOQ line selection changes
        if (name === "boqLineIds" && type === "change") {
          const currentBoqLineIds = form.getFieldValue("boqLineIds") || [];
          const newBoqLineIds = (value.boqLineIds || []).filter(
            (id): id is string => id !== undefined,
          );

          // Only update if values are actually different to avoid infinite loop
          if (
            JSON.stringify(currentBoqLineIds) !== JSON.stringify(newBoqLineIds)
          ) {
            // Use setTimeout to break the synchronous loop
            setTimeout(() => {
              isProcessingChangesRef.current = true;
              setValue("boqLineIds", newBoqLineIds, {
                shouldDirty: true,
                shouldTouch: false,
              });
              // Reset the flag after a short delay
              setTimeout(() => {
                isProcessingChangesRef.current = false;
              }, 100);
            }, 0);
          }
        }
      });

      return () => subscription.unsubscribe();
    }
  }, [isEditMode, watch, setValue, form]);

  // Store the original allowEdit value when component loads
  useEffect(() => {
    if (
      TaskListInDetailModal.allowEdit !== undefined &&
      originalAllowEdit === undefined
    ) {
      setOriginalAllowEdit(TaskListInDetailModal.allowEdit);
    }
  }, [TaskListInDetailModal.allowEdit, originalAllowEdit]);

  // Monitor task status changes and update allowEdit accordingly
  useEffect(() => {
    if (selectedTaskDetails?.taskStatus && originalAllowEdit !== undefined) {
      const isTaskEditable = isTaskStatusEditable(
        selectedTaskDetails.taskStatus,
      );

      // Check if current user is in hierarchyUserIds - filter out duplicates and invalid values
      const isInHierarchy =
        selectedTaskDetails?.hierarchyUserIds &&
        Array.isArray(selectedTaskDetails.hierarchyUserIds) &&
        currentUser?.id &&
        selectedTaskDetails.hierarchyUserIds
          .filter((id: number) => id > 0) // Filter out 0 and negative values
          .includes(currentUser?.id);

      // Check if current user is admin
      const isAdmin = currentUser?.genericRole?.toLowerCase() === "admin";

      // Check if current user is the task owner
      const isTaskOwner = selectedTaskDetails?.taskOwnerId === currentUser?.id;

      // Base conditions: project status, phase status, activity status, and lock checks
      // Recalculate from task detail response instead of relying on originalAllowEdit
      const projectStatus =
        selectedTaskDetails?.projectStatus || projectResponse?.status || "";
      const phaseStatus =
        selectedTaskDetails?.phaseStatus || phaseResponse?.status || "";
      const activityStatus = selectedTaskDetails?.activityStatus || "";
      const isLocked = (projectResponse as any)?.isLocked || false;
      const lockedBy = (projectResponse as any)?.lockedBy;

      const baseConditionsMet =
        !["onhold", "cancelled", "archived", "closed"].includes(
          projectStatus?.toLowerCase(),
        ) &&
        !["onhold", "cancelled", "archived", "closed"].includes(
          phaseStatus?.toLowerCase(),
        ) &&
        !["onhold", "cancelled", "archived", "closed", "deleted"].includes(
          activityStatus?.toLowerCase(),
        ) &&
        (isLocked === false || lockedBy == currentUser?.id);

      // Check module-level edit permission
      const hasModulePermission = hasModuleEditPermission("Tasks");

      // Update allowEdit in store based on current task status, original allowEdit, and hierarchy
      // Start with restrictive model: false by default
      let updatedAllowEdit = false;

      // Grant edit access if:
      // 1. Task status is editable AND base conditions are met AND
      // 2. Conditions:
      //    - Owner: Module permission AND owner check
      //    - Admin: Module permission only (hierarchy not needed)
      //    - Others: Module permission AND hierarchy (both required)
      if (isTaskEditable && baseConditionsMet) {
        if (isTaskOwner && hasModulePermission) {
          updatedAllowEdit = true;
        } else if (isAdmin && hasModulePermission) {
          updatedAllowEdit = true;
        } else if (hasModulePermission && isInHierarchy) {
          updatedAllowEdit = true;
        }
      }

      if (updatedAllowEdit !== TaskListInDetailModal.allowEdit) {
        dispatch(
          updateTaskListDetailsModalSlice({
            allowEdit: updatedAllowEdit,
          }),
        );

        // If task is no longer editable and we're in edit mode, exit edit mode
        if (!updatedAllowEdit && isEditMode) {
          setIsEditMode(false);
          setDisableSubmit(false);
        }
      }
    }
  }, [
    selectedTaskDetails?.taskStatus,
    selectedTaskDetails?.hierarchyUserIds,
    selectedTaskDetails?.projectStatus,
    selectedTaskDetails?.phaseStatus,
    selectedTaskDetails?.activityStatus,
    selectedTaskDetails?.taskOwnerId,
    (projectResponse as any)?.isLocked,
    (projectResponse as any)?.lockedBy,
    phaseResponse?.status,
    originalAllowEdit,
    TaskListInDetailModal.allowEdit,
    dispatch,
    isEditMode,
    messageApi,
    isTaskStatusEditable,
    currentUser?.id,
    hasModuleEditPermission,
  ]);

  // Form values come back as strings while projectUsers[].value is numeric,
  // so normalize with Number() before comparing — strict !== misses matches
  // and lets the owner leak into the team list.
  const teamOptions = projectUsers
    .filter(
      (user) =>
        selectedOwnerId && Number(user.value) !== Number(selectedOwnerId),
    )
    .map((usr: any) => {
      return {
        ...usr,
        value: usr.value, // Use the existing value (userId)
      };
    });

  // Mutual exclusion: hide users already chosen in Associated Team from the
  // Owner dropdown. Both fields here use userId as their value.
  const selectedTeam: any[] = watch("projectUserId") || [];
  const ownerOptions = projectUsers.filter(
    (user: any) =>
      !selectedTeam.some((id: any) => Number(id) === Number(user.value)),
  );

  // Wrap each selected chip in a Tooltip so the user's full name is visible
  // on hover even when many chips are present and the column is narrow.
  const teamTagRender = (props: any) => {
    const { label, value, closable, onClose } = props;
    const matched = teamOptions.find(
      (o: any) => Number(o.value) === Number(value),
    );
    const displayLabel = label || matched?.label || String(value);
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tooltip title={displayLabel} placement="top">
        <Tag
          onMouseDown={onPreventMouseDown}
          closable={closable}
          onClose={onClose}
          style={{ marginInlineEnd: 4, maxWidth: "100%" }}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "inline-block",
              maxWidth: 120,
              verticalAlign: "bottom",
            }}
          >
            {displayLabel}
          </span>
        </Tag>
      </Tooltip>
    );
  };

  // Seed the Associated Team field once both the task's taskUsers and the
  // project user list are loaded. taskUsers[].projectUserId is the membership
  // id; teamOptions[].value is the userId. We look up each task user against
  // projectUsers (matching by projectUserId, falling back to userId) and write
  // the matched option values into the form. Owner is excluded — it is stored
  // in taskUsers but rendered separately and filtered out of teamOptions.
  useEffect(() => {
    const taskUsers = selectedTaskDetails?.taskUsers;
    if (
      !isEditMode ||
      !Array.isArray(taskUsers) ||
      taskUsers.length === 0 ||
      projectUsers.length === 0
    ) {
      return;
    }
    const teamIds = taskUsers
      .filter((user: any) => user?.taskUserType !== "Owner")
      .map((user: any) => {
        const candidateId = user.projectUserId ?? user.userId;
        const matched = projectUsers.find(
          (pu: any) =>
            (pu.projectUserId != null &&
              Number(pu.projectUserId) === Number(candidateId)) ||
            Number(pu.value) === Number(candidateId) ||
            Number((pu as any).userId) === Number(candidateId),
        );
        return matched?.value;
      })
      .filter((v: any) => v !== undefined && v !== null);
    setValue("projectUserId", teamIds, { shouldDirty: false });
  }, [
    isEditMode,
    selectedTaskDetails?.taskUsers,
    projectUsers,
    setValue,
  ]);

  // Handle budget line selection to update budget value
  useEffect(() => {
    if (budgetLineIdWatch && budgetLines.length > 0) {
      const selectedBudget = budgetLines.find(
        (budget) => budget.value === budgetLineIdWatch,
      );
      if (selectedBudget?.extra) {
        setSelectedBudgetValue(selectedBudget.extra.budgetValue);
        setSelectedBudgetLine({
          lineId: selectedBudget.value as number,
          headerId: 0, // Not needed for display
          budgetId: selectedBudget.extra.budgetId,
          budgetLineName: selectedBudget.extra.budgetLineName,
          budgetValue: selectedBudget.extra.budgetValue,
          allocatedLineValue: selectedBudget.extra.allocatedLineValue,
        });
      }
    } else if (budgetLineIdWatch === null || budgetLineIdWatch === undefined) {
      // Only clear when explicitly set to null/undefined, not when budgetLines is empty
      setSelectedBudgetValue(null);
      setSelectedBudgetLine(null);
    }
  }, [budgetLineIdWatch, budgetLines]);

  // Update selectedBudgetLine with fresh data when budgetLines are fetched in edit mode
  useEffect(() => {
    if (
      isEditMode &&
      budgetLines.length > 0 &&
      selectedTaskDetails?.budgetLineId
    ) {
      const selectedBudget = budgetLines.find(
        (budget) => budget.value === selectedTaskDetails.budgetLineId,
      );
      if (selectedBudget?.extra) {
        setSelectedBudgetLine({
          lineId: selectedBudget.value as number,
          headerId: 0,
          budgetId: selectedBudget.extra.budgetId,
          budgetLineName: selectedBudget.extra.budgetLineName,
          budgetValue: selectedBudget.extra.budgetValue,
          allocatedLineValue: selectedBudget.extra.allocatedLineValue,
        });
      }
    }
  }, [budgetLines, isEditMode, selectedTaskDetails?.budgetLineId]);

  // Sync form values with state for tools
  useEffect(() => {
    if (toolTypeWatch !== selectedToolType) {
      setSelectedToolType(toolTypeWatch || null);
      if (toolTypeWatch !== selectedToolType) {
        setSelectedTool(null);
        setValue("toolMachinery", null);
      }
    }
  }, [toolTypeWatch, selectedToolType, setValue]);

  useEffect(() => {
    if (toolMachineryWatch !== selectedTool) {
      setSelectedTool(toolMachineryWatch || null);
    }
  }, [toolMachineryWatch, selectedTool]);

  const [isLoadingProjectUsers, setIsLoadingProjectUsers] = useState(false);

  const fetchProjectUsers = async () => {
    if (isLoadingProjectUsers) return;
    setIsLoadingProjectUsers(true);
    try {
      const response: any = await projectsApis.fetchProjectUsers(
        (projectId || 0) as number,
      );
      const { data: usersList, status } = response || {};
      if (status) {
        const formatted = usersList?.map(
          (user: { userId: string; userName: string; roleName: string }) => ({
            ...user,
            label: user?.userName,
            value: user?.userId,
            roleName: user?.roleName,
          }),
        );
        setProjectUsers(formatted);
      }
    } catch (error) {
      // Error handled silently
    } finally {
      setIsLoadingProjectUsers(false);
    }
  };

  const fetchContractorList = useCallback(async (siteIds: string) => {
    try {
      const { data, status } = await contractorsApis.fetchContractorBySiteId({
        id: siteIds, // Pass all site IDs (comma-separated)
      });

      if (status && data) {
        const _contractorOptions = data.map((item: any) => ({
          label: item?.companyName,
          value: item?.contractorID?.toString(),
        }));

        setContractorOptions(_contractorOptions);
      } else {
        setContractorOptions([]);
      }
    } catch (error) {
      setContractorOptions([]);
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility) {
        const options = currentUser.siteAccessibility.map((item: any) => ({
          label: item.siteName,
          value: item.siteID,
        }));
        setSiteOptions(options);

        const allIds = siteAccessibility
          .map((item: any) => item.siteID)
          .join(",");
        setAllSiteIds(allIds);
      }
    }
  }, [currentUser]);

  useEffect(() => {
    if (allSiteIds) {
      fetchContractorList(allSiteIds);
    }
  }, [allSiteIds, fetchContractorList]);

  const [isLoadingTaskDetails, setIsLoadingTaskDetails] = useState(false);

  const fetchTaskById = async () => {
    if (TaskListInDetailModal.id && !isLoadingTaskDetails) {
      setIsLoadingTaskDetails(true);
      try {
        const response: any = await tasksApis.fetchTaskById(
          TaskListInDetailModal.id as number,
          derivedProjectStatus || "Active",
        );
        const { data, status } = response || {};
        if (status) {
          setRemainders({ ...data.reminders, isLoadedFromAPI: true });
          dispatch(
            updateTaskListDetailsModalSlice({
              selectedTaskDetails: {
                ...data,
                taskUsers: data.taskUsers?.map((item: any) => {
                  return {
                    ...item,
                    value: item.projectUserId,
                    label: item.taskUserName,
                  };
                }),
                taskResources: data.taskResources?.map((item: any) => {
                  return {
                    ...item,
                    laborCategory: item.laborCategoryName,
                  };
                }),
              },
            }),
          );
          // Scroll to top and reset tab when task data is successfully loaded
          window.scrollTo(0, 0);
          setActiveTab("comments");
        }
      } catch (error) {
        // Error handled silently
      } finally {
        setIsLoadingTaskDetails(false);
      }
    }
  };

  const [isLoadingProjectTags, setIsLoadingProjectTags] = useState(false);

  const fetchProjectTags = async () => {
    if (isLoadingProjectTags) return;
    setIsLoadingProjectTags(true);
    try {
      const response: any = await tasksApis.fetchProjectTags(
        (projectId || 0) as number,
      );
      const { data, status } = response || {};
      if (status) {
        setProjectTagsList(
          data?.map((item: any) => ({
            ...item,
            label: item.tagName,
            value: item.tagId,
          })),
        );
      }
    } catch (error) {
      // Error handled silently
    } finally {
      setIsLoadingProjectTags(false);
    }
  };

  const [isLoadingPriority, setIsLoadingPriority] = useState(false);

  const fetchPriority = async () => {
    if (isLoadingPriority) return;
    setIsLoadingPriority(true);
    try {
      const response: any = await tasksApis.priorityList();
      const { data, status } = response || {};
      if (status) {
        setPriorityList(
          data?.map((item: { priorityName: any; priorityId: any }) => ({
            ...item,
            label: item.priorityName,
            value: item.priorityId,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching priority list:", error);
      messageApi.open({
        type: "error",
        content: "Failed to fetch priority list. Please try again.",
      });
    } finally {
      setIsLoadingPriority(false);
    }
  };

  const [isLoadingWorkCategories, setIsLoadingWorkCategories] = useState(false);

  const fetchWorkCatgoriesList = async () => {
    if (isLoadingWorkCategories) return;
    setIsLoadingWorkCategories(true);
    try {
      const response: any = await tasksApis.fetchWorkCatgoriesList();
      const { data, status } = response || {};
      if (status) {
        const formatted = data.map(
          (item: { workCategoryName: any; workCategoryId: any }) => ({
            label: item.workCategoryName,
            value: item.workCategoryId,
          }),
        );
        setWorkCatgoriesList(formatted);
      }
    } catch (error) {
      console.error("Error fetching work categories:", error);
      messageApi.open({
        type: "error",
        content: "Failed to fetch work categories. Please try again.",
      });
    } finally {
      setIsLoadingWorkCategories(false);
    }
  };

  const fetchWorkUomsList = async () => {
    try {
      const response = await tasksApis.workUom({});
      const { data, status } = response || {};
      if (status) {
        setWorkUomsList(
          data?.map((item: any) => ({
            label: item.uomName,
            value: item.workUomId,
          })) || [],
        );
      }
    } catch (error) {
      console.error("Error fetching work UOMs:", error);
      messageApi.error("Failed to fetch work UOMs");
    }
  };

  // Machinery split-cost material options for the per-tool materials manager.
  const fetchMaterialOptions = async () => {
    try {
      const response = await tasksApis.fetchMachineryMaterialDropDown();
      const { data, status } = response || {};
      if (status) {
        setMaterialOptions(
          (data || []).map((item: any) => ({
            label: item.materialName ?? item.name ?? item.label,
            value: item.id ?? item.materialId ?? item.value,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching machinery materials:", error);
    }
  };

  const fetchToolsAndMachineryTypes = async () => {
    try {
      const response = await tasksApis.fetchToolsAndMachineryTypes();
      const { data, status } = response || {};
      if (status) {
        setToolsAndMachineryTypes(
          data?.map((item: any) => ({
            label: item.typeName,
            value: item.toolTypeId,
          })) || [],
        );
      }
    } catch (error) {
      console.error("Error fetching tools & machinery types:", error);
      messageApi.error("Failed to fetch tools & machinery types");
    }
  };

  const fetchToolsAndMachinery = async () => {
    try {
      const response = await tasksApis.fetchToolsAndMachinery();
      const { data, status } = response || {};
      if (status) {
        setToolsAndMachineryList(
          data?.map((item: any) => ({
            label: item.toolName,
            value: item.id,
            extra: {
              typeName: item.typeName,
              typeId: item.typeId,
            },
          })) || [],
        );
      }
    } catch (error) {
      console.error("Error fetching tools & machinery:", error);
      messageApi.error("Failed to fetch tools & machinery");
    }
  };

  const fetchBudgetLines = async () => {
    if (
      isLoadingBudgetLines ||
      !projectId ||
      budgetLinesFetchedRef.current === projectId
    )
      return;

    setIsLoadingBudgetLines(true);
    budgetLinesFetchedRef.current = projectId;

    try {
      const response = await projectsApis.fetchBudgetLines(projectId, {
        budgetFor: "Service",
      });
      const { data, status }: { data: BudgetLine[]; status: boolean } =
        response || {};
      if (status && data?.length > 0) {
        setHasProjectBudget(true);
        setBudgetLines(
          data?.map((item) => ({
            label: item.budgetLineName,
            value: item.lineId,
            extra: {
              budgetValue: item.budgetValue,
              allocatedLineValue: item.allocatedLineValue,
              budgetId: item.budgetId,
              budgetLineName: item.budgetLineName,
            },
          })),
        );
      } else {
        setHasProjectBudget(false);
        setBudgetLines([]);
      }
    } catch (error) {
      console.error("Error fetching budget lines:", error);
      setHasProjectBudget(false);
      setBudgetLines([]);
      budgetLinesFetchedRef.current = null; // Reset on error to allow retry
    } finally {
      setIsLoadingBudgetLines(false);
    }
  };

  // Fetch budget lines when component mounts or projectId changes
  useEffect(() => {
    if (projectId && budgetLinesFetchedRef.current !== projectId) {
      fetchBudgetLines();
    }
  }, [projectId]);

  // Site Hierarchy API functions
  const loadTowerOptions = useCallback(
    async (siteIdParam?: string | number) => {
      if (!siteIdParam) {
        setTowerOptions([]);
        return;
      }
      try {
        const response = await sitebreakdownMasterApis.fetchUnitsBySiteId({
          siteId: siteIdParam,
        });
        if (response?.status && Array.isArray(response.data)) {
          setTowerOptions(
            response.data.map((unit: any) => ({
              label: unit.unitName,
              value: unit.id?.toString(),
            })),
          );
        } else {
          setTowerOptions([]);
        }
      } catch (error) {
        console.error("Error fetching tower options:", error);
        setTowerOptions([]);
      }
    },
    [],
  );

  const loadFloorOptions = useCallback(
    async (siteIdParam?: string | number, unitId?: string | number) => {
      if (!siteIdParam || !unitId) {
        setFloorOptions([]);
        return;
      }
      try {
        const response =
          await sitebreakdownMasterApis.fetchFloorNumbersByUnitId({
            siteId: siteIdParam,
            unitId,
          });
        if (response?.status && Array.isArray(response.data)) {
          setFloorOptions(
            response.data.map((floor: any) => ({
              label: floor.floorNo,
              value: floor.floorNo?.toString(),
            })),
          );
        } else {
          setFloorOptions([]);
        }
      } catch (error) {
        console.error("Error fetching floor options:", error);
        setFloorOptions([]);
      }
    },
    [],
  );

  const loadFlatOptions = useCallback(
    async (
      siteIdParam?: string | number,
      unitId?: string | number,
      floorNoValue?: string | null,
    ) => {
      if (!siteIdParam || !unitId) {
        setFlatOptions([]);
        return;
      }
      try {
        const response = await sitebreakdownMasterApis.fetchSubUnitsByUnitId({
          siteId: siteIdParam,
          unitId,
          floorNo: floorNoValue ?? undefined,
        });
        if (response?.status && Array.isArray(response.data)) {
          setFlatOptions(
            response.data.map((flat: any) => ({
              label: flat.subUnitName || flat.unitName,
              value: flat.id?.toString(),
            })),
          );
        } else {
          setFlatOptions([]);
        }
      } catch (error) {
        console.error("Error fetching flat options:", error);
        setFlatOptions([]);
      }
    },
    [],
  );

  const loadLocationOptions = useCallback(
    async (siteIdParam?: string | number) => {
      if (!siteIdParam) {
        setLocationOptions([]);
        return;
      }
      try {
        const response = await sitebreakdownMasterApis.fetchLocationsBySiteId({
          siteId: siteIdParam,
        });
        if (response?.status && Array.isArray(response.data)) {
          setLocationOptions(
            response.data.map((loc: any) => ({
              label: loc.locationName,
              value: loc.id?.toString(),
            })),
          );
        } else {
          setLocationOptions([]);
        }
      } catch (error) {
        console.error("Error fetching location options:", error);
        setLocationOptions([]);
      }
    },
    [],
  );

  // Load location options when projectSiteId changes
  useEffect(() => {
    if (projectSiteId) {
      loadLocationOptions(Number(projectSiteId));
    } else {
      setLocationOptions([]);
    }
  }, [projectSiteId, loadLocationOptions]);

  // Load tower options when hierarchy is enabled and site is selected
  useEffect(() => {
    if (!shouldShowHierarchy) {
      setTowerOptions([]);
      setFloorOptions([]);
      setFlatOptions([]);
      setValue("siteStructureUnitId", null);
      setValue("floorNo", null);
      setValue("siteStructureSubUnitId", null);
      return;
    }
    if (!projectSiteId) {
      // Site is still loading; don't wipe form values that may have just been
      // populated from selectedTaskDetails — wait for projectSiteId to arrive.
      return;
    }
    loadTowerOptions(Number(projectSiteId));
  }, [shouldShowHierarchy, projectSiteId, loadTowerOptions, setValue]);

  // Load floor options when unit is selected and hierarchy is FLOOR or FLAT
  useEffect(() => {
    const hierarchyDisablesFloor =
      !shouldShowHierarchy ||
      (workLevelSetting !== "FLOOR" && workLevelSetting !== "FLAT");
    if (hierarchyDisablesFloor) {
      setFloorOptions([]);
      if (watch("floorNo")) {
        setValue("floorNo", null);
      }
      return;
    }
    if (!projectSiteId || !selectedUnitId) {
      // Dependencies still loading; preserve form value while we wait.
      return;
    }
    loadFloorOptions(Number(projectSiteId), selectedUnitId);
  }, [
    shouldShowHierarchy,
    projectSiteId,
    selectedUnitId,
    workLevelSetting,
    loadFloorOptions,
    setValue,
    watch,
  ]);

  // Load flat options when unit and floor are selected and hierarchy is FLAT
  useEffect(() => {
    const hierarchyDisablesFlat =
      !shouldShowHierarchy || workLevelSetting !== "FLAT";
    if (hierarchyDisablesFlat) {
      setFlatOptions([]);
      if (watch("siteStructureSubUnitId")) {
        setValue("siteStructureSubUnitId", null);
      }
      return;
    }
    if (!projectSiteId || !selectedUnitId) {
      return;
    }
    loadFlatOptions(
      Number(projectSiteId),
      selectedUnitId,
      selectedFloorNo ?? null,
    );
  }, [
    shouldShowHierarchy,
    projectSiteId,
    workLevelSetting,
    selectedUnitId,
    selectedFloorNo,
    loadFlatOptions,
    setValue,
    watch,
  ]);

  const [isLoadingActivityResources, setIsLoadingActivityResources] =
    useState(false);

  const fetchActivityResourcesList = async () => {
    if (isLoadingActivityResources) return;
    setIsLoadingActivityResources(true);
    try {
      const response: any = await tasksApis.fetchActivityResources(
        (id || "") as string,
      );
      const { data, status } = response || {};
      if (status) {
        // Map laborCategoryName to laborCategory
        const formattedData = data?.map((resource: any) => ({
          ...resource,
          laborCategory: resource.laborCategoryName,
        }));
        setActivityResourcesList(formattedData || []);
      }
    } catch (error) {
      // Error handled silently
    } finally {
      setIsLoadingActivityResources(false);
    }
  };

  const [isLoadingActivityBoqs, setIsLoadingActivityBoqs] = useState(false);

  const fetchActivityBoqs = async () => {
    if (isLoadingActivityBoqs) return;
    setIsLoadingActivityBoqs(true);
    try {
      const response: any = await tasksApis.fetchActivityBoqs(
        (id || "") as string,
      );
      const { data, status } = response || {};
      if (status) {
        setAllActivityBoqList(data);
        // Filter based on site hierarchy if available
        filterBoqListBySiteHierarchy(data);
      }
    } catch (error) {
      // Error handled silently
    } finally {
      setIsLoadingActivityBoqs(false);
    }
  };

  // Filter BOQ list based on site hierarchy selection
  const filterBoqListBySiteHierarchy = useCallback(
    (boqList: any[]) => {
      // Use the watch values that are already defined at component level
      // These are reactive and will have the latest values
      const unitId = selectedUnitId;
      const floorNo = selectedFloorNo;
      const subUnitId = selectedSubUnitId;
      const locationId = selectedLocationId;
      const currentWorkLevelSetting = workLevelSetting;

      // If site hierarchy is not selected or is NONE, show all BOQs
      if (
        !currentWorkLevelSetting ||
        currentWorkLevelSetting === "NONE" ||
        !unitId
      ) {
        setActivityBoqList(boqList);
        return;
      }

      // Filter BOQs based on site hierarchy (site hierarchy fields are at BOQ level, not line level)
      const filteredBoqs = boqList.filter((boq: any) => {
        // Check if BOQ matches the selected site hierarchy
        // If a field is selected, the BOQ MUST have that field and it must match
        // If a BOQ doesn't have site hierarchy data at all, exclude it when filtering

        // Always check unitId (Tower/Block or Villa) - BOQ must have this field
        if (unitId) {
          // Convert both to numbers for comparison (handles string/number mismatch)
          const boqUnitId = boq.siteStructureUnitId
            ? Number(boq.siteStructureUnitId)
            : null;
          const selectedUnitIdNum = Number(unitId);

          // If BOQ doesn't have site hierarchy data, exclude it
          if (
            boqUnitId === null ||
            boqUnitId === undefined ||
            isNaN(boqUnitId)
          ) {
            return false; // BOQ doesn't have unitId, exclude it
          }
          if (boqUnitId !== selectedUnitIdNum) {
            return false; // UnitId doesn't match
          }
        }

        // For FLOOR and FLAT, check floorNo - BOQ must have this field if we're filtering by it
        if (
          (currentWorkLevelSetting === "FLOOR" ||
            currentWorkLevelSetting === "FLAT") &&
          floorNo
        ) {
          // If BOQ doesn't have floorNo, exclude it
          const boqFloorNo = boq.floorNo ? String(boq.floorNo).trim() : null;
          const selectedFloorNoStr = String(floorNo).trim();

          if (!boqFloorNo || boqFloorNo === "") {
            return false; // BOQ doesn't have floorNo, exclude it
          }
          if (boqFloorNo !== selectedFloorNoStr) {
            return false; // FloorNo doesn't match
          }
        }

        // For FLAT, check subUnitId (Flat) - BOQ must have this field if we're filtering by it
        if (currentWorkLevelSetting === "FLAT" && subUnitId) {
          // Convert both to numbers for comparison
          const boqSubUnitId = boq.siteStructureSubUnitId
            ? Number(boq.siteStructureSubUnitId)
            : null;
          const selectedSubUnitIdNum = Number(subUnitId);

          // If BOQ doesn't have subUnitId, exclude it
          if (
            boqSubUnitId === null ||
            boqSubUnitId === undefined ||
            isNaN(boqSubUnitId)
          ) {
            return false; // BOQ doesn't have subUnitId, exclude it
          }
          if (boqSubUnitId !== selectedSubUnitIdNum) {
            return false; // SubUnitId doesn't match
          }
        }

        // Check locationId if provided - BOQ must have this field if we're filtering by it
        if (locationId) {
          // Convert both to numbers for comparison
          const boqLocationId = boq.locationId ? Number(boq.locationId) : null;
          const selectedLocationIdNum = Number(locationId);

          // If BOQ doesn't have locationId, exclude it
          if (
            boqLocationId === null ||
            boqLocationId === undefined ||
            isNaN(boqLocationId)
          ) {
            return false; // BOQ doesn't have locationId, exclude it
          }
          if (boqLocationId !== selectedLocationIdNum) {
            return false; // LocationId doesn't match
          }
        }

        return true; // All checks passed, include this BOQ with all its lines
      });

      setActivityBoqList(filteredBoqs);
    },
    [
      selectedUnitId,
      selectedFloorNo,
      selectedSubUnitId,
      selectedLocationId,
      workLevelSetting,
    ],
  );

  // Check if site hierarchy is complete based on workLevelSetting
  const isSiteHierarchyComplete = () => {
    if (!workLevelSetting || workLevelSetting === "NONE") {
      return false;
    }

    // TOWER_BLOCK and VILLA require only unitId
    if (workLevelSetting === "TOWER_BLOCK" || workLevelSetting === "VILLA") {
      return !!selectedUnitId;
    }

    // FLOOR requires unitId and floorNo
    if (workLevelSetting === "FLOOR") {
      return !!selectedUnitId && !!selectedFloorNo;
    }

    // FLAT requires unitId, floorNo, and subUnitId
    if (workLevelSetting === "FLAT") {
      return !!selectedUnitId && !!selectedFloorNo && !!selectedSubUnitId;
    }

    return false;
  };

  // Filter BOQ lines when site hierarchy changes
  useEffect(() => {
    if (allActivityBoqList.length > 0) {
      filterBoqListBySiteHierarchy(allActivityBoqList);
    }
  }, [
    workLevelSetting,
    selectedUnitId,
    selectedFloorNo,
    selectedSubUnitId,
    selectedLocationId,
    allActivityBoqList,
    filterBoqListBySiteHierarchy,
  ]);

  // Clear BOQ selection when site hierarchy actually changes (separate effect to avoid loop)
  useEffect(() => {
    const currentHierarchy = {
      workLevelSetting,
      unitId: selectedUnitId,
      floorNo: selectedFloorNo,
      subUnitId: selectedSubUnitId,
      locationId: selectedLocationId,
    };

    // Check if site hierarchy actually changed
    const hasChanged =
      prevSiteHierarchyRef.current.workLevelSetting !==
        currentHierarchy.workLevelSetting ||
      prevSiteHierarchyRef.current.unitId !== currentHierarchy.unitId ||
      prevSiteHierarchyRef.current.floorNo !== currentHierarchy.floorNo ||
      prevSiteHierarchyRef.current.subUnitId !== currentHierarchy.subUnitId ||
      prevSiteHierarchyRef.current.locationId !== currentHierarchy.locationId;

    // Only clear if hierarchy changed and there are selected BOQs
    if (hasChanged && boqLineIdsWatch && boqLineIdsWatch.length > 0) {
      setValue("boqLineIds", []);
    }

    // Update the ref with current values
    prevSiteHierarchyRef.current = currentHierarchy;
  }, [
    workLevelSetting,
    selectedUnitId,
    selectedFloorNo,
    selectedSubUnitId,
    selectedLocationId,
    boqLineIdsWatch,
    setValue,
  ]);

  const updateTask = async (data: any) => {
    try {
      setDisableSubmit(true);

      // Ensure data is valid
      if (!data || typeof data !== "object") {
        console.error("Invalid data provided to updateTask");
        setDisableSubmit(false);
        return;
      }

      // Ensure selectedTaskDetails is valid
      if (!selectedTaskDetails || typeof selectedTaskDetails !== "object") {
        console.error("Invalid selectedTaskDetails");
        setDisableSubmit(false);
        return;
      }

      // Block save when approval is required but no approvers are set —
      // e.g. the only approver was auto-removed after being made the owner.
      if (isApprovalRequired && (approvals.data?.length || 0) === 0) {
        Swal.fire({
          title: "Approval Required",
          text: 'Please set approval hierarchy by clicking "Set Approvals"',
          icon: "warning",
          confirmButtonText: "OK",
          confirmButtonColor: "#1890ff",
        });
        setDisableSubmit(false);
        return;
      }

      // Get only the dirty (changed) fields from the form
      let dirtyData = getDirtyFieldsOnly(data, dirtyFields);

      // priorityId can be spuriously "dirty" with an empty value (null task
      // priority defaults to "" and the Select can emit a change on mount).
      // Never send an empty priorityId — it isn't a real change.
      if (
        dirtyData &&
        (dirtyData.priorityId === "" ||
          dirtyData.priorityId === null ||
          dirtyData.priorityId === undefined)
      ) {
        delete dirtyData.priorityId;
      }

      // Check for BOQ quantity changes that might not be tracked in dirtyFields
      const hasBOQQuantityChanges = (() => {
        const currentFormValues = form.getFieldsValue();
        if (!currentFormValues || typeof currentFormValues !== "object")
          return false;

        // Check if any BOQ quantity fields have changed
        const boqQuantityFields = Object.keys(currentFormValues).filter((key) =>
          key.startsWith("allocateBOQQuantity_"),
        );

        const hasChanges = boqQuantityFields.some((fieldName) => {
          const [, boqTargetId] = fieldName.split("_");
          const currentValue = currentFormValues[fieldName];
          const currentRate = currentFormValues[`boqRate_${boqTargetId}`];
          const existingBoq = selectedTaskDetails?.taskBoqs?.find(
            (tb: any) => tb.activityBoqId === Number(boqTargetId),
          );
          return (
            existingBoq &&
            (Number(existingBoq.allotedQuantity || 0) !==
              Number(currentValue || 0) ||
              Number(existingBoq.rate || 0) !== Number(currentRate || 0))
          );
        });

        return hasChanges;
      })();

      const existingTags: any = selectedTaskDetails?.taskTags || [];
      const hasTagChanges =
        tags.length !== existingTags.length ||
        tags.some(
          (tag: any, index) => tag.value !== existingTags[index]?.tagId,
        );

      // Use the improved reminder change detection function
      const hasReminderChanges = hasReminderMeaningfulChanges();

      // Check for taskTools changes
      const hasTaskToolsChanges = (() => {
        const existingTools = selectedTaskDetails?.taskTools || [];
        const currentTools = taskTools || [];

        // Check if count has changed
        if (currentTools.length !== existingTools.length) return true;

        for (const currentTool of currentTools) {
          if (!currentTool) continue; // Type guard
          const existingTool = existingTools.find(
            (et: any) => et.toolId === currentTool.toolId,
          );
          if (
            !existingTool ||
            (existingTool.serialNumber ?? "") !==
              (currentTool.serialNumber ?? "") ||
            Number(existingTool.rate ?? "") !==
              Number(currentTool.rate ?? "") ||
            Number(existingTool.quantity ?? "") !==
              Number(currentTool.quantity ?? "") ||
            Number(existingTool.workUomId ?? "") !==
              Number(currentTool.workUomId ?? "")
          ) {
            return true;
          }
        }

        return false;
      })();

      // Check for hyperlinks changes
      const hasHyperlinksChanges = (() => {
        const existingHyperlinks = selectedTaskDetails?.hyperlinks || [];
        const currentHyperlinks = hyperlinks || [];

        // Check if count has changed
        if (currentHyperlinks.length !== existingHyperlinks.length) return true;

        for (const currentHyperlink of currentHyperlinks) {
          if (!currentHyperlink) continue; // Type guard
          const existingHyperlink = existingHyperlinks.find(
            (eh: any) => eh.name === currentHyperlink.name,
          );
          if (
            !existingHyperlink ||
            existingHyperlink.link !== currentHyperlink.link
          ) {
            return true;
          }
        }

        return false;
      })();

      // Check for approval hierarchy changes
      const hasApprovalHierarchyChanges = (() => {
        const existingApprovals =
          selectedTaskDetails?.stateApprovalHierarchy || [];
        const currentApprovals = approvals.data || [];

        // Check if count has changed
        if (currentApprovals.length !== existingApprovals.length) return true;

        for (const currentApproval of currentApprovals) {
          if (!currentApproval) continue; // Type guard
          const existingApproval = existingApprovals.find(
            (ea: any) =>
              ea.stateApprovalHierarchyId ===
              currentApproval.stateApprovalHierarchyId,
          );
          if (
            !existingApproval ||
            existingApproval.userId !== currentApproval.userId ||
            existingApproval.roleId !== currentApproval.roleId ||
            existingApproval.level !== currentApproval.level ||
            existingApproval.approvalStage !== currentApproval.approvalStage
          ) {
            return true;
          }
        }

        return false;
      })();

      // Check for resource changes (based on checkbox selection)
      const hasResourceChanges = (() => {
        const resourceFormValues = resourceForm.getFieldsValue();
        const currentResources = selectedResourceKeys
          .map((key: React.Key) => {
            const activityResourceId = Number(key);
            const resourceCount = resourceFormValues[`resourceCount_${key}`];
            if (resourceCount && Number(resourceCount) > 0) {
              return {
                activityResourceId,
                resourceCount: Number(resourceCount),
                rate: resourceFormValues[`rate_${key}`],
                quantity: resourceFormValues[`quantity_${key}`],
                workUomId: resourceFormValues[`workUomId_${key}`],
              };
            }
            return null;
          })
          .filter((r: any) => r !== null);

        const existingResources = selectedTaskDetails?.taskResources || [];

        // Check if count has changed
        if (currentResources.length !== existingResources.length) return true;

        for (const currentRes of currentResources) {
          if (!currentRes) continue; // Type guard
          const existingRes = existingResources.find(
            (er: any) =>
              er.activityResourceId === currentRes.activityResourceId,
          );
          if (
            !existingRes ||
            existingRes.resourceCount !== currentRes.resourceCount ||
            Number(existingRes.rate ?? "") !== Number(currentRes.rate ?? "") ||
            Number(existingRes.quantity ?? "") !==
              Number(currentRes.quantity ?? "") ||
            Number(existingRes.workUomId ?? "") !==
              Number(currentRes.workUomId ?? "")
          ) {
            return true;
          }
        }

        return false;
      })();

      // Detect Associated Team changes independently of dirtyFields. When an
      // array field is emptied, RHF leaves no per-element dirty entry, so
      // removing every team member would otherwise go unnoticed — getDirtyFieldsOnly
      // strips the empty array and updateTask bails out as a no-op. Compare the
      // current selection against the task's existing non-Owner users (resolved
      // through projectUsers the same way the seeding effect does).
      const hasTaskUserChanges = (() => {
        const currentTeamIds = ((data?.projectUserId as any[]) || [])
          .filter((v: any) => v !== undefined && v !== null)
          .map((v: any) => Number(v));
        const existingTeamIds = (selectedTaskDetails?.taskUsers || [])
          .filter((u: any) => u?.taskUserType !== "Owner")
          .map((u: any) => {
            const candidateId = u.projectUserId ?? u.userId;
            const matched = projectUsers.find(
              (pu: any) =>
                (pu.projectUserId != null &&
                  Number(pu.projectUserId) === Number(candidateId)) ||
                Number(pu.value) === Number(candidateId) ||
                Number((pu as any).userId) === Number(candidateId),
            );
            return matched?.value;
          })
          .filter((v: any) => v !== undefined && v !== null)
          .map((v: any) => Number(v));
        if (currentTeamIds.length !== existingTeamIds.length) return true;
        const existingSet = new Set(existingTeamIds);
        return currentTeamIds.some((id: number) => !existingSet.has(id));
      })();

      // If only BOQ/resource/tag/reminder/taskTools changes exist and no other dirty fields, create a minimal dirtyData
      if (
        (!dirtyData || Object.keys(dirtyData).length === 0) &&
        (hasBOQQuantityChanges ||
          hasResourceChanges ||
          hasTagChanges ||
          hasReminderChanges ||
          hasTaskToolsChanges ||
          hasHyperlinksChanges ||
          hasApprovalHierarchyChanges ||
          hasTaskUserChanges)
      ) {
        // Create a minimal dirtyData object to ensure the API call proceeds
        dirtyData = {};
      }

      // Ensure dirtyData is a valid object (allow empty objects)
      if (dirtyData === null || dirtyData === undefined) {
        setDisableSubmit(false);
        return;
      }

      // Allow proceeding if we have resource changes, BOQ changes, tag changes, reminder changes, or taskTools changes
      if (
        !dirtyData ||
        typeof dirtyData !== "object" ||
        (Object.keys(dirtyData).length === 0 &&
          !hasResourceChanges &&
          !hasBOQQuantityChanges &&
          !hasTagChanges &&
          !hasReminderChanges &&
          !hasTaskToolsChanges &&
          !hasHyperlinksChanges &&
          !hasApprovalHierarchyChanges &&
          !hasTaskUserChanges)
      ) {
        setDisableSubmit(false);
        return;
      }

      // Check for BOQ changes
      const hasBOQChanges = (() => {
        const currentFormValues = form.getFieldsValue();
        if (!currentFormValues || typeof currentFormValues !== "object")
          return false;

        const currentBoqMap = new Map<number, number>();
        Object.entries(currentFormValues)
          .filter(([key]) => key.startsWith("allocateBOQQuantity_"))
          .forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              const [, boqTargetId] = key.split("_");
              currentBoqMap.set(Number(boqTargetId), Number(value));
            }
          });

        const existingBoqs = selectedTaskDetails?.taskBoqs || [];

        // Check if any BOQ quantity has changed
        for (const [activityBoqId, currentQuantity] of Array.from(
          currentBoqMap.entries(),
        )) {
          const existingBoq = existingBoqs.find(
            (eb: any) => eb.activityBoqId === activityBoqId,
          );
          if (
            !existingBoq ||
            Number(existingBoq.allotedQuantity || 0) !== currentQuantity
          ) {
            return true;
          }
        }

        // Check if any BOQ has been removed
        for (const existingBoq of existingBoqs) {
          if (!currentBoqMap.has(existingBoq.activityBoqId)) {
            return true;
          }
        }

        // Check if BOQ line selection has changed
        const currentBoqLineIds = boqLineIdsWatch || [];
        const existingBoqLineIds = existingBoqs.map(
          (boq: any) => boq.activityBoqId,
        );

        if (currentBoqLineIds.length !== existingBoqLineIds.length) {
          return true;
        }

        for (const currentId of currentBoqLineIds) {
          if (currentId && !existingBoqLineIds.includes(currentId)) {
            return true;
          }
        }

        return false;
      })();

      // Additional check for BOQ line selection changes
      const hasBOQLineSelectionChanges = (() => {
        const currentBoqLineIds = (boqLineIdsWatch || []).filter(
          (id): id is string => id !== undefined,
        );
        const existingBoqLineIds = (selectedTaskDetails?.taskBoqs || []).map(
          (boq: any) => boq.activityBoqId,
        );

        if (currentBoqLineIds.length !== existingBoqLineIds.length) {
          return true;
        }

        for (const currentId of currentBoqLineIds) {
          if (!existingBoqLineIds.includes(currentId)) {
            return true;
          }
        }

        return false;
      })();

      // Check if there are any dirty fields or changes in tags/reminders/BOQ/Resources/TaskTools/Hyperlinks/ApprovalHierarchy
      const hasChanges =
        (dirtyFields &&
          typeof dirtyFields === "object" &&
          Object.keys(dirtyFields).length > 0) ||
        hasTagChanges ||
        hasReminderChanges ||
        hasBOQChanges ||
        hasBOQLineSelectionChanges ||
        hasBOQQuantityChanges ||
        hasResourceChanges ||
        hasTaskToolsChanges ||
        hasHyperlinksChanges ||
        hasApprovalHierarchyChanges ||
        hasTaskUserChanges;

      // If no fields have changed, just return to view mode without calling API
      if (!hasChanges) {
        setIsEditMode(false);
        setDisableSubmit(false);
        setTaskTools([]); // Reset task tools
        setSelectedToolType(null); // Reset tool selection
        setSelectedTool(null); // Reset tool selection
        setHyperlinks([]); // Reset hyperlinks
        setCurrentHyperlinkName(""); // Reset current hyperlink name
        setCurrentHyperlinkUrl(""); // Reset current hyperlink URL
        // Reset approval hierarchy
        setIsApprovalRequired(selectedTaskDetails?.approvalRequired || false);
        setAutoApprovalRemarks(selectedTaskDetails?.autoApprovalRemarks || "");
        if (
          selectedTaskDetails?.stateApprovalHierarchy &&
          selectedTaskDetails.stateApprovalHierarchy.length > 0
        ) {
          const approvalData = selectedTaskDetails.stateApprovalHierarchy.map(
            (approval: any) => ({
              roleId: approval.roleId,
              level: approval.level,
              userId: approval.userId,
              approvalStage: approval.approvalStage,
              stateApprovalHierarchyId: approval.stateApprovalHierarchyId,
            }),
          );
          setApprovals({
            open: false,
            data: approvalData,
          });
        } else {
          setApprovals({
            open: false,
            data: [],
          });
        }
        return;
      }

      const { projectUserId } = data || {};

      // === Process BOQ lines, resources, users, tags, etc. (existing code) ===
      const boqMap = new Map<number, number>();
      const currentFormValues = form.getFieldsValue();

      if (currentFormValues && typeof currentFormValues === "object") {
        Object.entries(currentFormValues)
          .filter(([key]) => key.startsWith("allocateBOQQuantity_"))
          .forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              const [, boqTargetId] = key.split("_");
              boqMap.set(Number(boqTargetId), Number(value));
            }
          });
      }

      const numField = (v: any) =>
        v !== undefined && v !== null && v !== "" ? Number(v) : undefined;
      const selectedBoqsForPayload = Array.from(boqMap.entries()).map(
        ([boqTargetId, value]) => {
          const rateVal = currentFormValues[`boqRate_${boqTargetId}`];
          const raQty = numField(
            currentFormValues[`boqRateAnalysisQty_${boqTargetId}`],
          );
          const raRate = numField(
            currentFormValues[`boqRateAnalysisRate_${boqTargetId}`],
          );
          // BOQ UOM is the material's fixed UOM (not editable) — not sent;
          // the backend preserves the existing rateAnalysisUomId.
          return {
            activityBoqId: boqTargetId,
            allotedQuantity: value,
            ...(rateVal !== undefined && rateVal !== null && rateVal !== ""
              ? { rate: Number(rateVal) }
              : {}),
            ...(raQty !== undefined ? { rateAnalysisQty: raQty } : {}),
            ...(raRate !== undefined ? { rateAnalysisRate: raRate } : {}),
          };
        },
      );

      // Guard: the INCREASE in a material's allocate qty cannot exceed the
      // task-available qty for that BOQ line (decreases are always fine).
      // e.g. current 10, available 1 → max new = 11; 12 errors, 9 is fine.
      for (const boq of selectedBoqsForPayload) {
        const rec = (boqLinesDataSource || []).find(
          (r: any) => r.activityBoqId === boq.activityBoqId,
        );
        if (!rec) continue;
        const existingAllocated = Number(rec.taskAllocatedQuantity ?? 0);
        const available = Number(
          rec.taskAvailableQuantity ??
            rec.availableQuantity ??
            rec.allotedQuantity ??
            0,
        );
        const newQty = Number(boq.allotedQuantity ?? 0);
        if (newQty - existingAllocated > available + 1e-9) {
          const matName =
            rec.materialName ||
            rec.material?.materialName ||
            rec.boqLineName ||
            rec.boqTitle ||
            "this material";
          messageApi.error(
            `Allocate Qty (${newQty}) for "${matName}" exceeds availability — you can allocate at most ${
              existingAllocated + available
            } (current ${existingAllocated} + available ${available}).`,
          );
          setDisableSubmit(false);
          return;
        }
      }

      const existingBoqs = selectedTaskDetails?.taskBoqs || [];

      const newBoqs = selectedBoqsForPayload.filter(
        (boq: any) =>
          boq &&
          boq.activityBoqId &&
          !existingBoqs.some(
            (eb: any) =>
              eb && eb.activityBoqId && eb.activityBoqId === boq.activityBoqId,
          ),
      );

      const updatedBoqs = selectedBoqsForPayload
        .filter(
          (boq: any) =>
            boq &&
            boq.activityBoqId &&
            existingBoqs.some(
              (eb: any) =>
                eb &&
                eb.activityBoqId &&
                eb.activityBoqId === boq.activityBoqId &&
                (Number(eb.allotedQuantity || 0) !==
                  Number(boq.allotedQuantity || 0) ||
                  Number(eb.rate || 0) !== Number(boq.rate || 0) ||
                  Number(eb.rateAnalysisQty ?? "") !==
                    Number(boq.rateAnalysisQty ?? "") ||
                  Number(eb.rateAnalysisRate ?? "") !==
                    Number(boq.rateAnalysisRate ?? "")),
            ),
        )
        .map((boq: any) => {
          const existingBoq = existingBoqs.find(
            (eb: any) =>
              eb && eb.activityBoqId && eb.activityBoqId === boq.activityBoqId,
          );
          return {
            taskBoqId: existingBoq?.taskBoqId,
            allotedQuantity: boq.allotedQuantity || 0,
            ...(boq.rate !== undefined ? { rate: boq.rate } : {}),
            ...(boq.rateAnalysisQty !== undefined
              ? { rateAnalysisQty: boq.rateAnalysisQty }
              : {}),
            ...(boq.rateAnalysisRate !== undefined
              ? { rateAnalysisRate: boq.rateAnalysisRate }
              : {}),
          };
        });

      const deletedBoqs = existingBoqs
        .filter(
          (eb: any) =>
            eb &&
            eb.activityBoqId &&
            !selectedBoqsForPayload.some(
              (sb: any) =>
                sb && sb.activityBoqId && sb.activityBoqId === eb.activityBoqId,
            ),
        )
        .map((eb: any) => ({ taskBoqId: eb.taskBoqId }));

      // === Process Activity Resources ===
      const resourceFormValues = resourceForm.getFieldsValue();
      // Collect all resources that have a valid resource count entered
      const selectedActivityResources = activityResourcesList
        .map((resource: any) => {
          const id = resource.activityResourceId;
          const resourceCount = resourceFormValues[`resourceCount_${id}`];
          const rate = resourceFormValues[`rate_${id}`];
          const quantity = resourceFormValues[`quantity_${id}`];
          const workUomId = resourceFormValues[`workUomId_${id}`];
          const raQty = resourceFormValues[`rateAnalysisQty_${id}`];
          const raUom = resourceFormValues[`rateAnalysisUomId_${id}`];
          const raRate = resourceFormValues[`rateAnalysisRate_${id}`];
          // Only include resources that have a valid count entered
          if (resourceCount && Number(resourceCount) > 0) {
            return {
              activityResourceId: id,
              resourceCount: Number(resourceCount),
              ...(rate !== undefined && rate !== null && rate !== ""
                ? { rate: Number(rate) }
                : {}),
              ...(quantity !== undefined && quantity !== null && quantity !== ""
                ? { quantity: Number(quantity) }
                : {}),
              ...(workUomId !== undefined && workUomId !== null
                ? { workUomId: Number(workUomId) }
                : {}),
              ...(raQty !== undefined && raQty !== null && raQty !== ""
                ? { rateAnalysisQty: Number(raQty) }
                : {}),
              ...(raUom !== undefined && raUom !== null
                ? { rateAnalysisUomId: Number(raUom) }
                : {}),
              ...(raRate !== undefined && raRate !== null && raRate !== ""
                ? { rateAnalysisRate: Number(raRate) }
                : {}),
            };
          }
          return null;
        })
        .filter(
          (
            resource: any,
          ): resource is {
            activityResourceId: number;
            resourceCount: number;
            rate?: number;
            quantity?: number;
            workUomId?: number;
            rateAnalysisQty?: number;
            rateAnalysisUomId?: number;
            rateAnalysisRate?: number;
          } => resource !== null,
        );

      // Budget guard — two independent checks (matches create + backend):
      //  (a) budget >= sum of overall section lines (materials+resources+tools)
      //  (b) budget >= task qty * rate (Quantity work type)
      {
        const fmtINR = (n: number) =>
          `₹${(Number.isFinite(n) ? n : 0).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
        const allocated = Number(
          data.allocatedBudgetAmount ??
            selectedTaskDetails?.budgetValue ??
            selectedTaskDetails?.allocatedBudgetAmount ??
            0,
        );
        if (allocated > 0) {
          const boqSum = selectedBoqsForPayload.reduce(
            (s: number, b: any) =>
              s + Number(b.rate || 0) * Number(b.allotedQuantity || 0),
            0,
          );
          const resSum = selectedActivityResources.reduce(
            (s: number, r: any) =>
              s +
              Number(r.rate || 0) *
                Number(r.quantity || 0) *
                Number(r.resourceCount || 0),
            0,
          );
          const toolSum = (taskTools || []).reduce(
            (s: number, t: any) =>
              s +
              Number(t.rate || 0) *
                Number(t.quantity || 0) *
                (Number(t.count || 0) || 1),
            0,
          );
          const sectionsTotal = boqSum + resSum + toolSum;
          if (allocated + 0.001 < sectionsTotal) {
            messageApi.error(
              `Total allocated budget (${fmtINR(
                allocated,
              )}) must be greater than or equal to the sum of all section totals (${fmtINR(
                sectionsTotal,
              )}).`,
            );
            setDisableSubmit(false);
            return;
          }
          const wType = data.workType ?? selectedTaskDetails?.workType;
          const qtyWorkTotal =
            wType === "Quantity"
              ? Number(
                  data.workQuantity ?? selectedTaskDetails?.workQuantity ?? 0,
                ) * Number(data.rate ?? selectedTaskDetails?.rate ?? 0)
              : 0;
          if (qtyWorkTotal > 0 && allocated + 0.001 < qtyWorkTotal) {
            messageApi.error(
              `Total allocated budget (${fmtINR(
                allocated,
              )}) must be greater than or equal to the task value (Work Qty × Rate = ${fmtINR(
                qtyWorkTotal,
              )}).`,
            );
            setDisableSubmit(false);
            return;
          }
        }
      }

      const existingResources = selectedTaskDetails?.taskResources || [];
      const existingActivityResourceIds = new Set(
        existingResources.map((res: any) => res.activityResourceId),
      );
      const currentActivityResourceIds = new Set(
        selectedActivityResources.map((res: any) => res.activityResourceId),
      );

      const newResources = selectedActivityResources.filter(
        (res: any) => !existingActivityResourceIds.has(res.activityResourceId),
      );

      const updatedResources = selectedActivityResources
        .filter((res: any) =>
          existingActivityResourceIds.has(res.activityResourceId),
        )
        .filter((res: any) => {
          const existingResource = existingResources.find(
            (er: any) => er.activityResourceId === res.activityResourceId,
          );
          return (
            existingResource &&
            (Number(existingResource.resourceCount || 0) !==
              Number(res.resourceCount) ||
              Number(existingResource.rate ?? "") !==
                Number(res.rate ?? "") ||
              Number(existingResource.quantity ?? "") !==
                Number(res.quantity ?? "") ||
              Number(existingResource.workUomId ?? "") !==
                Number(res.workUomId ?? "") ||
              Number(existingResource.rateAnalysisQty ?? "") !==
                Number(res.rateAnalysisQty ?? "") ||
              Number(existingResource.rateAnalysisUomId ?? "") !==
                Number(res.rateAnalysisUomId ?? "") ||
              Number(existingResource.rateAnalysisRate ?? "") !==
                Number(res.rateAnalysisRate ?? ""))
          );
        })
        .map((res: any) => {
          const existingResource = existingResources.find(
            (er: any) => er.activityResourceId === res.activityResourceId,
          );
          return {
            taskResourceId: existingResource.taskResourceId,
            resourceCount: res.resourceCount,
            ...(res.rate !== undefined ? { rate: res.rate } : {}),
            ...(res.quantity !== undefined ? { quantity: res.quantity } : {}),
            ...(res.workUomId !== undefined
              ? { workUomId: res.workUomId }
              : {}),
            ...(res.rateAnalysisQty !== undefined
              ? { rateAnalysisQty: res.rateAnalysisQty }
              : {}),
            ...(res.rateAnalysisUomId !== undefined
              ? { rateAnalysisUomId: res.rateAnalysisUomId }
              : {}),
            ...(res.rateAnalysisRate !== undefined
              ? { rateAnalysisRate: res.rateAnalysisRate }
              : {}),
          };
        });

      const deletedResources = existingResources
        .filter(
          (er: any) => !currentActivityResourceIds.has(er.activityResourceId),
        )
        .map((er: any) => ({ taskResourceId: er.taskResourceId }));

      // === Process Task Users ===
      const selectedTaskUsers =
        projectUserId && Array.isArray(projectUserId)
          ? projectUserId
              .filter((user: any) => user !== undefined && user !== null)
              .map((user: string) => {
                const projectUser = projectUsers.find(
                  (pu: any) => pu.label === user || pu.value === user,
                );
                if (!projectUser) return null;
                // The form field holds userId; the payload needs the actual
                // projectUserId (membership id), kept on the option via the
                // ...user spread in fetchProjectUsers.
                return {
                  projectUserId: Number(
                    (projectUser as any).projectUserId ?? projectUser.value,
                  ),
                  taskUserType: "Resource Head",
                };
              })
              .filter(Boolean) || []
          : [];

      const existingUsers = selectedTaskDetails?.taskUsers || [];

      const newUsers = selectedTaskUsers.filter(
        (user: any) =>
          user &&
          user.projectUserId &&
          !existingUsers.some(
            (eu: any) =>
              eu && eu.projectUserId && eu.projectUserId === user.projectUserId,
          ),
      );

      const deletedUsers = existingUsers
        .filter(
          (eu: any) =>
            eu &&
            eu.projectUserId &&
            !selectedTaskUsers.some(
              (su: any) =>
                su && su.projectUserId && su.projectUserId === eu.projectUserId,
            ),
        )
        .map((eu: any) => ({ taskUserId: eu.taskUserId }));

      const newTags = tags
        .filter(
          (tag: any) =>
            typeof tag.value === "string" ||
            !existingTags.some((et: any) => et.tagId === tag.value),
        )
        .map((tag: any) => ({
          tagName: tag.label || tag.value,
          ...(typeof tag.value !== "string" && { tagId: Number(tag.value) }),
        }));

      const deletedTags = existingTags
        .filter((et: any) => !tags.some((t: any) => t.value === et.tagId))
        .map((et: any) => ({ tasksTagId: et.tasksTagId }));

      // === Process Reminders ===
      // Only process reminders if they have actually changed
      let formattedReminders = undefined;

      if (
        hasReminderChanges &&
        (remainders || selectedTaskDetails?.reminders)
      ) {
        formattedReminders = {
          reminderType:
            remainders?.reminderType ||
            selectedTaskDetails?.reminders?.reminderType,
          specificTime:
            remainders?.specificTime ||
            selectedTaskDetails?.reminders?.specificTime,
          moduleType: "Task",
          // Include reminderId if it exists (for updates)
          ...(selectedTaskDetails?.reminders?.reminderId && {
            reminderId: selectedTaskDetails.reminders.reminderId,
          }),
          // Always include recurrenceType, even if it's "None"
          recurrenceType:
            (remainders?.recurrenceType !== undefined
              ? remainders.recurrenceType
              : selectedTaskDetails?.reminders?.recurrenceType) || "None",
          // Include recurrenceInterval and maxRecurrenceCount only if recurrenceType is not "None"
          ...((remainders?.recurrenceType ||
            selectedTaskDetails?.reminders?.recurrenceType) &&
            (remainders?.recurrenceType ||
              selectedTaskDetails?.reminders?.recurrenceType) !== "None" && {
              recurrenceInterval:
                remainders?.recurrenceInterval ||
                selectedTaskDetails?.reminders?.recurrenceInterval ||
                1,
              maxRecurrenceCount:
                remainders?.maxRecurrenceCount ||
                selectedTaskDetails?.reminders?.maxRecurrenceCount ||
                1,
            }),
          // Include specific date for "On" type reminders with "Specific Date" offset
          ...((remainders?.reminderType ||
            selectedTaskDetails?.reminders?.reminderType) === "On" &&
            (remainders?.offsetFrom ||
              selectedTaskDetails?.reminders?.offsetFrom) === "Specific Date" &&
            (remainders?.specificDate ||
              selectedTaskDetails?.reminders?.specificDate) && {
              specificDate:
                remainders?.specificDate ||
                selectedTaskDetails?.reminders?.specificDate,
              offsetFrom: "Specific Date", // Required for "On" type
            }),
          // Include offset fields for "Before" or "After" type reminders
          ...(((remainders?.reminderType ||
            selectedTaskDetails?.reminders?.reminderType) === "Before" ||
            (remainders?.reminderType ||
              selectedTaskDetails?.reminders?.reminderType) === "After") && {
            daysOffset:
              remainders?.daysOffset ||
              selectedTaskDetails?.reminders?.daysOffset,
            offsetFrom:
              remainders?.offsetFrom ||
              selectedTaskDetails?.reminders?.offsetFrom,
          }),
          // For "On" type reminders with non-"Specific Date" offset, include both specificDate and offset fields
          // The API expects specificDate for "On" type regardless of offset type
          ...((remainders?.reminderType ||
            selectedTaskDetails?.reminders?.reminderType) === "On" &&
            (remainders?.offsetFrom ||
              selectedTaskDetails?.reminders?.offsetFrom) !==
              "Specific Date" && {
              specificDate:
                remainders?.specificDate ||
                selectedTaskDetails?.reminders?.specificDate, // API expects this for "On" type
              daysOffset:
                remainders?.daysOffset ||
                selectedTaskDetails?.reminders?.daysOffset,
              offsetFrom:
                remainders?.offsetFrom ||
                selectedTaskDetails?.reminders?.offsetFrom,
            }),
          reminderUsers: (() => {
            const currentUserIds =
              (
                remainders?.reminderUsers ||
                selectedTaskDetails?.reminders?.reminderUsers
              )?.map((user: any) => user.userId) || [];
            const existingUserIds =
              selectedTaskDetails?.reminders?.reminderUsers?.map(
                (user: any) => user.userId,
              ) || [];

            // Find new users (users that are selected but not in existing)
            const newUserIds = currentUserIds.filter(
              (id: number) => !existingUserIds.includes(id),
            );

            // Find deleted users (users that were in existing but not in current)
            const deletedUserIds = existingUserIds.filter(
              (id: number) => !currentUserIds.includes(id),
            );

            // Get the reminderUserId for deleted users
            const deletedReminderUsers =
              selectedTaskDetails?.reminders?.reminderUsers
                ?.filter((user: any) => deletedUserIds.includes(user.userId))
                .map((user: any) => ({
                  reminderUserId: user.reminderUserId,
                })) || [];

            return {
              newLines: newUserIds.map((userId: number) => ({ userId })),
              deletedLines: deletedReminderUsers,
            };
          })(),
        };

        // Validate reminder data structure before sending
        if (formattedReminders) {
          // Check if required fields are present
          if (
            !formattedReminders.reminderType ||
            !formattedReminders.specificTime
          ) {
            formattedReminders = undefined;
          } else {
            // Ensure all required recurrence fields are present with defaults
            if (
              formattedReminders.recurrenceType &&
              formattedReminders.recurrenceType !== "None"
            ) {
              if (!formattedReminders.recurrenceInterval) {
                formattedReminders.recurrenceInterval = 1;
              }
              if (!formattedReminders.maxRecurrenceCount) {
                formattedReminders.maxRecurrenceCount = 1;
              }
            }
            // If recurrenceType is "None", ensure it's explicitly set
            if (formattedReminders.recurrenceType === "None") {
              // Remove recurrenceInterval and maxRecurrenceCount when recurrenceType is "None"
              delete formattedReminders.recurrenceInterval;
              delete formattedReminders.maxRecurrenceCount;
            }

            // Ensure reminderId is present for updates
            if (
              selectedTaskDetails?.reminders?.reminderId &&
              !formattedReminders.reminderId
            ) {
              formattedReminders.reminderId =
                selectedTaskDetails.reminders.reminderId;
            }
          }
        }
      }

      // === Construct Final Payload ===
      // Filter out tool-related and hyperlink form fields as they are processed separately
      const {
        toolType,
        toolMachinery,
        toolCount,
        toolRate,
        toolQuantity,
        toolWorkUomId,
        toolSerialNumber,
        hyperlinkName,
        hyperlinkUrl,
        ...restDirtyData
      } = dirtyData || {};

      // Also strip dynamic per-line form fields (keyed by id). These are composed
      // into the structured taskBoqs / taskResources sections and must not leak
      // into the top-level payload.
      const dynamicLineFieldPrefixes = [
        "allocateBOQQuantity_",
        "boqRate_",
        "rate_",
        "quantity_",
        "workUomId_",
        "resourceCount_",
      ];
      const filteredDirtyData = Object.fromEntries(
        Object.entries(restDirtyData || {}).filter(
          ([key]) => !dynamicLineFieldPrefixes.some((p) => key.startsWith(p)),
        ),
      );

      // Check if only tools are being updated (similar to resources pattern)
      // For tools-only update, we need to check if there are no actual form field changes
      // (excluding tool-related fields that are processed separately)
      const hasActualFormChanges =
        dirtyFields &&
        Object.keys(dirtyFields).some(
          (key) =>
            !key.startsWith("tool") &&
            !key.startsWith("hyperlink") &&
            dirtyFields[key] === true,
        );

      const isOnlyToolsUpdate =
        !hasActualFormChanges &&
        hasTaskToolsChanges &&
        !hasResourceChanges &&
        !hasBOQQuantityChanges &&
        !hasTagChanges &&
        !hasReminderChanges &&
        !hasHyperlinksChanges;

      let payload: any = {
        // required
        taskId: selectedTaskDetails.taskId,
        projectId: projectId,
        phaseId: phaseId,
        activityId: id,
        // Preserve task status across unrelated updates. taskStatus is not a
        // form field (it's managed via the separate statusChangeTask API),
        // so without this the backend received no status on save and reset
        // the task back to its default (e.g. "Under QC" → "Open").
        ...(selectedTaskDetails?.taskStatus && {
          taskStatus: selectedTaskDetails.taskStatus,
        }),
      };

      // Only include form data if it's not a tools-only update
      if (!isOnlyToolsUpdate) {
        payload = {
          ...payload,
          ...filteredDirtyData,
        };
      }

      // Convert isBillable to boolean if it has changed
      if (dirtyFields && dirtyFields.isBillable && !isOnlyToolsUpdate) {
        payload.isBillable = data.isBillable === "Billable" ? true : false;
      }

      // Weightage comes from a text input (to allow 2 decimals) — send as number
      if (
        payload.taskWeightagePercentage !== undefined &&
        payload.taskWeightagePercentage !== null &&
        payload.taskWeightagePercentage !== ""
      ) {
        payload.taskWeightagePercentage = Number(
          payload.taskWeightagePercentage,
        );
      }

      // BOQ / materials allocation is delinked — materials now come from norms
      // (Apply Norm). Task BOQ is never sent on save, so existing task_boqs
      // rows are left untouched (no accidental deletedLines wipe).

      if (
        !isOnlyToolsUpdate &&
        (newResources.length > 0 ||
          updatedResources.length > 0 ||
          deletedResources.length > 0)
      ) {
        payload.taskResources = {
          ...(newResources.length > 0 && { newLines: newResources }),
          ...(updatedResources.length > 0 && {
            updatedLines: updatedResources,
          }),
          ...(deletedResources.length > 0 && {
            deletedLines: deletedResources,
          }),
        };
      }

      if (
        (dirtyFields?.projectUserId || hasTaskUserChanges) &&
        !isOnlyToolsUpdate
      ) {
        payload.taskUsers = {
          newLines: newUsers,
          deletedLines: deletedUsers,
        };
      }

      // For tags, check if there are actual changes in the tags array
      if (
        !isOnlyToolsUpdate &&
        (newTags.length > 0 || deletedTags.length > 0)
      ) {
        payload.taskTags = {
          newLines: newTags,
          deletedLines: deletedTags,
        };
      }

      // === Process Task Tools ===
      const existingTools = selectedTaskDetails?.taskTools || [];
      const currentTools = taskTools || [];

      // Rate-analysis fields shared by new/updated tool payloads.
      const toolRateAnalysisFields = (tool: any) => ({
        ...(tool.rateAnalysisQty !== undefined && tool.rateAnalysisQty !== null
          ? { rateAnalysisQty: Number(tool.rateAnalysisQty) }
          : {}),
        ...(tool.rateAnalysisUomId !== undefined &&
        tool.rateAnalysisUomId !== null
          ? { rateAnalysisUomId: Number(tool.rateAnalysisUomId) }
          : {}),
        ...(tool.rateAnalysisRate !== undefined &&
        tool.rateAnalysisRate !== null
          ? { rateAnalysisRate: Number(tool.rateAnalysisRate) }
          : {}),
      });

      // Find new tools (tools that are in current but not in existing)
      const newTools = currentTools
        .filter(
          (currentTool: any) =>
            !existingTools.some(
              (existingTool: any) => existingTool.toolId === currentTool.toolId,
            ),
        )
        .map((tool: any) => {
          const materials = (tool.toolMaterials || []).map(toolMaterialToLine);
          return {
            toolId: tool.toolId,
            ...(tool.serialNumber ? { serialNumber: tool.serialNumber } : {}),
            ...(tool.rate !== undefined && tool.rate !== null
              ? { rate: Number(tool.rate) }
              : {}),
            ...(tool.quantity !== undefined && tool.quantity !== null
              ? { quantity: Number(tool.quantity) }
              : {}),
            ...(tool.workUomId !== undefined && tool.workUomId !== null
              ? { workUomId: Number(tool.workUomId) }
              : {}),
            ...toolRateAnalysisFields(tool),
            ...(materials.length > 0 ? { toolMaterials: materials } : {}),
          };
        });

      // Find updated tools — existing tools with a changed overall/rate-analysis
      // field OR a changed split-materials set. Count is excluded from updates.
      const updatedTools = currentTools
        .filter((currentTool: any) =>
          existingTools.some(
            (existingTool: any) => existingTool.toolId === currentTool.toolId,
          ),
        )
        .map((currentTool: any) => {
          const existingTool = existingTools.find(
            (et: any) => et.toolId === currentTool.toolId,
          );
          const materialsDiff = buildToolMaterialsDiff(
            currentTool.toolMaterials || [],
            existingTool?.toolMaterials || [],
          );
          const fieldsChanged =
            !!existingTool &&
            ((existingTool.serialNumber ?? "") !==
              (currentTool.serialNumber ?? "") ||
              Number(existingTool.rate ?? "") !==
                Number(currentTool.rate ?? "") ||
              Number(existingTool.quantity ?? "") !==
                Number(currentTool.quantity ?? "") ||
              Number(existingTool.workUomId ?? "") !==
                Number(currentTool.workUomId ?? "") ||
              Number(existingTool.rateAnalysisQty ?? "") !==
                Number(currentTool.rateAnalysisQty ?? "") ||
              Number(existingTool.rateAnalysisUomId ?? "") !==
                Number(currentTool.rateAnalysisUomId ?? "") ||
              Number(existingTool.rateAnalysisRate ?? "") !==
                Number(currentTool.rateAnalysisRate ?? ""));
          return { currentTool, existingTool, materialsDiff, fieldsChanged };
        })
        .filter(
          ({ fieldsChanged, materialsDiff }: any) =>
            fieldsChanged || toolMaterialsDiffHasChanges(materialsDiff),
        )
        .map(({ currentTool, existingTool, materialsDiff }: any) => ({
          taskToolId: existingTool?.taskToolId,
          toolId: currentTool.toolId,
          ...(currentTool.serialNumber
            ? { serialNumber: currentTool.serialNumber }
            : {}),
          ...(currentTool.rate !== undefined && currentTool.rate !== null
            ? { rate: Number(currentTool.rate) }
            : {}),
          ...(currentTool.quantity !== undefined &&
          currentTool.quantity !== null
            ? { quantity: Number(currentTool.quantity) }
            : {}),
          ...(currentTool.workUomId !== undefined &&
          currentTool.workUomId !== null
            ? { workUomId: Number(currentTool.workUomId) }
            : {}),
          ...toolRateAnalysisFields(currentTool),
          ...(toolMaterialsDiffHasChanges(materialsDiff)
            ? { toolMaterials: materialsDiff }
            : {}),
        }));

      // Find deleted tools (tools that exist in existing but not in current)
      const deletedTools = existingTools
        .filter(
          (existingTool: any) =>
            !currentTools.some(
              (currentTool: any) => currentTool.toolId === existingTool.toolId,
            ),
        )
        .map((existingTool: any) => ({
          taskToolId: existingTool.taskToolId,
        }));

      // Edge case: a tool's split-materials total must not exceed its overall
      // total (rate * qty * count) — re-checked at submit since the tool's
      // rate/qty may have been lowered after materials were added.
      for (const tool of currentTools) {
        const materials = tool.toolMaterials || [];
        if (!materials.length) continue;
        const toolTotal =
          (Number(tool.rate) || 0) *
          (Number(tool.quantity) || 0) *
          (Number(tool.count) || 1);
        const materialsTotal = materials.reduce(
          (s: number, m: any) =>
            s + (Number(m.quantity) || 0) * (Number(m.rate) || 0),
          0,
        );
        if (
          toolTotal > 0 &&
          Math.round(materialsTotal * 100) / 100 >
            Math.round(toolTotal * 100) / 100
        ) {
          messageApi.error(
            `Total of split materials (${materialsTotal.toFixed(
              2,
            )}) cannot exceed the tool/machinery total (${toolTotal.toFixed(
              2,
            )}) for ${tool.toolName || "the selected tool"}.`,
          );
          setDisableSubmit(false);
          return;
        }
      }

      // Include taskTools if there are any changes
      if (
        newTools.length > 0 ||
        updatedTools.length > 0 ||
        deletedTools.length > 0
      ) {
        payload.taskTools = {
          ...(newTools.length > 0 && { newLines: newTools }),
          ...(updatedTools.length > 0 && { updatedLines: updatedTools }),
          ...(deletedTools.length > 0 && { deletedLines: deletedTools }),
        };
      }

      // === Process Hyperlinks ===
      const existingHyperlinks = selectedTaskDetails?.hyperlinks || [];
      const currentHyperlinks = hyperlinks || [];

      // Find new hyperlinks (hyperlinks that are in current but not in existing)
      const newHyperlinks = currentHyperlinks
        .filter(
          (currentHyperlink: any) =>
            !existingHyperlinks.some(
              (existingHyperlink: any) =>
                existingHyperlink.name === currentHyperlink.name,
            ),
        )
        .map((hyperlink: any) => ({
          name: hyperlink.name,
          link: hyperlink.link,
        }));

      // Find updated hyperlinks (hyperlinks that exist in both but with different link)
      const updatedHyperlinks = currentHyperlinks
        .filter((currentHyperlink: any) =>
          existingHyperlinks.some(
            (existingHyperlink: any) =>
              existingHyperlink.name === currentHyperlink.name,
          ),
        )
        .filter((currentHyperlink: any) => {
          const existingHyperlink = existingHyperlinks.find(
            (eh: any) => eh.name === currentHyperlink.name,
          );
          return (
            existingHyperlink &&
            existingHyperlink.link !== currentHyperlink.link
          );
        })
        .map((currentHyperlink: any) => {
          const existingHyperlink = existingHyperlinks.find(
            (eh: any) => eh.name === currentHyperlink.name,
          );
          return {
            hyperlinkId: existingHyperlink?.hyperlinkId,
            name: currentHyperlink.name,
            link: currentHyperlink.link,
          };
        });

      // Find deleted hyperlinks (hyperlinks that exist in existing but not in current)
      const deletedHyperlinks = existingHyperlinks
        .filter(
          (existingHyperlink: any) =>
            !currentHyperlinks.some(
              (currentHyperlink: any) =>
                currentHyperlink.name === existingHyperlink.name,
            ),
        )
        .map((existingHyperlink: any) => ({
          hyperlinkId: existingHyperlink.hyperlinkId,
        }));

      // Include hyperlinks if there are any changes
      if (
        !isOnlyToolsUpdate &&
        (newHyperlinks.length > 0 ||
          updatedHyperlinks.length > 0 ||
          deletedHyperlinks.length > 0)
      ) {
        payload.hyperlinks = {
          ...(newHyperlinks.length > 0 && { newLines: newHyperlinks }),
          ...(updatedHyperlinks.length > 0 && {
            updatedLines: updatedHyperlinks,
          }),
          ...(deletedHyperlinks.length > 0 && {
            deletedLines: deletedHyperlinks,
          }),
        };
      }

      // === Process State Approval Hierarchy ===
      const existingApprovals =
        selectedTaskDetails?.stateApprovalHierarchy || [];
      const currentApprovals = approvals.data || [];

      // Find new approval hierarchy entries (entries that are in current but not in existing)
      const newApprovals = currentApprovals
        .filter(
          (currentApproval: any) =>
            !currentApproval.stateApprovalHierarchyId ||
            !existingApprovals.some(
              (existingApproval: any) =>
                existingApproval.stateApprovalHierarchyId ===
                currentApproval.stateApprovalHierarchyId,
            ),
        )
        .map((approval: any) => ({
          userId: Number(approval.userId),
          roleId: Number(approval.roleId),
          level: approval.level,
          approvalStage: approval.approvalStage,
        }));

      // Find updated approval hierarchy entries (entries that exist in both but with different values)
      const updatedApprovals = currentApprovals
        .filter((currentApproval: any) =>
          existingApprovals.some(
            (existingApproval: any) =>
              existingApproval.stateApprovalHierarchyId ===
              currentApproval.stateApprovalHierarchyId,
          ),
        )
        .filter((currentApproval: any) => {
          const existingApproval = existingApprovals.find(
            (ea: any) =>
              ea.stateApprovalHierarchyId ===
              currentApproval.stateApprovalHierarchyId,
          );
          return (
            existingApproval &&
            (existingApproval.userId !== currentApproval.userId ||
              existingApproval.roleId !== currentApproval.roleId ||
              existingApproval.level !== currentApproval.level ||
              existingApproval.approvalStage !== currentApproval.approvalStage)
          );
        })
        .map((currentApproval: any) => ({
          stateApprovalHierarchyId: currentApproval.stateApprovalHierarchyId,
          userId: Number(currentApproval.userId),
          roleId: Number(currentApproval.roleId),
          level: currentApproval.level,
          approvalStage: currentApproval.approvalStage,
        }));

      // Find deleted approval hierarchy entries (entries that exist in existing but not in current)
      const deletedApprovals = existingApprovals
        .filter(
          (existingApproval: any) =>
            !currentApprovals.some(
              (currentApproval: any) =>
                currentApproval.stateApprovalHierarchyId ===
                existingApproval.stateApprovalHierarchyId,
            ),
        )
        .map((existingApproval: any) => ({
          stateApprovalHierarchyId: existingApproval.stateApprovalHierarchyId,
        }));

      // Include stateApprovalHierarchy if there are any changes
      if (
        !isOnlyToolsUpdate &&
        (newApprovals.length > 0 ||
          updatedApprovals.length > 0 ||
          deletedApprovals.length > 0)
      ) {
        payload.stateApprovalHierarchy = {
          ...(newApprovals.length > 0 && { newLines: newApprovals }),
          ...(updatedApprovals.length > 0 && {
            updatedLines: updatedApprovals,
          }),
          ...(deletedApprovals.length > 0 && {
            deletedLines: deletedApprovals,
          }),
        };
        // Always include approvalRequired when there are approval hierarchy changes
        payload.approvalRequired = isApprovalRequired;
      } else if (
        !isOnlyToolsUpdate &&
        isApprovalRequired !== selectedTaskDetails?.approvalRequired
      ) {
        // Include approvalRequired if it has changed (without hierarchy changes)
        payload.approvalRequired = isApprovalRequired;
      }

      // Include autoApprovalRemarks when approval is not required and remarks
      // were entered or changed
      if (
        !isOnlyToolsUpdate &&
        !isApprovalRequired &&
        autoApprovalRemarks !== (selectedTaskDetails?.autoApprovalRemarks || "")
      ) {
        payload.autoApprovalRemarks = autoApprovalRemarks;
      }

      // === Include Site Hierarchy fields if they have changed ===
      if (
        !isOnlyToolsUpdate &&
        (dirtyFields?.siteId ||
          dirtyFields?.workLevelSetting ||
          dirtyFields?.siteStructureUnitId ||
          dirtyFields?.floorNo ||
          dirtyFields?.siteStructureSubUnitId ||
          dirtyFields?.locationId)
      ) {
        // Segment Level is mandatory for Site Structure Validation
        if (data.workLevelSetting || selectedTaskDetails?.workLevelSetting) {
          const workLevelSettingValue =
            data.workLevelSetting || selectedTaskDetails?.workLevelSetting;
          payload.segmentLevel = getSegmentLevel(workLevelSettingValue);
          payload.workLevelSetting = workLevelSettingValue;
        }
        // Site ID is required for site structure validation
        if (data.siteId || selectedTaskDetails?.siteId) {
          payload.siteId = Number(data.siteId || selectedTaskDetails?.siteId);
        }
        if (data.siteStructureUnitId) {
          const n = Number(data.siteStructureUnitId);
          if (!Number.isNaN(n)) payload.siteStructureUnitId = n;
        }
        if (data.floorNo) {
          payload.floorNo = data.floorNo;
        }
        if (data.siteStructureSubUnitId) {
          const n = Number(data.siteStructureSubUnitId);
          if (!Number.isNaN(n)) payload.siteStructureSubUnitId = n;
        }
        if (data.locationId) {
          const n = Number(data.locationId);
          if (!Number.isNaN(n)) payload.locationId = n;
        }
      }

      // Format and include date fields if they've changed
      if (
        dirtyFields &&
        dirtyFields.taskPlannedStartDate &&
        !isOnlyToolsUpdate
      ) {
        payload.taskPlannedStartDate = dayjs(data.taskPlannedStartDate).format(
          "YYYY-MM-DD",
        );
      }

      if (dirtyFields && dirtyFields.taskPlannedEndDate && !isOnlyToolsUpdate) {
        payload.taskPlannedEndDate = dayjs(data.taskPlannedEndDate).format(
          "YYYY-MM-DD",
        );
      }

      // Include budget line information if it has changed
      if (dirtyFields && dirtyFields.budgetLineId && !isOnlyToolsUpdate) {
        payload.budgetLineId = Number(data.budgetLineId);
      }

      if (
        dirtyFields &&
        dirtyFields.allocatedBudgetAmount &&
        !isOnlyToolsUpdate
      ) {
        payload.budgetValue = Number(data.allocatedBudgetAmount);
      }

      // Include rate if it has changed. Only the GST-inclusive rate and the two
      // percentages are sent — the server re-derives base/GST/cess/upload from them.
      if (dirtyFields && dirtyFields.rate && !isOnlyToolsUpdate) {
        payload.rate = Number(data.rate);
      }
      if (dirtyFields && dirtyFields.gstPercent && !isOnlyToolsUpdate) {
        payload.gstPercent = Number(data.gstPercent);
      }
      if (dirtyFields && dirtyFields.cessPercent && !isOnlyToolsUpdate) {
        payload.cessPercent = Number(data.cessPercent);
      }

      // Include reminders ONLY if they exist and have been modified
      if (formattedReminders && hasReminderChanges && !isOnlyToolsUpdate) {
        // Remove internal properties that shouldn't be sent to API
        const cleanReminders = { ...formattedReminders };
        delete cleanReminders.isLoadedFromAPI;

        // Ensure all required fields are present and reminderId is included for updates
        if (cleanReminders.reminderType && cleanReminders.specificTime) {
          // For updates, ensure reminderId is present
          if (
            selectedTaskDetails?.reminders?.reminderId &&
            !cleanReminders.reminderId
          ) {
            cleanReminders.reminderId =
              selectedTaskDetails.reminders.reminderId;
          }

          payload.reminders = cleanReminders;
          // Only set reminderFlag if it's not already set by form changes
          if (!dirtyFields?.reminderFlag) {
            payload.reminderFlag = true; // Set reminderFlag to true when reminders exist
          }
        }
      } else if (
        selectedTaskDetails?.reminders &&
        !remainders &&
        hasReminderChanges
      ) {
        // If there was a reminder before but now it's removed, set reminderFlag to false
        // Only if reminders have actually changed (not just other fields)
        if (!dirtyFields?.reminderFlag) {
          payload.reminderFlag = false;
        }
      }
      // If reminders exist but no changes, DON'T send them to avoid API errors

      // Only proceed with general update if there are other changes besides task completion
      // Remove taskOwnerId from payload if it hasn't actually changed
      if (
        payload?.taskOwnerId &&
        selectedTaskDetails?.taskOwnerId &&
        String(payload.taskOwnerId) === String(selectedTaskDetails.taskOwnerId)
      ) {
        delete payload.taskOwnerId;
      }

      payload.contractorId = Number(payload?.contractorId);

      if (
        payload?.contractorId &&
        selectedTaskDetails?.contractorId &&
        payload.contractorId === Number(selectedTaskDetails.contractorId)
      ) {
        delete payload.contractorId;
      }
      // Check if there are any meaningful changes after removing taskOwnerId
      const meaningfulFields = Object.keys(payload).filter(
        (key) =>
          !["taskId", "projectId", "phaseId", "activityId"].includes(key),
      );

      // If no meaningful changes, don't call API
      if (meaningfulFields.length === 0) {
        setLoading(false);
        setDisableSubmit(false);
        return;
      }

      const hasOwnerChanged =
        payload?.taskOwnerId &&
        selectedTaskDetails?.taskOwnerId &&
        String(payload.taskOwnerId) !== String(selectedTaskDetails.taskOwnerId);

      if (hasOwnerChanged) {
        setLoading(false);
        setOwnerChangeModalOpen({
          open: true,
          extraData: payload,
        });
      } else {
        updateTaskDetails(payload);
      }
    } catch (error: any) {
      console.error("Error updating task:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update task. Please check your inputs and try again.";
      messageApi.open({
        type: "error",
        content: errorMessage,
      });
    } finally {
      setDisableSubmit(false);
    }
  };

  // Render-time "are there unsaved edits?" detector used to gate the Save
  // button. Mirrors the change checks in submitNewTask, but ignores a
  // spuriously-dirty empty priorityId, and compares each section against the
  // originally-loaded task so that reverting an edit back to its original
  // value correctly reports "no change" (button disables again).
  const hasUnsavedChanges = (): boolean => {
    try {
      // 1) Scalar (react-hook-form) fields — ignore empty/unchanged priorityId,
      // and skip array/section fields that are compared explicitly below
      // (their RHF dirty flags can be noisy; the section diffs are accurate).
      const sectionHandledKeys = new Set([
        "taskBoqs",
        "boqLineIds",
        "projectUserId",
      ]);
      const dirtyKeys = Object.keys(dirtyFields || {}).filter((k) => {
        if (!(dirtyFields as any)[k]) return false;
        if (sectionHandledKeys.has(k)) return false;
        if (k === "priorityId") {
          const cur = methods.getValues("priorityId");
          if (cur === "" || cur === null || cur === undefined) return false;
          return (
            Number(cur) !== Number(selectedTaskDetails?.priorityId ?? NaN)
          );
        }
        return true;
      });
      if (dirtyKeys.length > 0) return true;

      // 2) Tags
      const origTags = selectedTaskDetails?.taskTags || [];
      if (tags.length !== origTags.length) return true;
      if (tags.some((t: any, i: number) => t.value !== origTags[i]?.tagId))
        return true;

      // 3) Reminders
      if (hasReminderMeaningfulChanges()) return true;

      // 4) Tools (+ their split-materials)
      const existingTools = selectedTaskDetails?.taskTools || [];
      const currentTools = taskTools || [];
      if (currentTools.length !== existingTools.length) return true;
      for (const ct of currentTools) {
        const et = existingTools.find((e: any) => e.toolId === ct.toolId);
        if (
          !et ||
          (et.serialNumber ?? "") !== (ct.serialNumber ?? "") ||
          Number(et.rate ?? "") !== Number(ct.rate ?? "") ||
          Number(et.quantity ?? "") !== Number(ct.quantity ?? "") ||
          Number(et.workUomId ?? "") !== Number(ct.workUomId ?? "") ||
          Number(et.rateAnalysisQty ?? "") !==
            Number(ct.rateAnalysisQty ?? "") ||
          Number(et.rateAnalysisRate ?? "") !==
            Number(ct.rateAnalysisRate ?? "")
        )
          return true;
        const em = et.toolMaterials || [];
        const cm = ct.toolMaterials || [];
        if (em.length !== cm.length) return true;
        for (const m of cm) {
          if (m.taskToolMaterialId == null) return true; // newly added
          const pm = em.find(
            (x: any) => x.taskToolMaterialId === m.taskToolMaterialId,
          );
          if (
            !pm ||
            Number(pm.rate ?? "") !== Number(m.rate ?? "") ||
            Number(pm.quantity ?? "") !== Number(m.quantity ?? "") ||
            Number(pm.workUomId ?? "") !== Number(m.workUomId ?? "") ||
            Number(pm.subMaterialId ?? "") !== Number(m.subMaterialId ?? "") ||
            Number(pm.rateAnalysisQty ?? "") !==
              Number(m.rateAnalysisQty ?? "") ||
            Number(pm.rateAnalysisRate ?? "") !==
              Number(m.rateAnalysisRate ?? "")
          )
            return true;
        }
      }

      // 5) Hyperlinks
      const existingLinks = selectedTaskDetails?.hyperlinks || [];
      const currentLinks = hyperlinks || [];
      if (currentLinks.length !== existingLinks.length) return true;
      for (const ch of currentLinks) {
        const eh = existingLinks.find((e: any) => e.name === ch.name);
        if (!eh || eh.link !== ch.link) return true;
      }

      // 6) Approval hierarchy
      const existingAppr = selectedTaskDetails?.stateApprovalHierarchy || [];
      const currentAppr = approvals.data || [];
      if (currentAppr.length !== existingAppr.length) return true;
      for (const ca of currentAppr) {
        const ea = existingAppr.find(
          (e: any) =>
            e.stateApprovalHierarchyId === ca.stateApprovalHierarchyId,
        );
        if (
          !ea ||
          ea.userId !== ca.userId ||
          ea.roleId !== ca.roleId ||
          ea.level !== ca.level ||
          ea.approvalStage !== ca.approvalStage
        )
          return true;
      }

      // 7) Resources
      const resVals = resourceForm.getFieldsValue();
      const existingRes = selectedTaskDetails?.taskResources || [];
      const currentRes = (selectedResourceKeys || [])
        .map((key: React.Key) => {
          const count = resVals[`resourceCount_${key}`];
          if (count && Number(count) > 0) {
            return {
              activityResourceId: Number(key),
              resourceCount: Number(count),
              rate: resVals[`rate_${key}`],
              quantity: resVals[`quantity_${key}`],
              workUomId: resVals[`workUomId_${key}`],
              rateAnalysisQty: resVals[`rateAnalysisQty_${key}`],
              rateAnalysisRate: resVals[`rateAnalysisRate_${key}`],
            };
          }
          return null;
        })
        .filter(Boolean) as any[];
      if (currentRes.length !== existingRes.length) return true;
      for (const cr of currentRes) {
        const er = existingRes.find(
          (e: any) => e.activityResourceId === cr.activityResourceId,
        );
        if (
          !er ||
          Number(er.resourceCount ?? "") !== Number(cr.resourceCount ?? "") ||
          Number(er.rate ?? "") !== Number(cr.rate ?? "") ||
          Number(er.quantity ?? "") !== Number(cr.quantity ?? "") ||
          Number(er.workUomId ?? "") !== Number(cr.workUomId ?? "") ||
          Number(er.rateAnalysisQty ?? "") !==
            Number(cr.rateAnalysisQty ?? "") ||
          Number(er.rateAnalysisRate ?? "") !==
            Number(cr.rateAnalysisRate ?? "")
        )
          return true;
      }

      // 8) BOQ (allocate qty / rate / rate-analysis + line add/remove)
      const boqVals = form.getFieldsValue();
      const existingBoqs = selectedTaskDetails?.taskBoqs || [];
      const currentBoqIds = new Set<number>();
      Object.keys(boqVals)
        .filter((k) => k.startsWith("allocateBOQQuantity_"))
        .forEach((k) => {
          const id = Number(k.split("_")[1]);
          if (boqVals[k] !== undefined && boqVals[k] !== null)
            currentBoqIds.add(id);
        });
      if (currentBoqIds.size !== existingBoqs.length) return true;
      for (const b of existingBoqs) {
        if (!currentBoqIds.has(b.activityBoqId)) return true;
        const id = b.activityBoqId;
        if (
          Number(b.allotedQuantity ?? "") !==
            Number(boqVals[`allocateBOQQuantity_${id}`] ?? "") ||
          Number(b.rate ?? "") !== Number(boqVals[`boqRate_${id}`] ?? "") ||
          Number(b.rateAnalysisQty ?? "") !==
            Number(boqVals[`boqRateAnalysisQty_${id}`] ?? "") ||
          Number(b.rateAnalysisRate ?? "") !==
            Number(boqVals[`boqRateAnalysisRate_${id}`] ?? "")
        )
          return true;
      }

      // 9) Associated team users
      const currentTeamIds = (
        (methods.getValues("projectUserId") as any[]) || []
      )
        .filter((v: any) => v !== undefined && v !== null)
        .map((v: any) => Number(v));
      const existingTeamIds = (selectedTaskDetails?.taskUsers || [])
        .filter((u: any) => u?.taskUserType !== "Owner")
        .map((u: any) => {
          const candidateId = u.projectUserId ?? u.userId;
          const matched = projectUsers.find(
            (pu: any) =>
              (pu.projectUserId != null &&
                Number(pu.projectUserId) === Number(candidateId)) ||
              Number(pu.value) === Number(candidateId) ||
              Number((pu as any).userId) === Number(candidateId),
          );
          return matched?.value;
        })
        .filter((v: any) => v !== undefined && v !== null)
        .map((v: any) => Number(v));
      if (currentTeamIds.length !== existingTeamIds.length) return true;
      const teamSet = new Set(existingTeamIds);
      if (currentTeamIds.some((id: number) => !teamSet.has(id))) return true;

      return false;
    } catch {
      // On any unexpected error, don't block saving.
      return true;
    }
  };

  const updateTaskDetails = async (payload: any) => {
    try {
      // === Send API Call ===
      const response: ApiResponse = await tasksApis.updateTaskbyId(payload);
      const { data: task, status, error, message } = response || {};

      if (status) {
        // Update task completion percentage only if it has changed

        Swal.fire({
          icon: "success" as SweetAlertIcon,
          title: "Success",
          text: task?.message,
          confirmButtonText: "Ok",
        }).then(async () => {
          // When mounted from the new TaskQuickViewCard summary
          // (initialEditMode=true), bounce the user back to the summary
          // IMMEDIATELY so they don't briefly see the legacy in-form
          // read-only view while async refreshes run. The summary card
          // subscribes to Redux, so fetchTaskById's dispatch below will
          // refresh it with the updated data.
          if (onBackToSummary) {
            onBackToSummary();
          }

          setIsEditMode(false);
          // Reset dirty fields after successful update
          reset(); // Reset form to clear dirty state
          setSelectedResourceKeys([]);
          setEditingResourceKeys([]);
          setTaskTools([]); // Reset task tools
          setSelectedToolType(null); // Reset tool selection
          setSelectedTool(null); // Reset tool selection
          setHyperlinks([]); // Reset hyperlinks
          setCurrentHyperlinkName(""); // Reset current hyperlink name
          setCurrentHyperlinkUrl(""); // Reset current hyperlink URL
          resourceForm.resetFields();

          // Reset approval hierarchy - will be reloaded from fetchTaskById
          setApprovals({
            open: false,
            data: [],
          });

          // Refresh task data to show updated information
          await fetchTaskById();

          // Refresh budget lines to get updated allocation data
          if (projectId) {
            await fetchBudgetLines();
          }

          // Refresh subtask list if subtask tab is active
          if (activeTab === "SubTasks") {
            dispatch(
              updateTaskListDetailsModalSlice({ refreshSubTaskList: true }),
            );
          }

          safeRefreshData();
        });
      } else {
        setErrorData({ error, message });
        setTimeout(() => {
          setErrorData(undefined);
        }, 10000);
      }
    } catch (error) {
      console.error("Error updating task:", error);
    } finally {
      setLoading(false);
      setDisableSubmit(false);
    }
  };

  const updateTaskCompletion = async (
    taskId: number,
    taskCompletionPercentage: number,
    callFetchTaskById: boolean = true,
  ) => {
    try {
      const response = await tasksApis.statusChangeTask(
        taskId,
        taskCompletionPercentage,
      );
      const { status: apiStatus } = response || {};

      if (apiStatus) {
        return true; // Return success
      } else {
        // Show error message
        messageApi.open({
          type: "error",
          content: "Failed to update task completion percentage.",
        });
        return false; // Return failure
      }
    } catch (error) {
      console.error("Error updating task completion:", error);
      // Show error message
      messageApi.open({
        type: "error",
        content: "Error updating task completion percentage.",
      });
      return false; // Return failure
    } finally {
      setIsEditMode(false);
      if (callFetchTaskById) fetchTaskById();
    }
  };
  const [isLoadingTaskStatus, setIsLoadingTaskStatus] = useState(false);

  // Fetch task status list
  const fetchTaskStatusList = async () => {
    if (isLoadingTaskStatus) return;
    setIsLoadingTaskStatus(true);
    try {
      setLoading(true);
      const response = await tasksApis.fetchTaskStatusList("TaskStatusChange");
      const { data, status } = response || {};
      if (status) {
        // Define your desired order: Open -> InProgress -> Resolved -> UnderQC -> Closed
        const desiredOrder = [
          "Open",
          "OnHold",
          "InProgress",
          "Resolved",
          "UnderQC",
          "Closed",
        ];
        // Map and filter the API data to match the desired order
        const formattedStatusList = desiredOrder
          .map((statusName) => {
            // Find the item in the API data (case-insensitive)
            const item = data.find(
              (d: string) => d.toLowerCase() === statusName.toLowerCase(),
            );
            if (item) {
              return {
                label: item,
                key: item,
                value: item,
              };
            }
            return null;
          })
          .filter(Boolean); // Remove any nulls if a status is missing in API

        setTaskStatusList(formattedStatusList);
      }
    } catch (error: any) {
      console.error("Error fetching task status list:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch task status list. Please try again.";
      messageApi.open({
        type: "error",
        content: errorMessage,
      });
    } finally {
      setLoading(false);
      setIsLoadingTaskStatus(false);
    }
  };

  // Helper to show executed rate modal when closing task
  const showExecutedRateModal = (newStatus: string): void => {
    setPendingClosureStatus(newStatus);
    setExecutedRate(null);
    setRateRemarks("");
    setExecutedRateModalVisible(true);
    // Reset the cancelled flag when showing the modal again
    setExecutedRateModalCancelled(false);
  };

  // Handle executed rate modal submit
  const handleExecutedRateSubmit = async () => {
    const taskRate = selectedTaskDetails?.rate || 0;

    if (executedRate === null || executedRate === undefined) {
      messageApi.open({
        type: "error",
        content: "Please enter the executed rate.",
      });
      return;
    }

    const executedRateNum = Number(executedRate);

    if (executedRateNum !== taskRate && (!rateRemarks || !rateRemarks.trim())) {
      messageApi.open({
        type: "error",
        content: "Please provide remarks for the rate difference.",
      });
      return;
    }

    try {
      setExecutedRateModalVisible(false);
      setExecutedRateModalCancelled(false); // Reset cancelled flag on successful submit
      await changeStatus(pendingClosureStatus!, {
        executedRate: executedRateNum,
        ...(executedRateNum !== taskRate && rateRemarks?.trim()
          ? { rateRemarks: rateRemarks.trim() }
          : {}),
      });
      setPendingClosureStatus(null);
      setExecutedRate(null);
      setRateRemarks("");
    } catch (error) {
      console.error("Error closing task:", error);
      setExecutedRateModalVisible(true); // Re-open modal on error
    }
  };

  // Helper to change status and show Swal
  const changeStatus = async (
    newStatus: string,
    additionalData?: { executedRate?: number; rateRemarks?: string },
  ) => {
    try {
      setLoading(true);
      const response = await tasksApis.statusChangeTask(
        selectedTaskDetails.taskId,
        newStatus,
        additionalData,
      );
      const { status, message, data } = response || {};
      if (status) {
        // Clear any existing error data when status change is successful
        setErrorData(undefined);
        Swal.fire({
          icon: "success" as SweetAlertIcon,
          title: "Success",
          text:
            data?.message ||
            message ||
            `Task status updated to ${newStatus} successfully!`,
          confirmButtonText: "Ok",
        });
        dispatch(
          updateTaskListDetailsModalSlice({
            selectedTaskDetails: {
              ...selectedTaskDetails,
              taskStatus: newStatus,
              // Clear OnHold fields if status is no longer OnHold
              ...(newStatus.toLowerCase() !== "onhold" && {
                taskOnholdReason: undefined,
                taskOnholdDate: undefined,
                taskETAResolution: undefined,
              }),
            },
          }),
        );
        await fetchTaskById();
        refreshData();

        // Update allowEdit based on new task status
        const isTaskEditable = isTaskStatusEditable(newStatus);

        // Update allowEdit in store - combine task status check with original allowEdit
        const updatedAllowEdit = isTaskEditable && originalAllowEdit;

        dispatch(
          updateTaskListDetailsModalSlice({
            allowEdit: updatedAllowEdit,
          }),
        );

        // If task is no longer editable and we're in edit mode, exit edit mode
        if (!updatedAllowEdit && isEditMode) {
          setIsEditMode(false);
          setDisableSubmit(false);
        }

        safeRefreshData();
      } else {
        Swal.fire({
          icon: "error" as SweetAlertIcon,
          title: "Error",
          text: message || "Failed to update status.",
          confirmButtonText: "Ok",
        });
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update status. Please try again.";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonText: "Ok",
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper to sequentially show modals for multiple checklists

  // Handle status change
  const handleStatusChange = async ({ key }: { key: string }) => {
    const newStatus = key;
    const currentStatus = selectedTaskDetails?.taskStatus;
    // Add 'Active' and 'Resolved' to the status order for checklist logic
    const statusOrder = [
      "Open",
      "Active",
      "InProgress",
      "Resolved",
      "UnderQC",
      "Closed",
    ];
    const currentStatusIndex = statusOrder.findIndex(
      (s) => s.toLowerCase() === (currentStatus || "").toLowerCase(),
    );
    const newStatusIndex = statusOrder.findIndex(
      (s) => s.toLowerCase() === (newStatus || "").toLowerCase(),
    );

    // === OnHold Modal Logic - For InProgress/UnderQC/Active/Open → OnHold ===
    if (
      newStatus.toLowerCase() === "onhold" &&
      (currentStatus === "InProgress" ||
        currentStatus === "UnderQC" ||
        currentStatus === "Active" ||
        currentStatus === "Open")
    ) {
      const modal = Modal.confirm({
        title: "",
        content: (
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <label
                style={{ display: "block", marginBottom: 8, fontWeight: 500 }}
              >
                Reason for putting task on hold: *
              </label>
              <Input.TextArea
                id="onhold-reason"
                placeholder="Please provide the reason for putting this task on hold..."
                rows={3}
                style={{ width: "100%" }}
              />
            </div>
            <div>
              <label
                style={{ display: "block", marginBottom: 8, fontWeight: 500 }}
              >
                Expected date to resume: *
              </label>
              <DatePicker
                id="expected-resume-date"
                style={{ width: "100%" }}
                placeholder="Select expected resume date"
                format="YYYY-MM-DD"
                disabledDate={(current) => {
                  // Disable past dates
                  return current && current < dayjs().startOf("day");
                }}
              />
            </div>
            <Space style={{ marginTop: 8 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    onClick={async () => {
                      const reason = (
                        document.getElementById(
                          "onhold-reason",
                        ) as HTMLTextAreaElement
                      )?.value;
                      const expectedResumeDate = (
                        document.getElementById("expected-resume-date") as any
                      )?.value;

                      if (!reason || !reason.trim()) {
                        messageApi.open({
                          type: "error",
                          content:
                            "Please provide a reason for putting the task on hold.",
                        });
                        return Promise.reject();
                      }

                      if (!expectedResumeDate) {
                        messageApi.open({
                          type: "error",
                          content: "Please select an expected resume date.",
                        });
                        return Promise.reject();
                      }

                      // Continue with status change API call
                      try {
                        setLoading(true);
                        const response = await tasksApis.statusChangeTask(
                          selectedTaskDetails.taskId,
                          newStatus,
                          {
                            taskOnholdReason: reason.trim(),
                            taskETAResolution:
                              dayjs(expectedResumeDate).format("YYYY-MM-DD"),
                          },
                        );
                        const { status, message } = response || {};
                        if (status) {
                          // Clear any existing error data when status change is successful
                          setErrorData(undefined);
                          modal.destroy();
                          Swal.fire({
                            icon: "success",
                            title: "Success",
                            text: "Task status updated successfully!",
                            confirmButtonText: "Ok",
                          });
                          const updatedTaskDetails = {
                            ...selectedTaskDetails,
                            taskStatus: newStatus,
                            taskOnholdReason: reason.trim(),
                            taskOnholdDate: dayjs().format("YYYY-MM-DD"),
                            taskETAResolution:
                              dayjs(expectedResumeDate).format("YYYY-MM-DD"),
                          };

                          dispatch(
                            updateTaskListDetailsModalSlice({
                              selectedTaskDetails: updatedTaskDetails,
                            }),
                          );

                          // Update allowEdit based on new task status
                          const isTaskEditable =
                            isTaskStatusEditable(newStatus);

                          // Update allowEdit in store - combine task status check with original allowEdit
                          const updatedAllowEdit =
                            isTaskEditable && originalAllowEdit;

                          dispatch(
                            updateTaskListDetailsModalSlice({
                              allowEdit: updatedAllowEdit,
                            }),
                          );

                          // If task is no longer editable and we're in edit mode, exit edit mode
                          if (!updatedAllowEdit && isEditMode) {
                            setIsEditMode(false);
                            setDisableSubmit(false);
                          }

                          safeRefreshData();
                        } else {
                          modal.destroy();
                          // Add a small delay to ensure modal is fully closed before showing error
                          setTimeout(() => {
                            Swal.fire({
                              icon: "error",
                              title: "Error",
                              text: message,
                              confirmButtonText: "Ok",
                            });
                          }, 100);
                        }
                      } catch (error: any) {
                        console.error("Error updating task status:", error);
                        modal.destroy();
                        // Add a small delay to ensure modal is fully closed before showing error
                        setTimeout(() => {
                          const errorMessage =
                            error?.response?.data?.message ||
                            error?.message ||
                            "Failed to update status. Please try again.";
                          Swal.fire({
                            icon: "error",
                            title: "Error",
                            text: errorMessage,
                            confirmButtonText: "Ok",
                          });
                        }, 100);
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    Submit
                  </Button>
                  <Button
                    onClick={() => {
                      modal.destroy();
                    }}
                  >
                    Cancel
                  </Button>
            </Space>
          </div>
        ),
        width: 500,
        icon: <></>,
        footer: null,
        closable: true,
        onCancel: () => {
          // User cancelled the action
        },
      });
      return;
    }

    // === OnHold → InProgress/UnderQC: Use showSequentialChecklists ===
    if (
      currentStatus === "OnHold" &&
      (newStatus === "InProgress" || newStatus === "UnderQC")
    ) {
      await showSequentialChecklists([newStatus], () =>
        changeStatus(newStatus),
      );
      return;
    }

    // === Direct OnHold change (Open → OnHold) ===
    if (newStatus.toLowerCase() === "onhold") {
      await changeStatus(newStatus);
      return;
    }

    // UnderQC → Closed: special logic
    if (currentStatus === "UnderQC" && newStatus === "Closed") {
      // Validate task completion percentage before proceeding
      const taskCompletionPercentage =
        selectedTaskDetails?.taskCompletionPercentage || 0;
      if (Math.round(taskCompletionPercentage) !== 100) {
        Swal.fire({
          icon: "warning",
          title: "Cannot Close Task",
          text: `Task completion percentage must be 100% to close the task. Current: ${Math.round(
            taskCompletionPercentage,
          )}%`,
          confirmButtonText: "Ok",
        });
        return;
      }

      const inProgressChecklist = selectedTaskDetails?.checklistLinks?.find(
        (cl: any) => cl.status === "InProgress",
      );
      const underQCChecklist = selectedTaskDetails?.checklistLinks?.find(
        (cl: any) => cl.status === "UnderQC",
      );
      const closedChecklist = selectedTaskDetails?.checklistLinks?.find(
        (cl: any) => cl.status === "Closed",
      );

      if (closedChecklist) {
        // Check if previous checklists are filled (only if they exist)
        const hasUnfilledPreviousChecklists =
          (inProgressChecklist && !inProgressChecklist.answered) ||
          (underQCChecklist && !underQCChecklist.answered);

        if (hasUnfilledPreviousChecklists) {
          Swal.fire({
            icon: "warning",
            title: "Please fill required checklist(s) below.",
            text: "You must fill all previous checklists before closing.",
            confirmButtonText: "Ok",
          });
          return;
        }

        // If checklist is already answered, skip directly to rate modal
        if (closedChecklist.answered) {
          showExecutedRateModal(newStatus);
          return;
        }

        // If previous checklists are filled, show Closed checklist modal
        if (!closedChecklist.answered) {
          Modal.confirm({
            icon: null,
            title: (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <span>Checklist Required</span>
                <CloseOutlined
                  style={{
                    fontSize: 18,
                    cursor: "pointer",
                    marginRight: -8,
                    color: "#888",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (typeof (Modal as any).destroyAll === "function") {
                      (Modal as any).destroyAll();
                    }
                  }}
                  data-testid="checklist-modal-close"
                />
              </div>
            ),
            content: (
              <div style={{ marginTop: 16 }}>
                <p>
                  To change the status to{" "}
                  <strong style={{ color: "#2557d6", fontWeight: 600 }}>
                    Closed
                  </strong>{" "}
                  you need to complete the checklist:{" "}
                  <strong style={{ color: "#2557d6", fontWeight: 500 }}>
                    {closedChecklist.checklistName}
                  </strong>
                </p>
              </div>
            ),
            width: 500,
            okText: "Fill Now",
            cancelButtonProps: { style: { display: "none" } }, // Hide skip
            okButtonProps: { type: "primary" },
            onOk: () => {
              setSelectedChecklistForModal({
                checklistId: closedChecklist.checklistId,
                checklistLinkId: closedChecklist.checklistLinkId,
              });
              setShowChecklistModal(true);
              pendingStatusRef.current = newStatus;
            },
          });
          return;
        }
      }
      // If no checklist for Closed, or all are filled, show executed rate modal before changing status
      showExecutedRateModal(newStatus);
      return;
    }

    // Helper to normalize status strings (lowercase, no spaces)

    // Open → Active, Open → InProgress, Active → InProgress, or InProgress → UnderQC: Use showSequentialChecklists
    if (
      (currentStatusIndex === 0 && newStatusIndex === 1) || // Open → Active
      (currentStatusIndex === 0 && newStatusIndex === 2) || // Open → InProgress
      (currentStatusIndex === 1 && newStatusIndex === 2) || // Active → InProgress
      (currentStatusIndex === 2 && newStatusIndex === 3) // InProgress → UnderQC
    ) {
      await showSequentialChecklists([newStatus], () =>
        changeStatus(newStatus),
      );
      return;
    }

    // For Closed status from any other status, validate and show executed rate modal
    if (newStatus.toLowerCase() === "closed") {
      // Validate task completion percentage before proceeding
      const taskCompletionPercentage =
        selectedTaskDetails?.taskCompletionPercentage || 0;
      if (Math.round(taskCompletionPercentage) !== 100) {
        Swal.fire({
          icon: "warning",
          title: "Cannot Close Task",
          text: `Task completion percentage must be 100% to close the task. Current: ${Math.round(
            taskCompletionPercentage,
          )}%`,
          confirmButtonText: "Ok",
        });
        return;
      }
      showExecutedRateModal(newStatus);
      return;
    }

    // All other transitions: just call API, no modal
    await changeStatus(newStatus);
  };

  // Helper to sequentially show modals for multiple checklists
  const showSequentialChecklists = async (
    statusesToCheck: string[],
    onComplete: () => Promise<void>,
  ) => {
    for (const status of statusesToCheck) {
      const checklist = selectedTaskDetails?.checklistLinks?.find(
        (cl: any) => cl.status === status,
      );

      // Only show modal if checklist exists and is not answered
      if (checklist && !checklist.answered) {
        await new Promise<void>((resolve) => {
          Modal.confirm({
            icon: null,
            title: (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <span>Checklist Required</span>
                <CloseOutlined
                  style={{
                    fontSize: 18,
                    cursor: "pointer",
                    marginRight: -8,
                    color: "#888",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (typeof (Modal as any).destroyAll === "function") {
                      (Modal as any).destroyAll();
                    }
                  }}
                  data-testid="checklist-modal-close"
                />
              </div>
            ),
            content: (
              <div style={{ marginTop: 16 }}>
                <p>
                  To change the status to{" "}
                  <strong style={{ color: "#2557d6", fontWeight: 600 }}>
                    {status}
                  </strong>{" "}
                  you need to complete the checklist:{" "}
                  <strong style={{ color: "#2557d6", fontWeight: 500 }}>
                    {checklist.checklistName}
                  </strong>
                </p>
                <p>
                  Would you like to fill the checklist now or skip it for later?
                </p>
              </div>
            ),
            width: 500,
            okText: "Fill Now",
            cancelText: "Skip for Later",
            okButtonProps: { type: "primary" },
            cancelButtonProps: { type: "default" },
            onOk: () => {
              setSelectedChecklistForModal({
                checklistId: checklist.checklistId,
                checklistLinkId: checklist.checklistLinkId,
              });
              setShowChecklistModal(true);
              pendingStatusRef.current = status;
            },
            onCancel: async () => {
              // For skip, resolve immediately to call status API
              resolve();
              return Promise.resolve();
            },
          });
        });
      }
      // If checklist doesn't exist or is already answered, continue to next
    }
    await onComplete();
  };

  // Handle modal close and status update
  const handleChecklistModalClose = async (
    action: "skip" | "save",
    checklistSuccess?: boolean,
  ) => {
    setShowChecklistModal(false);
    setModalAction(null);
    const statusToSet = pendingStatusRef.current;

    // If action is "save" and checklist submission failed, don't proceed with status change
    if (action === "save" && checklistSuccess === false) {
      pendingStatusRef.current = null;
      return;
    }

    if (statusToSet) {
      // If status is "Closed", validate completion percentage and show executed rate modal
      if (statusToSet.toLowerCase() === "closed") {
        // Validate task completion percentage before showing rate modal
        const taskCompletionPercentage =
          selectedTaskDetails?.taskCompletionPercentage || 0;
        if (Math.round(taskCompletionPercentage) !== 100) {
          pendingStatusRef.current = null;
          Swal.fire({
            icon: "warning",
            title: "Cannot Close Task",
            text: `Task completion percentage must be 100% to close the task. Current: ${Math.round(
              taskCompletionPercentage,
            )}%`,
            confirmButtonText: "Ok",
          });
          // Refresh task data to update checklist status
          await fetchTaskById();
          return;
        }
        pendingStatusRef.current = null;
        showExecutedRateModal(statusToSet);
        return;
      }

      try {
        setLoading(true);
        const response = await tasksApis.statusChangeTask(
          selectedTaskDetails.taskId,
          statusToSet,
        );
        const { status, message } = response || {};
        if (status) {
          // Clear any existing error data when status change is successful
          setErrorData(undefined);
          await Swal.fire({
            icon: "success" as SweetAlertIcon,
            title: "Success",
            text: `Task status updated to ${statusToSet} successfully!`,
            confirmButtonText: "Ok",
          });
          dispatch(
            updateTaskListDetailsModalSlice({
              selectedTaskDetails: {
                ...selectedTaskDetails,
                taskStatus: statusToSet,
                // Clear OnHold fields if status is no longer OnHold
                ...(statusToSet.toLowerCase() !== "onhold" && {
                  taskOnholdReason: undefined,
                  taskOnholdDate: undefined,
                  taskETAResolution: undefined,
                }),
              },
            }),
          );

          // Update allowEdit based on new task status
          const isTaskEditable = isTaskStatusEditable(statusToSet);

          // Update allowEdit in store - combine task status check with original allowEdit
          const updatedAllowEdit = isTaskEditable && originalAllowEdit;

          dispatch(
            updateTaskListDetailsModalSlice({
              allowEdit: updatedAllowEdit,
            }),
          );

          // If task is no longer editable and we're in edit mode, exit edit mode
          if (!updatedAllowEdit && isEditMode) {
            setIsEditMode(false);
            setDisableSubmit(false);
          }

          await fetchTaskById(); // Always refresh checklist/task data after status change
          safeRefreshData();
        } else {
          await Swal.fire({
            icon: "error" as SweetAlertIcon,
            title: "Error",
            text: message || "Failed to update status.",
            confirmButtonText: "Ok",
          });
        }
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update status. Please try again.";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMessage,
          confirmButtonText: "Ok",
        });
      } finally {
        setLoading(false);
        pendingStatusRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (pendingSwal) {
      Swal.fire({
        icon: pendingSwal.icon,
        title: pendingSwal.title,
        text: pendingSwal.text,
        confirmButtonText: "Ok",
      });
      setPendingSwal(null);
    }
  }, [pendingSwal]);

  if (loading) {
    return (
      <div
        style={{
          position: "relative",
          minHeight: "200px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255, 255, 255, 0.8)",
        }}
      >
        <Spin indicator={<LoadingOutlined spin />} size="large" />
      </div>
    );
  }

  interface CollapsePanelProps {
    isEditMode: boolean;
  }

  const CollapsePanel: FC<CollapsePanelProps> = ({ isEditMode }) => {
    // View mode columns - for displaying selectedTaskDetails.taskBoqs
    const viewBoqColumns = [
      {
        title: "BOQ Title",
        dataIndex: "boqTargetNumber",
        key: "boqTargetNumber",
        width: 120,
        align: "center",
        render: (text: any, record: any) =>
          record?.boqTitle || record?.boqTargetNumber,
      },
      {
        title: "Category",
        dataIndex: "categoryName",
        key: "categoryName",
        width: 150,
        align: "center",
      },
      {
        title: "Material",
        dataIndex: "materialName",
        key: "materialName",
        width: 200,
        align: "center",
      },
      {
        title: "Task Available Qty",
        dataIndex: "taskAvailableQuantity",
        key: "taskAvailableQuantity",
        width: 150,
        align: "center",
        render: (text: any, record: any) => {
          const value =
            text ??
            record.taskAvailableQuantity ??
            record.availableQuantity ??
            record.allotedQuantity;
          if (value === undefined || value === null || isNaN(value)) {
            return "N/A";
          }
          return `${Number(value)} ${record.uomName || ""}`;
        },
      },
      {
        title: "Allocated Qty",
        dataIndex: "allotedQuantity",
        key: "allotedQuantity",
        width: 150,
        align: "center",
        render: (text: any, record: any) =>
          `${Number(text)} ${record.uomName || ""}`,
      },
      {
        title: "Rate",
        dataIndex: "rate",
        key: "rate",
        width: 120,
        align: "center",
        render: (text: any) => (text !== undefined && text !== null ? text : ""),
      },
      {
        title: "Total Amount",
        dataIndex: "totalAmount",
        key: "totalAmount",
        width: 140,
        align: "center",
        render: (text: any) => (text !== undefined && text !== null ? text : ""),
      },
    ];

    // Edit mode columns - for displaying activityBoqList data (same as TaskCreation)
    const editBoqColumns = [
      {
        title: "BOQ Title",
        dataIndex: "boqTargetNumber",
        key: "boqTargetNumber",
        width: 100,
        fixed: "left",
        align: "center",
        render: (text: any, record: any) =>
          record?.boqTitle || record?.boqTargetNumber,
      },
      {
        title: "Category",
        dataIndex: "categoryName",
        key: "categoryName",
        width: 150,
        align: "center",
      },
      {
        title: "Material",
        dataIndex: "materialName",
        key: "materialName",
        width: 200,
        align: "center",
      },
      {
        title: "BOQ Qty",
        dataIndex: "allotedQuantity",
        key: "allotedQuantity",
        width: 150,
        align: "center",
        render: (text: any, record: any) => {
          // Check if allotedQuantity is undefined, null, or NaN
          if (text === undefined || text === null || isNaN(text)) {
            return "N/A";
          }
          // Display BOQ quantity with UOM from the BOQ API response
          return `${Number(text)} ${record.uomName || ""}`;
        },
      },
      {
        title: "Task Available Qty",
        dataIndex: "taskAvailableQuantity",
        key: "taskAvailableQuantity",
        width: 150,
        align: "center",
        render: (text: any, record: any) => {
          const value =
            text ??
            record.taskAvailableQuantity ??
            record.availableQuantity ??
            record.allotedQuantity;
          if (value === undefined || value === null || isNaN(value)) {
            return "N/A";
          }
          return `${Number(value)} ${record.uomName || ""}`;
        },
      },
      ...(showRateAnalysis
        ? [
            {
              title: rateAnalysisLabel,
              align: "center" as const,
              onHeaderCell: () => ({
                style: { background: "#e6f4ff", fontWeight: 600 },
              }),
              children: [
                {
                  title: "Qty",
                  dataIndex: "boqRateAnalysisQty",
                  key: "boqRaQty",
                  width: 120,
                  align: "center" as const,
                  onCell: (record: any) => ({
                    record,
                    inputType: "number",
                    dataIndex: "boqRateAnalysisQty",
                    title: "RA Qty",
                    form,
                    outputFactor,
                  }),
                },
                {
                  // BOQ UOM is the material's fixed UOM (same as Allocate) —
                  // shown read-only, not editable.
                  title: "UOM",
                  dataIndex: "uomName",
                  key: "boqRaUom",
                  width: 120,
                  align: "center" as const,
                  render: (_: any, record: any) => record.uomName || "",
                },
                {
                  title: "Rate",
                  dataIndex: "boqRateAnalysisRate",
                  key: "boqRaRate",
                  width: 120,
                  align: "center" as const,
                  onCell: (record: any) => ({
                    record,
                    inputType: "number",
                    dataIndex: "boqRateAnalysisRate",
                    title: "RA Rate",
                    form,
                  }),
                },
                {
                  title: "Total",
                  dataIndex: "boqRateAnalysisTotalAmount",
                  key: "boqRaTotal",
                  width: 120,
                  align: "center" as const,
                  onCell: (record: any) => ({
                    record,
                    dataIndex: "boqRateAnalysisTotalAmount",
                    title: "Total",
                    form,
                  }),
                },
              ],
            },
          ]
        : []),
      ...(() => {
        const overall = [
          {
            title: "Allocate Qty",
            dataIndex: "allocateBOQQuantity",
            key: "allocateBOQQuantity",
            width: 180,
            align: "center" as const,
            onCell: (record: any) => ({
              record,
              inputType: "number",
              dataIndex: "allocateBOQQuantity",
              title: "Allocate Qty",
              form,
            }),
          },
          {
            // Material's fixed UOM (read-only) — same as the allocated UOM.
            title: "UOM",
            dataIndex: "uomName",
            key: "boqOverallUom",
            width: 120,
            align: "center" as const,
            render: (_: any, record: any) => record.uomName || "",
          },
          {
            title: "Rate",
            dataIndex: "boqRate",
            key: "boqRate",
            width: 150,
            align: "center" as const,
            onCell: (record: any) => ({
              record,
              inputType: "number",
              dataIndex: "boqRate",
              title: "Rate",
              form,
            }),
          },
          {
            title: "Total Amount",
            dataIndex: "boqTotalAmount",
            key: "boqTotalAmount",
            width: 150,
            align: "center" as const,
            onCell: (record: any) => ({
              record,
              inputType: "number",
              dataIndex: "boqTotalAmount",
              title: "Total Amount",
              form,
            }),
          },
        ];
        return showRateAnalysis
          ? [
              {
                title: overallLabel,
                align: "center" as const,
                onHeaderCell: () => ({
                  style: { background: "#f6ffed", fontWeight: 600 },
                }),
                children: overall,
              },
            ]
          : overall;
      })(),
    ];

    if (!isEditMode) {
      return (
        <>
          <div
            className="card"
            style={{
              marginBottom: "24px",
              border: "1px solid #e8e8e8",
              borderRadius: "8px",
            }}
          >
            <div className="card-body" style={{ padding: "20px" }}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <Icon
                    name="package"
                    style={{
                      fontSize: "20px",
                      color: "#004293",
                      marginRight: "8px",
                    }}
                  />
                  <h6
                    className="section-header mb-0"
                    style={{ color: "#004293", fontWeight: "600" }}
                  >
                    Materials
                  </h6>
                </div>
              </div>
              <div className="table-responsive poTable">
                <Table
                  className="customTable"
                  columns={viewBoqColumns as any}
                  dataSource={selectedTaskDetails?.taskBoqs || []}
                  scroll={{ x: "max-content" }}
                  bordered
                  size="middle"
                  pagination={false}
                  style={{ minWidth: "600px" }}
                />
              </div>
            </div>
          </div>
          <div
            className="card"
            style={{
              marginBottom: "24px",
              border: "1px solid #e8e8e8",
              borderRadius: "8px",
            }}
          >
            <div className="card-body" style={{ padding: "20px" }}>
              <div className="d-flex align-items-center mb-3">
                <Icon
                  name="users"
                  style={{
                    fontSize: "20px",
                    color: "#004293",
                    marginRight: "8px",
                  }}
                />
                <h6
                  className="section-header mb-0"
                  style={{ color: "#004293", fontWeight: "600" }}
                >
                  Allocated Manpower
                </h6>
              </div>
              <div className="table-responsive poTable">
                <Table
                  className="customTable"
                  rowKey={(record: any) => `${record.taskResourceId}`}
                  columns={[
                    {
                      title: "Labour Type",
                      dataIndex: "laborTypeName",
                      key: "laborTypeName",
                      width: 200,
                      align: "center",
                    },
                    {
                      title: "Labour Category",
                      dataIndex: "laborCategory",
                      key: "laborCategory",
                      width: 200,
                      align: "center",
                    },
                    {
                      title: "Resource Count",
                      dataIndex: "resourceCount",
                      key: "resourceCount",
                      width: 150,
                      align: "center",
                    },
                    {
                      title: "Quantity",
                      dataIndex: "quantity",
                      key: "quantity",
                      width: 120,
                      align: "center",
                      render: (text: any) =>
                        text !== undefined && text !== null ? text : "",
                    },
                    {
                      title: "UOM",
                      dataIndex: "workUomName",
                      key: "workUomName",
                      width: 150,
                      align: "center",
                      render: (text: any) => text || "",
                    },
                    {
                      title: "Rate",
                      dataIndex: "rate",
                      key: "rate",
                      width: 120,
                      align: "center",
                      render: (text: any) =>
                        text !== undefined && text !== null ? text : "",
                    },
                    {
                      title: "Total Amount",
                      dataIndex: "totalAmount",
                      key: "totalAmount",
                      width: 140,
                      align: "center",
                      render: (text: any) =>
                        text !== undefined && text !== null ? text : "",
                    },
                  ]}
                  dataSource={selectedTaskDetails?.taskResources || []}
                  scroll={{ x: "max-content" }}
                  bordered
                  size="middle"
                  pagination={false}
                  style={{ minWidth: "600px" }}
                />
              </div>
            </div>
          </div>
        </>
      );
    }

    // ---- Cost Summary helpers (live; recomputed on every render — the form
    // onValuesChange tick + taskTools state changes trigger re-render) ----
    const formatCurrency = (value: number) =>
      `₹${(Number.isFinite(value) ? value : 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    const SectionTotal: React.FC<{ label: string; amount: number }> = ({
      label,
      amount,
    }) => (
      <div
        className="d-flex justify-content-end align-items-center mt-2"
        style={{ gap: "8px" }}
      >
        <Text strong style={{ color: "#004293" }}>
          {label}:
        </Text>
        <Text strong style={{ color: "#004293", fontSize: "15px" }}>
          {formatCurrency(amount)}
        </Text>
      </div>
    );
    const boqVals = form.getFieldsValue();
    const resVals = resourceForm.getFieldsValue();
    const boqOverallTotal = (boqLinesDataSource || []).reduce(
      (s: number, r: any) => {
        const id = r.activityBoqId;
        return (
          s +
          (Number(boqVals[`allocateBOQQuantity_${id}`]) || 0) *
            (Number(boqVals[`boqRate_${id}`]) || 0)
        );
      },
      0,
    );
    const boqRaTotal = (boqLinesDataSource || []).reduce((s: number, r: any) => {
      const id = r.activityBoqId;
      return (
        s +
        (Number(boqVals[`boqRateAnalysisQty_${id}`]) || 0) *
          (Number(boqVals[`boqRateAnalysisRate_${id}`]) || 0)
      );
    }, 0);
    const resOverallTotal = (activityResourcesList || []).reduce(
      (s: number, r: any) => {
        const id = r.activityResourceId;
        const count = Number(resVals[`resourceCount_${id}`]) || 0;
        if (count <= 0) return s;
        return (
          s +
          (Number(resVals[`quantity_${id}`]) || 0) *
            (Number(resVals[`rate_${id}`]) || 0) *
            count
        );
      },
      0,
    );
    const resRaTotal = (activityResourcesList || []).reduce(
      (s: number, r: any) => {
        const id = r.activityResourceId;
        const count = Number(resVals[`resourceCount_${id}`]) || 0;
        if (count <= 0) return s;
        return (
          s +
          (Number(resVals[`rateAnalysisQty_${id}`]) || 0) *
            (Number(resVals[`rateAnalysisRate_${id}`]) || 0)
        );
      },
      0,
    );
    const toolsOverallTotal = (taskTools || []).reduce(
      (s, t) =>
        s +
        (Number(t.rate) || 0) *
          (Number(t.quantity) || 0) *
          (Number(t.count) || 1),
      0,
    );
    const toolsRaTotal = (taskTools || []).reduce(
      (s, t) =>
        s + (Number(t.rateAnalysisQty) || 0) * (Number(t.rateAnalysisRate) || 0),
      0,
    );

    return (
      <div>
        <div
          className="card"
          style={{
            marginBottom: "24px",
            border: "1px solid #e8e8e8",
            borderRadius: "8px",
          }}
        >
          <div className="card-body" style={{ padding: "20px" }}>
            <div className="d-flex align-items-center mb-3">
              <Icon
                name="users"
                style={{
                  fontSize: "20px",
                  color: "#004293",
                  marginRight: "8px",
                }}
              />
              <h6
                className="section-header mb-0"
                style={{ color: "#004293", fontWeight: "600" }}
              >
                Allocate Manpower
              </h6>
            </div>
            {/* Vertically center inline inputs and remove Form.Item error-space
                so Rate-Analysis / Overall cells line up across a row. */}
            <style>{`
              .ra-edit-table .ant-table-tbody > tr > td { vertical-align: middle; }
              .ra-edit-table .ant-form-item { margin-bottom: 0 !important; }
              .ra-edit-table .ant-form-item-explain,
              .ra-edit-table .ant-form-item-extra { min-height: 0; }
              .ra-edit-table .ant-input-number,
              .ra-edit-table .ant-select,
              .ra-edit-table .ant-picker { width: 100%; }
            `}</style>
            <Form
              form={resourceForm}
              component={false}
              onValuesChange={() => setCostTick((t) => t + 1)}
            >
              <Table
                className="ra-edit-table"
                rowKey={(record) => `${record.activityResourceId}`}
                columns={([
                  {
                    title: "Labour Type",
                    dataIndex: "laborTypeName",
                    key: "laborTypeName",
                    width: 200,
                    align: "center",
                  },
                  {
                    title: "Labour Category",
                    dataIndex: "laborCategory",
                    key: "laborCategory",
                    width: 200,
                    align: "center",
                  },
                  {
                    title: "Resource Count",
                    dataIndex: "resourceCount",
                    key: "resourceCount",
                    width: 150,
                    align: "center",
                    onCell: (record: any) => ({
                      record,
                      dataIndex: "resourceCount",
                      title: "Resource Count",
                      form: resourceForm,
                      editing: isResourceEditing(record),
                    }),
                    render: (text: any) => text || "",
                  },
                  ...(showRateAnalysis
                    ? [
                        {
                          title: rateAnalysisLabel,
                          align: "center" as const,
                          onHeaderCell: () => ({
                            style: { background: "#e6f4ff", fontWeight: 600 },
                          }),
                          children: [
                            {
                              title: "Qty",
                              dataIndex: "resourceRateAnalysisQty",
                              key: "resRaQty",
                              width: 120,
                              align: "center" as const,
                              onCell: (record: any) => ({
                                record,
                                dataIndex: "resourceRateAnalysisQty",
                                title: "RA Qty",
                                form: resourceForm,
                                editing: isResourceEditing(record),
                                outputFactor,
                              }),
                            },
                            {
                              title: "UOM",
                              dataIndex: "resourceRateAnalysisUomId",
                              key: "resRaUom",
                              width: 150,
                              align: "center" as const,
                              onCell: (record: any) => ({
                                record,
                                dataIndex: "resourceRateAnalysisUomId",
                                title: "RA UOM",
                                form: resourceForm,
                                editing: isResourceEditing(record),
                                workUomsList,
                              }),
                            },
                            {
                              title: "Rate",
                              dataIndex: "resourceRateAnalysisRate",
                              key: "resRaRate",
                              width: 120,
                              align: "center" as const,
                              onCell: (record: any) => ({
                                record,
                                dataIndex: "resourceRateAnalysisRate",
                                title: "RA Rate",
                                form: resourceForm,
                                editing: isResourceEditing(record),
                              }),
                            },
                          ],
                        },
                      ]
                    : []),
                  ...(() => {
                    const overall = [
                      {
                        title: "Quantity",
                        dataIndex: "resourceQuantity",
                        key: "resourceQuantity",
                        width: 150,
                        align: "center" as const,
                        onCell: (record: any) => ({
                          record,
                          dataIndex: "resourceQuantity",
                          title: "Quantity",
                          form: resourceForm,
                          editing: isResourceEditing(record),
                        }),
                        render: (text: any) => text || "",
                      },
                      {
                        title: "UOM",
                        dataIndex: "workUomId",
                        key: "workUomId",
                        width: 180,
                        align: "center" as const,
                        onCell: (record: any) => ({
                          record,
                          dataIndex: "workUomId",
                          title: "UOM",
                          form: resourceForm,
                          editing: isResourceEditing(record),
                          workUomsList,
                        }),
                        render: (text: any) =>
                          workUomsList.find(
                            (u: SelectOption) => u.value === text,
                          )?.label || "",
                      },
                      {
                        title: "Rate",
                        dataIndex: "resourceRate",
                        key: "resourceRate",
                        width: 150,
                        align: "center" as const,
                        onCell: (record: any) => ({
                          record,
                          dataIndex: "resourceRate",
                          title: "Rate",
                          form: resourceForm,
                          editing: isResourceEditing(record),
                        }),
                        render: (text: any) => text || "",
                      },
                      {
                        title: "Total Amount",
                        dataIndex: "resourceTotalAmount",
                        key: "resourceTotalAmount",
                        width: 150,
                        align: "center" as const,
                        onCell: (record: any) => ({
                          record,
                          dataIndex: "resourceTotalAmount",
                          title: "Total Amount",
                          form: resourceForm,
                          editing: isResourceEditing(record),
                        }),
                      },
                    ];
                    return showRateAnalysis
                      ? [
                          {
                            title: overallLabel,
                            align: "center" as const,
                            onHeaderCell: () => ({
                              style: {
                                background: "#f6ffed",
                                fontWeight: 600,
                              },
                            }),
                            children: overall,
                          },
                        ]
                      : overall;
                  })(),
                ] as any)}
                rowSelection={{
                  selectedRowKeys: selectedResourceKeys,
                  onChange: onResourceSelectChange,
                }}
                components={{
                  body: {
                    cell: ResourceEditableCell,
                  },
                }}
                dataSource={activityResourcesList}
                pagination={false}
                bordered
                size="middle"
                scroll={{ x: 600 }}
              />
            </Form>
          </div>
        </div>

        {/* Tools & Machinery Section */}
        <div
          className="card"
          style={{
            marginBottom: "24px",
            border: "1px solid #e8e8e8",
            borderRadius: "8px",
          }}
        >
          <div className="card-body" style={{ padding: "20px" }}>
            <div className="d-flex align-items-center mb-3">
              <Icon
                name="building"
                style={{
                  fontSize: "20px",
                  color: "#004293",
                  marginRight: "8px",
                }}
              />
              <h6
                className="section-header mb-0"
                style={{ color: "#004293", fontWeight: "600" }}
              >
                Machinery
              </h6>
            </div>
            <div className="row g-3 mt-2 align-items-end">
              <div className="col-3">
                <FormSelect
                  name="toolType"
                  label="Tool Type"
                  placeholder="Select Tool Type"
                  options={toolsAndMachineryTypes}
                  disabled={!isEditMode}
                />
              </div>
              <div className="col-3">
                <FormSelect
                  name="toolMachinery"
                  label="Machinery *"
                  placeholder="Select Machinery"
                  options={
                    selectedToolType
                      ? toolsAndMachineryList.filter(
                          (tool: any) =>
                            tool.extra?.typeId === selectedToolType,
                        )
                      : toolsAndMachineryList
                  }
                  disabled={!isEditMode || !selectedToolType}
                />
              </div>
              <div className="col-3">
                <div className="form-group">
                  <label className="form-label">Serial Number</label>
                  <div className="form-control-wrap">
                    <ToolSerialNumberInput
                      control={methods.control}
                      setValue={methods.setValue}
                      disabled={!isEditMode || !selectedTool}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Rate Analysis + Overall groups side by side */}
            <div className="row g-3 mt-2">
              {showRateAnalysis && (
                <div className="col-6">
                  <div
                    className="p-2 h-100"
                    style={{ background: "#e6f4ff", borderRadius: "6px" }}
                  >
                    <div
                      className="mb-2"
                      style={{ fontWeight: 600, color: "#004293" }}
                    >
                      {rateAnalysisLabel}
                    </div>
                    <div className="row g-2 align-items-end">
                      <div className="col-3">
                        <div className="form-group">
                          <label className="form-label">Qty</label>
                          <div className="form-control-wrap">
                            <input
                              type="number"
                              className="form-control"
                              placeholder="Unit qty"
                              min={0}
                              max={9999999999}
                              onKeyDown={handleNumberKeyDown}
                              step="0.01"
                              disabled={!isEditMode || !selectedTool}
                              style={{ height: "38px" }}
                              {...(() => {
                                const ra = methods.register(
                                  "toolRateAnalysisQty",
                                );
                                return {
                                  ...ra,
                                  onChange: (e: any) => {
                                    ra.onChange(e);
                                    const f = Number(outputFactor) || 0;
                                    const v = Number(e.target.value);
                                    if (f > 0 && !isNaN(v)) {
                                      methods.setValue(
                                        "toolQuantity",
                                        String(Number((f * v).toFixed(2))),
                                      );
                                    }
                                  },
                                };
                              })()}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-3">
                        <FormSelect
                          name="toolRateAnalysisUomId"
                          label="UOM"
                          placeholder="UOM"
                          options={workUomsList}
                          disabled={!isEditMode || !selectedTool}
                        />
                      </div>
                      <div className="col-3">
                        <div className="form-group">
                          <label className="form-label">Rate</label>
                          <div className="input-group">
                            <span className="input-group-text">₹</span>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="Unit rate"
                              min={0}
                              max={9999999999}
                              onKeyDown={handleNumberKeyDown}
                              step="0.01"
                              disabled={!isEditMode || !selectedTool}
                              style={{ height: "38px" }}
                              {...(() => {
                                const ra = methods.register(
                                  "toolRateAnalysisRate",
                                );
                                return {
                                  ...ra,
                                  onChange: (e: any) => {
                                    ra.onChange(e);
                                    methods.setValue(
                                      "toolRate",
                                      e.target.value,
                                    );
                                  },
                                };
                              })()}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-3">
                        <div className="form-group">
                          <label className="form-label">Total</label>
                          <div className="form-control-wrap">
                            <ToolRateAnalysisTotalPreview
                              control={methods.control}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className={showRateAnalysis ? "col-6" : "col-12"}>
                <div
                  className="p-2 h-100"
                  style={{ background: "#f6ffed", borderRadius: "6px" }}
                >
                  <div
                    className="mb-2"
                    style={{ fontWeight: 600, color: "#004293" }}
                  >
                    {overallLabel}
                  </div>
                  <div className="row g-2 align-items-end">
                    <div className="col-3">
                      <div className="form-group">
                        <label className="form-label">
                          Qty <span style={{ color: "#ff4d4f" }}>*</span>
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Qty"
                            min={0}
                            max={9999999999}
                            onKeyDown={handleNumberKeyDown}
                            step="0.01"
                            disabled={!isEditMode || !selectedTool}
                            style={{ height: "38px" }}
                            {...methods.register("toolQuantity")}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-3">
                      <FormSelect
                        name="toolWorkUomId"
                        label="UOM *"
                        placeholder="UOM"
                        options={workUomsList}
                        disabled={!isEditMode || !selectedTool}
                      />
                    </div>
                    <div className="col-3">
                      <div className="form-group">
                        <label className="form-label">
                          Rate <span style={{ color: "#ff4d4f" }}>*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text">₹</span>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Rate"
                            min={0}
                            max={9999999999}
                            onKeyDown={handleNumberKeyDown}
                            step="0.01"
                            disabled={!isEditMode || !selectedTool}
                            style={{ height: "38px" }}
                            {...methods.register("toolRate")}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-3">
                      <div className="form-group">
                        <label className="form-label">Total</label>
                        <div className="form-control-wrap">
                          <ToolTotalPreview control={methods.control} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-3">
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  const formToolSerialNumber =
                    methods.getValues("toolSerialNumber");
                  const formToolRate = methods.getValues("toolRate");
                  const formToolQuantity = methods.getValues("toolQuantity");
                  const formToolWorkUomId = methods.getValues("toolWorkUomId");
                  const formToolRaQty = methods.getValues(
                    "toolRateAnalysisQty",
                  );
                  const formToolRaRate = methods.getValues(
                    "toolRateAnalysisRate",
                  );
                  const formToolRaUomId = methods.getValues(
                    "toolRateAnalysisUomId",
                  );
                  // Mandatory fields (also required by backend).
                  if (!selectedTool) {
                    messageApi.error("Please select a Tool / Machinery");
                    return;
                  }
                  if (
                    formToolQuantity === undefined ||
                    formToolQuantity === null ||
                    formToolQuantity === ""
                  ) {
                    messageApi.error("Please enter Quantity");
                    return;
                  }
                  if (
                    formToolWorkUomId === undefined ||
                    formToolWorkUomId === null
                  ) {
                    messageApi.error("Please select UOM");
                    return;
                  }
                  if (
                    formToolRate === undefined ||
                    formToolRate === null ||
                    formToolRate === ""
                  ) {
                    messageApi.error("Please enter Rate");
                    return;
                  }
                  const toolData = toolsAndMachineryList.find(
                    (t: any) => t.value === selectedTool,
                  );
                  const typeData = toolsAndMachineryTypes.find(
                    (t: any) => t.value === selectedToolType,
                  );

                  // Duplicate check (ignore the row being edited).
                  if (
                    taskTools.some(
                      (t, i) =>
                        t.toolId === selectedTool && i !== editingToolIndex,
                    )
                  ) {
                    messageApi.error("Tool already added");
                    return;
                  }

                  const builtTool: any = {
                    // Preserve the existing taskToolId + split-materials when
                    // editing so the update diff treats it as an update.
                    ...(editingToolIndex !== null
                      ? {
                          taskToolId: taskTools[editingToolIndex]?.taskToolId,
                          toolMaterials:
                            taskTools[editingToolIndex]?.toolMaterials || [],
                        }
                      : { toolMaterials: [] }),
                    toolId: selectedTool,
                    toolName: toolData?.label,
                    typeName: typeData?.label,
                    ...(formToolSerialNumber
                      ? { serialNumber: formToolSerialNumber }
                      : {}),
                    ...(formToolRate !== undefined &&
                    formToolRate !== null &&
                    formToolRate !== ""
                      ? { rate: Number(formToolRate) }
                      : {}),
                    ...(formToolQuantity !== undefined &&
                    formToolQuantity !== null &&
                    formToolQuantity !== ""
                      ? { quantity: Number(formToolQuantity) }
                      : {}),
                    ...(formToolWorkUomId !== undefined &&
                    formToolWorkUomId !== null
                      ? { workUomId: Number(formToolWorkUomId) }
                      : {}),
                    ...(formToolRaQty !== undefined &&
                    formToolRaQty !== null &&
                    formToolRaQty !== ""
                      ? { rateAnalysisQty: Number(formToolRaQty) }
                      : {}),
                    ...(formToolRaUomId !== undefined &&
                    formToolRaUomId !== null
                      ? { rateAnalysisUomId: Number(formToolRaUomId) }
                      : {}),
                    ...(formToolRaRate !== undefined &&
                    formToolRaRate !== null &&
                    formToolRaRate !== ""
                      ? { rateAnalysisRate: Number(formToolRaRate) }
                      : {}),
                  };

                  if (editingToolIndex !== null) {
                    setTaskTools((prev) =>
                      prev.map((t, i) =>
                        i === editingToolIndex ? builtTool : t,
                      ),
                    );
                  } else {
                    setTaskTools([...taskTools, builtTool]);
                  }

                  setEditingToolIndex(null);
                  setSelectedTool(null);
                  setSelectedToolType(null);
                  methods.setValue("toolType", null);
                  methods.setValue("toolMachinery", null);
                  methods.setValue("toolSerialNumber", "");
                  methods.setValue("toolRate", "");
                  methods.setValue("toolQuantity", "");
                  methods.setValue("toolWorkUomId", null);
                  methods.setValue("toolRateAnalysisQty", "");
                  methods.setValue("toolRateAnalysisRate", "");
                  methods.setValue("toolRateAnalysisUomId", null);
                }}
                disabled={!isEditMode}
              >
                {editingToolIndex !== null ? "Update Tool" : "Add Tool"}
              </Button>
              {editingToolIndex !== null && (
                <Button
                  size="small"
                  className="ms-2"
                  onClick={() => {
                    setEditingToolIndex(null);
                    setSelectedTool(null);
                    setSelectedToolType(null);
                    methods.setValue("toolType", null);
                    methods.setValue("toolMachinery", null);
                    methods.setValue("toolSerialNumber", "");
                    methods.setValue("toolRate", "");
                    methods.setValue("toolQuantity", "");
                    methods.setValue("toolWorkUomId", null);
                    methods.setValue("toolRateAnalysisQty", "");
                    methods.setValue("toolRateAnalysisRate", "");
                    methods.setValue("toolRateAnalysisUomId", null);
                  }}
                  disabled={!isEditMode}
                >
                  Cancel
                </Button>
              )}
            </div>

            {/* Display added tools (read-only; edit via the add-row above) */}
            {taskTools.length > 0 && (
              <div className="mt-3">
                <Table
                  dataSource={taskTools}
                  rowKey="toolId"
                  pagination={
                    taskTools.length > 10
                      ? { pageSize: 10, size: "small" }
                      : false
                  }
                  size="small"
                  bordered
                  scroll={{ x: "max-content" }}
                  columns={([
                    {
                      title: "Tool Type",
                      dataIndex: "typeName",
                      key: "typeName",
                      width: 300,
                      align: "center" as const,
                    },
                    {
                      title: "Machinery",
                      dataIndex: "toolName",
                      key: "toolName",
                      width: 350,
                      align: "center" as const,
                    },
                    {
                      title: "Serial Number",
                      dataIndex: "serialNumber",
                      key: "serialNumber",
                      width: 150,
                      align: "center" as const,
                      render: (text: any) => text ?? "",
                    },
                    ...(showRateAnalysis
                      ? [
                          {
                            title: rateAnalysisLabel,
                            align: "center" as const,
                            onHeaderCell: () => ({
                              style: {
                                background: "#e6f4ff",
                                textAlign: "center" as const,
                                fontWeight: 600,
                              },
                            }),
                            children: [
                              {
                                title: "Qty",
                                dataIndex: "rateAnalysisQty",
                                key: "rateAnalysisQty",
                                width: 110,
                                align: "center" as const,
                                render: (text: any) => text ?? "",
                              },
                              {
                                title: "UOM",
                                dataIndex: "rateAnalysisUomId",
                                key: "rateAnalysisUomId",
                                width: 130,
                                align: "center" as const,
                                render: (text: any) =>
                                  workUomsList.find(
                                    (u: SelectOption) => u.value === text,
                                  )?.label || "",
                              },
                              {
                                title: "Rate",
                                dataIndex: "rateAnalysisRate",
                                key: "rateAnalysisRate",
                                width: 110,
                                align: "center" as const,
                                render: (text: any) => text ?? "",
                              },
                              {
                                title: "Total Amount",
                                key: "raToolTotalAmount",
                                width: 130,
                                align: "center" as const,
                                render: (_: any, record: any) => {
                                  const total =
                                    (Number(record.rateAnalysisRate) || 0) *
                                    (Number(record.rateAnalysisQty) || 0);
                                  return total ? total.toFixed(2) : "";
                                },
                              },
                            ],
                          },
                        ]
                      : []),
                    {
                      title: overallLabel,
                      align: "center" as const,
                      onHeaderCell: () => ({
                        style: {
                          background: "#f6ffed",
                          textAlign: "center" as const,
                          fontWeight: 600,
                        },
                      }),
                      children: [
                        {
                          title: "Qty",
                          dataIndex: "quantity",
                          key: "quantity",
                          width: 110,
                          align: "center" as const,
                          render: (text: any) => text ?? "",
                        },
                        {
                          title: "UOM",
                          dataIndex: "workUomId",
                          key: "workUomId",
                          width: 130,
                          align: "center" as const,
                          render: (text: any) =>
                            workUomsList.find(
                              (u: SelectOption) => u.value === text,
                            )?.label || "",
                        },
                        {
                          title: "Rate",
                          dataIndex: "rate",
                          key: "rate",
                          width: 110,
                          align: "center" as const,
                          render: (text: any) => text ?? "",
                        },
                        {
                          title: "Total Amount",
                          key: "toolTotalAmount",
                          width: 130,
                          align: "center" as const,
                          render: (_: any, record: any) => {
                            const total =
                              (Number(record.rate) || 0) *
                              (Number(record.quantity) || 0) *
                              (Number(record.count) || 1);
                            return total ? total.toFixed(2) : "";
                          },
                        },
                      ],
                    },
                    {
                      title: "Materials",
                      key: "materials",
                      width: 170,
                      align: "center" as const,
                      render: (_: any, record: any, index: number) => {
                        const mats = record.toolMaterials || [];
                        const matTotal = mats.reduce(
                          (s: number, m: ToolMaterialItem) =>
                            s +
                            (Number(m.quantity) || 0) * (Number(m.rate) || 0),
                          0,
                        );
                        if (!isEditMode && !mats.length) return "-";
                        return (
                          <Button
                            type={mats.length ? "primary" : "default"}
                            size="small"
                            onClick={() =>
                              setMaterialsDrawer({ open: true, toolIndex: index })
                            }
                          >
                            <em className="icon ni ni-list" />
                            {mats.length
                              ? ` ${mats.length} · ₹${matTotal.toFixed(2)}`
                              : " Add"}
                          </Button>
                        );
                      },
                    },
                    {
                      title: "Action",
                      key: "action",
                      width: 110,
                      align: "center" as const,
                      render: (_: any, record: any, index: number) => (
                        <div
                          style={{
                            display: "flex",
                            gap: "12px",
                            justifyContent: "center",
                          }}
                        >
                          <em
                            className="icon ni ni-edit"
                            title="Edit tool"
                            onClick={() => {
                              if (!isEditMode) return;
                              const typeValue =
                                toolsAndMachineryTypes.find(
                                  (t: any) => t.label === record.typeName,
                                )?.value ?? null;
                              setSelectedToolType(typeValue);
                              setSelectedTool(record.toolId);
                              methods.setValue("toolType", typeValue);
                              methods.setValue("toolMachinery", record.toolId);
                              methods.setValue(
                                "toolSerialNumber",
                                record.serialNumber ?? "",
                              );
                              methods.setValue(
                                "toolQuantity",
                                record.quantity != null
                                  ? String(record.quantity)
                                  : "",
                              );
                              methods.setValue(
                                "toolWorkUomId",
                                record.workUomId ?? null,
                              );
                              methods.setValue(
                                "toolRate",
                                record.rate != null ? String(record.rate) : "",
                              );
                              methods.setValue(
                                "toolRateAnalysisQty",
                                record.rateAnalysisQty != null
                                  ? String(record.rateAnalysisQty)
                                  : "",
                              );
                              methods.setValue(
                                "toolRateAnalysisUomId",
                                record.rateAnalysisUomId ?? null,
                              );
                              methods.setValue(
                                "toolRateAnalysisRate",
                                record.rateAnalysisRate != null
                                  ? String(record.rateAnalysisRate)
                                  : "",
                              );
                              setEditingToolIndex(index);
                            }}
                            style={{
                              cursor: isEditMode ? "pointer" : "not-allowed",
                              fontSize: "16px",
                              color: "#004293",
                            }}
                          ></em>
                          <em
                            className="icon ni ni-trash"
                            title="Remove tool"
                            onClick={() => {
                              if (!isEditMode) return;
                              setTaskTools(
                                taskTools.filter((_t, i) => i !== index),
                              );
                              if (editingToolIndex === index) {
                                setEditingToolIndex(null);
                              }
                            }}
                            style={{
                              cursor: isEditMode ? "pointer" : "not-allowed",
                              fontSize: "16px",
                              color: "#ff4d4f",
                            }}
                          ></em>
                        </div>
                      ),
                    },
                  ] as any)}
                />
                <div
                  className="d-flex justify-content-end mt-2"
                  style={{ fontWeight: 600, color: "#004293" }}
                >
                  Machinery Total: ₹
                  {taskTools
                    .reduce(
                      (s, t) =>
                        s +
                        (Number(t.rate) || 0) *
                          (Number(t.quantity) || 0) *
                          (Number(t.count) || 1),
                      0,
                    )
                    .toFixed(2)}
                </div>
              </div>
            )}

            {/* Per-tool split-materials drawer (reuses the create-form manager) */}
            <Drawer
              title={
                materialsDrawer.toolIndex != null
                  ? `Split Materials — ${
                      taskTools[materialsDrawer.toolIndex]?.toolName || "Tool"
                    }`
                  : "Split Materials"
              }
              placement="right"
              width={Math.min(
                980,
                typeof window !== "undefined" ? window.innerWidth - 80 : 980,
              )}
              open={materialsDrawer.open}
              onClose={() =>
                setMaterialsDrawer({ open: false, toolIndex: null })
              }
              destroyOnClose
            >
              {materialsDrawer.toolIndex != null &&
                taskTools[materialsDrawer.toolIndex] && (
                  <ToolMaterialsManager
                    materials={
                      taskTools[materialsDrawer.toolIndex].toolMaterials || []
                    }
                    toolOverallTotal={
                      (Number(taskTools[materialsDrawer.toolIndex].rate) || 0) *
                      (Number(taskTools[materialsDrawer.toolIndex].quantity) ||
                        0) *
                      (Number(taskTools[materialsDrawer.toolIndex].count) || 1)
                    }
                    materialOptions={materialOptions}
                    workUomsList={workUomsList}
                    showRateAnalysis={showRateAnalysis}
                    outputFactor={outputFactor}
                    disabled={!isEditMode}
                    rateAnalysisLabel={rateAnalysisLabel}
                    overallLabel={overallLabel}
                    onChange={(updatedMaterials) => {
                      const idx = materialsDrawer.toolIndex as number;
                      setTaskTools((prev) =>
                        prev.map((t, i) =>
                          i === idx
                            ? { ...t, toolMaterials: updatedMaterials }
                            : t,
                        ),
                      );
                    }}
                  />
                )}
            </Drawer>
          </div>
        </div>

        {/* Cost Summary (Materials + Resources + Tools) */}
        <div
          className="card"
          style={{
            marginBottom: "24px",
            border: "1px solid #004293",
            borderRadius: "8px",
          }}
        >
          <div className="card-body" style={{ padding: "20px" }}>
            <div className="d-flex align-items-center mb-3">
              <Icon
                name="coin"
                style={{
                  fontSize: "20px",
                  color: "#004293",
                  marginRight: "8px",
                }}
              />
              <h6
                className="section-header mb-0"
                style={{ color: "#004293", fontWeight: "600" }}
              >
                Cost Summary
              </h6>
            </div>
            <div className="row">
              {showRateAnalysis && (
                <div className="col-md-6">
                  <div
                    className="p-3 h-100"
                    style={{ background: "#e6f4ff", borderRadius: "6px" }}
                  >
                    <div
                      className="mb-2"
                      style={{ fontWeight: 600, color: "#004293" }}
                    >
                      {rateAnalysisLabel}
                    </div>
                    <SectionTotal label="Materials" amount={boqRaTotal} />
                    <SectionTotal label="Manpower" amount={resRaTotal} />
                    <SectionTotal
                      label="Machinery"
                      amount={toolsRaTotal}
                    />
                    <hr className="my-2" />
                    <SectionTotal
                      label="Total (per unit basis)"
                      amount={boqRaTotal + resRaTotal + toolsRaTotal}
                    />
                  </div>
                </div>
              )}
              <div className={showRateAnalysis ? "col-md-6" : "col-12"}>
                <div
                  className="p-3 h-100"
                  style={{ background: "#f6ffed", borderRadius: "6px" }}
                >
                  <div
                    className="mb-2"
                    style={{ fontWeight: 600, color: "#004293" }}
                  >
                    {overallLabel}
                  </div>
                  <SectionTotal label="Materials" amount={boqOverallTotal} />
                  <SectionTotal label="Manpower" amount={resOverallTotal} />
                  <SectionTotal
                    label="Machinery"
                    amount={toolsOverallTotal}
                  />
                  <hr className="my-2" />
                  <SectionTotal
                    label="Overall Total"
                    amount={
                      boqOverallTotal + resOverallTotal + toolsOverallTotal
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ApprovalTimelineContent = () => {
    const approvalsData = selectedTaskDetails?.stateApprovalHierarchy;
    if (!approvalsData || approvalsData?.length === 0) {
      return <Badge color="outline-danger">No</Badge>;
    }

    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.map((approval: any, index: number) => {
              const isApproved = approval.approvalStatus === "Approved";
              const isRejected = approval.approvalStatus === "Rejected";
              return (
                <Timeline.Item
                  key={index}
                  dot={
                    isApproved ? (
                      <CheckCircleOutlined style={{ color: "green" }} />
                    ) : isRejected ? (
                      <CloseCircleOutlined style={{ color: "red" }} />
                    ) : undefined
                  }
                >
                  {approval?.userName}{" "}
                  {approval?.reason && (
                    <Icon
                      name="info"
                      className="ms-1"
                      title={`Reason : ${approval?.reason}`}
                    />
                  )}
                  <div className="designation">{approval?.roleName}</div>
                  {approval?.approvalStage && (
                    <div className="designation">{approval?.approvalStage}</div>
                  )}
                  {approval?.level && (
                    <div className="designation">Level: {approval?.level}</div>
                  )}
                  {approval?.approvalStatus && (
                    <div className="designation">
                      Status: {approval?.approvalStatus}
                    </div>
                  )}
                  {approval?.actionDate && (
                    <div className="designation">
                      {dayjs(approval?.actionDate).format("DD-MMM-YYYY")}
                    </div>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2557d6" }}>Approval Hierarchy</span>}
      >
        <span
          style={{
            color: "#2557d6",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          Yes
        </span>
      </Popover>
    );
  };

  // Reject Modal Component (similar to WorkApprovalListView)
  const RejectModal = ({
    isModalVisible,
    setIsModalVisible,
    onReject,
    title = "Reject Item",
    label = "Reason for rejection",
    isLoading = false,
  }: {
    isModalVisible: boolean;
    setIsModalVisible: (visible: boolean) => void;
    onReject: (reason: string) => void;
    title?: string;
    label?: string;
    isLoading?: boolean;
  }) => {
    const [form] = Form.useForm();

    const handleCancel = () => {
      setIsModalVisible(false);
    };

    const handleOk = () => {
      form
        .validateFields()
        .then((values) => {
          const reason = values.reason;
          onReject(reason);
          form.resetFields();
          setIsModalVisible(false);
        })
        .catch((info) => {
          // Validation failed
        });
    };

    return (
      <Modal
        title={title}
        open={isModalVisible}
        footer={null}
        onCancel={handleCancel}
      >
        <Form form={form} layout="vertical" name="rejectForm">
          <Form.Item
            name="reason"
            label={label}
            rules={[{ required: true, message: "Please input your reason!" }]}
          >
            <Input.TextArea placeholder="Type your reason here..." />
          </Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              onClick={handleOk}
              disabled={isLoading}
            >
              {isLoading ? "Submitting..." : "Submit"}
            </Button>
            <Button onClick={handleCancel} disabled={isLoading}>
              Cancel
            </Button>
          </Space>
        </Form>
      </Modal>
    );
  };

  const approvalData = selectedTaskDetails;

  // Approval/Reject handlers
  const handleApproveReject = async (
    action: "approve" | "reject",
    reason?: string,
  ) => {
    if (!approvalData) return;
    setIsProcessing(true);
    try {
      // Base request data without reason
      const baseRequestData = {
        projectId: approvalData.projectId,
        data: [
          {
            approvalLinkId: approvalData.approvalLinkId,
            parentModule: "Task",
          },
        ],
      };
      let responce: any;
      // For approve action
      if (action === "approve") {
        responce = await workApprovalApis.approveWorkItem(baseRequestData);
      }
      // For reject action
      else {
        if (!reason) {
          throw new Error("Reason is required for rejection");
        }

        responce = await workApprovalApis.rejectWorkItem({
          ...baseRequestData,
          reason: reason, // reason is only added for reject
        });
      }
      const { status, message } = responce;
      if (status) {
        const successMessage =
          responce?.data?.message ||
          message ||
          `Successfully ${action === "approve" ? "approved" : "rejected"} task`;
        // Success notification
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: successMessage,
          confirmButtonText: "OK",
          customClass: {
            container: "border-class",
          },
        });

        // Refresh task data instead of closing
        await fetchTaskById();
        safeRefreshData();
      }
      if (!status) {
        const errorMessage =
          responce?.data?.message ||
          responce?.response?.data?.message ||
          message ||
          `Failed to ${action} item. Please try again.`;
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMessage,
          confirmButtonText: "OK",
        });
      }
    } catch (error: any) {
      console.error(`Error ${action}ing item:`, error);

      await Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          error.message || // This will show our "Reason is required" message
          `Failed to ${action} item. Please try again.`,
        confirmButtonText: "OK",
        customClass: {
          container: "border-class",
        },
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Check if current user is an approver
  const isApprover =
    approvalData?.approvalLinkId && // Check if approval link exists at task level
    approvalData?.stateApprovals?.some(
      (approval: any) =>
        approval.userId === currentUser?.id && approval.status === "Pending",
    );
  // Responsive label/value layout helpers - card style layout
  const responsiveLabelValueStyle = {
    ...labelValueStyle,
    flexDirection: "column" as const,
    alignItems: "stretch" as const,
    gap: "4px",
  };
  const responsiveLabelStyle = {
    ...labelStyle,
    width: "100%",
    margin: 0,
    paddingTop: "0px",
    minHeight: "auto",
  } as const;
  const responsiveValueStyle = {
    ...valueStyle,
    width: "100%",
    paddingTop: "0px",
    minHeight: "auto",
  } as const;

  return (
    <>
      <OwnerChangeReason
        open={ownerChangeModalOpen?.open}
        setOpen={(open: boolean) => {
          setOwnerChangeModalOpen({
            open: open,
            extraData: { ...ownerChangeModalOpen?.extraData },
          });
        }}
        onSubmit={(data: any) => {
          setLoading(true);
          updateTaskDetails({
            ...ownerChangeModalOpen?.extraData,
            reassignedReason: data,
          });
        }}
      />

      <div
        className="task-information-container"
        style={{
          position: "relative",
          maxWidth: "100%",
          fontFamily: '"Roboto", sans-serif',
        }}
      >
        <div
          style={{
            position: "relative",
            maxWidth: "100%",
            fontFamily: '"Roboto", sans-serif',
          }}
        >
          <div
            style={
              hideInternalTabs
                ? {
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: "8px",
                    flexWrap: "wrap",
                    padding: "10px 16px",
                    marginBottom: 12,
                    background: "#fafafa",
                    borderBottom: "1px solid #f0f0f0",
                  }
                : {
                    position: "absolute",
                    top: 15,
                    right: 20,
                    zIndex: 100,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }
            }
          >
            {isEditMode ? (
              <>
                {onBackToSummary && (
                  <Button
                    size="small"
                    onClick={onBackToSummary}
                    disabled={isFromWorkUI}
                  >
                    <Icon name="eye" /> <span>Back to Summary</span>
                  </Button>
                )}
                <Button
                  type="primary"
                  htmlType="submit"
                  size="small"
                  onClick={() => {
                    // Mark all relevant fields as dirty before submitting
                    markAllRelevantFieldsAsDirty();
                    handleSubmit(updateTask)();
                  }}
                  disabled={
                    disableSubmit || isFromWorkUI || !hasUnsavedChanges()
                  }
                >
                  <Icon name="save" /> <span>Save</span>
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    Swal.fire({
                      title: "Are you sure?",
                      text: "You won't be able to revert this!",
                      icon: "warning",
                      showCancelButton: true,
                      confirmButtonColor: "#3085d6",
                      cancelButtonColor: "#d33",
                      confirmButtonText: "Yes, cancel it!",
                    }).then((result) => {
                      if (result.isConfirmed) {
                        Swal.fire({
                          title: "Cancelled",
                          icon: "success",
                          text: "Your changes have been cancelled.",
                        });
                        setIsEditMode(false);
                        setDisableSubmit(false);
                        setSelectedResourceKeys([]);
                        setEditingResourceKeys([]);
                        setTaskTools([]); // Reset task tools
                        setSelectedToolType(null); // Reset tool selection
                        setSelectedTool(null); // Reset tool selection
                        setHyperlinks([]); // Reset hyperlinks
                        setCurrentHyperlinkName(""); // Reset current hyperlink name
                        setCurrentHyperlinkUrl(""); // Reset current hyperlink URL
                        // Reset approval hierarchy
                        setIsApprovalRequired(
                          selectedTaskDetails?.approvalRequired || false,
                        );
                        setAutoApprovalRemarks(
                          selectedTaskDetails?.autoApprovalRemarks || "",
                        );
                        if (
                          selectedTaskDetails?.stateApprovalHierarchy &&
                          selectedTaskDetails.stateApprovalHierarchy.length > 0
                        ) {
                          const approvalData =
                            selectedTaskDetails.stateApprovalHierarchy.map(
                              (approval: any) => ({
                                roleId: approval.roleId,
                                level: approval.level,
                                userId: approval.userId,
                                approvalStage: approval.approvalStage,
                                stateApprovalHierarchyId:
                                  approval.stateApprovalHierarchyId,
                              }),
                            );
                          setApprovals({
                            open: false,
                            data: approvalData,
                          });
                        } else {
                          setApprovals({
                            open: false,
                            data: [],
                          });
                        }
                        resourceForm.resetFields();
                        reset();
                        // When the component was mounted directly into edit
                        // mode from the new TaskQuickViewCard summary,
                        // toggling isEditMode alone leaves the user on the
                        // legacy in-form read-only view. Bounce them back to
                        // the summary instead.
                        if (onBackToSummary) {
                          onBackToSummary();
                        }
                      }
                    });
                  }}
                  disabled={isFromWorkUI}
                >
                  <span>Cancel</span>
                </Button>
              </>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  {/* Task Completion Percentage */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 500,
                      }}
                    >
                      TASK COMPLETION :
                    </span>
                    <span
                      style={{
                        color: (() => {
                          const value = isApprover
                            ? (selectedTaskDetails?.updates
                                ?.taskCompletionPercentage ??
                              selectedTaskDetails?.taskCompletionPercentage)
                            : selectedTaskDetails?.taskCompletionPercentage;

                          if (value === null || value === undefined)
                            return "#2557d6";
                          const roundedValue = Math.round(value);
                          if (roundedValue >= 75) return "#38d39f"; // Green
                          if (roundedValue >= 50) return "#f4bd0e"; // Yellow
                          if (roundedValue >= 25) return "#f98c3c"; // Orange
                          return "#e85347"; // Red
                        })(),
                        fontWeight: 600,
                      }}
                    >
                      {(() => {
                        const value = isApprover
                          ? (selectedTaskDetails?.updates
                              ?.taskCompletionPercentage ??
                            selectedTaskDetails?.taskCompletionPercentage)
                          : selectedTaskDetails?.taskCompletionPercentage;

                        if (value === null || value === undefined) return "0%";
                        return `${Math.round(value)}%`;
                      })()}
                    </span>
                    {selectedTaskDetails?.updates?.taskCompletionPercentage !==
                      undefined && (
                      <Popover
                        content={
                          <div style={{ padding: "8px" }}>
                            <div>
                              {isApprover
                                ? "Previous percentage"
                                : "Requested percentage"}
                              :{" "}
                              <span
                                style={{
                                  color: (() => {
                                    const value = isApprover
                                      ? selectedTaskDetails?.taskCompletionPercentage
                                      : selectedTaskDetails?.updates
                                          ?.taskCompletionPercentage;

                                    if (value === null || value === undefined)
                                      return "#2557d6";
                                    const roundedValue = Math.round(value);
                                    if (roundedValue >= 75) return "#38d39f"; // Green
                                    if (roundedValue >= 50) return "#f4bd0e"; // Yellow
                                    if (roundedValue >= 25) return "#f98c3c"; // Orange
                                    return "#e85347"; // Red
                                  })(),
                                  fontWeight: 600,
                                }}
                              >
                                {(() => {
                                  const value = isApprover
                                    ? selectedTaskDetails?.taskCompletionPercentage
                                    : selectedTaskDetails?.updates
                                        ?.taskCompletionPercentage;

                                  if (value === null || value === undefined)
                                    return "N/A";
                                  return `${Math.round(value)}%`;
                                })()}
                              </span>
                            </div>
                          </div>
                        }
                        trigger="hover"
                      >
                        <Icon
                          name="info"
                          style={{
                            cursor: "pointer",
                            marginLeft: "2px",
                            color: "#2557d6",
                          }}
                        />
                      </Popover>
                    )}
                  </div>

                  {/* Current Status */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 500,
                      }}
                    >
                      CURRENT STATUS :
                    </span>
                    <Text
                      style={{
                        color: "#2557d6",
                        fontWeight: 500,
                      }}
                    >
                      {isApprover
                        ? selectedTaskDetails?.updates?.taskStatus ||
                          selectedTaskDetails?.taskStatus ||
                          "N/A"
                        : selectedTaskDetails?.taskStatus || "N/A"}
                    </Text>
                  </div>
                  {/* Show status dropdown if user has module edit permission OR is owner/admin/hierarchy, and base conditions are met */}
                  {(() => {
                    // Recalculate baseConditions from task detail data
                    const projectStatus =
                      selectedTaskDetails?.projectStatus ||
                      projectResponse?.status ||
                      "";
                    const phaseStatus =
                      selectedTaskDetails?.phaseStatus ||
                      phaseResponse?.status ||
                      "";
                    const activityStatus =
                      selectedTaskDetails?.activityStatus || "";
                    const isLocked =
                      (projectResponse as any)?.isLocked || false;
                    const lockedBy = (projectResponse as any)?.lockedBy;

                    const baseConditionsMet =
                      !["onhold", "cancelled", "archived", "closed"].includes(
                        projectStatus?.toLowerCase(),
                      ) &&
                      !["onhold", "cancelled", "archived", "closed"].includes(
                        phaseStatus?.toLowerCase(),
                      ) &&
                      ![
                        "onhold",
                        "cancelled",
                        "archived",
                        "closed",
                        "deleted",
                      ].includes(activityStatus?.toLowerCase()) &&
                      (isLocked === false || lockedBy == currentUser?.id);
                    const isInHierarchy =
                      selectedTaskDetails?.hierarchyUserIds &&
                      Array.isArray(selectedTaskDetails.hierarchyUserIds) &&
                      currentUser?.id &&
                      selectedTaskDetails.hierarchyUserIds
                        .filter((id: number) => id > 0)
                        .includes(currentUser?.id);
                    const isAdmin =
                      currentUser?.genericRole?.toLowerCase() === "admin";
                    const isTaskOwner =
                      selectedTaskDetails?.taskOwnerId === currentUser?.id;

                    // Check module-level edit permission
                    const hasModulePermission =
                      hasModuleEditPermission("Tasks");

                    // Show status dropdown if:
                    // - Owner: Module permission AND owner check
                    // - Admin: Module permission only (hierarchy not needed)
                    // - Others: Module permission AND hierarchy (both required)
                    const statusChangeAllowed = ![
                      "closed",
                      "archived",
                    ].includes(
                      (selectedTaskDetails?.taskStatus || "").toLowerCase(),
                    );

                    const canChangeStatus =
                      !isFromWorkUI &&
                      statusChangeAllowed &&
                      baseConditionsMet &&
                      ((isTaskOwner && hasModulePermission) ||
                        (isAdmin && hasModulePermission) ||
                        (hasModulePermission && isInHierarchy));

                    return (
                      canChangeStatus &&
                      taskStatusList &&
                      taskStatusList.length > 0 && (
                        <EditAccess moduleName="Tasks">
                          <Dropdown
                            menu={{
                              items: taskStatusList,
                              selectedKeys: [selectedTaskDetails?.taskStatus],
                              onClick: handleStatusChange,
                            }}
                            disabled={isFromWorkUI}
                            trigger={["hover", "click"]}
                            placement="bottomLeft"
                            overlayStyle={{ zIndex: 1050 }}
                          >
                            <a
                              onClick={(e) => e.preventDefault()}
                              style={{
                                cursor: "pointer",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              <Space>
                                <DownOutlined style={{ color: "#2557d6" }} />
                              </Space>
                            </a>
                          </Dropdown>
                        </EditAccess>
                      )
                    );
                  })()}

                  {
                    // isApprover &&
                    selectedTaskDetails?.updates?.taskStatus && (
                      <Popover
                        content={
                          <div style={{ padding: "8px" }}>
                            <div>
                              {isApprover
                                ? "Previous status"
                                : "Requested status"}
                              :{" "}
                              {isApprover
                                ? selectedTaskDetails?.taskStatus
                                : selectedTaskDetails?.updates?.taskStatus ||
                                  "N/A"}
                            </div>
                          </div>
                        }
                        trigger="hover"
                      >
                        <Icon
                          name="info"
                          style={{
                            cursor: "pointer",
                            marginLeft: "2px",
                            color: "#2557d6",
                          }}
                        />
                      </Popover>
                    )
                  }

                  {/* Approval Timeline */}
                  {selectedTaskDetails?.stateApprovalHierarchy?.some(
                    (approval: any) => approval.status === "Rejected",
                  ) &&
                    !isApprover && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 500,
                          }}
                        >
                          APPROVAL STATUS :
                        </span>
                        <Text
                          style={{
                            color: "#a94442",
                            fontWeight: 500,
                          }}
                        >
                          Rejected
                        </Text>
                        <Popover
                          content={
                            <div style={{ padding: "8px" }}>
                              {selectedTaskDetails?.stateApprovalHierarchy
                                ?.filter(
                                  (approval: any) =>
                                    approval.status === "Rejected",
                                )
                                ?.slice(0, 1) // Get the most recent rejected approval
                                ?.map((approval: any, index: number) => (
                                  <div key={index}>
                                    <div>Status: {approval.status}</div>
                                    {approval.reason && (
                                      <div>Reason: {approval.reason}</div>
                                    )}
                                    <div>By: {approval.userName}</div>
                                    <div>Role: {approval.roleName}</div>
                                    {approval.actionDate && (
                                      <div>
                                        {dayjs(approval.actionDate).format(
                                          "DD-MMM-YYYY",
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                            </div>
                          }
                          trigger="hover"
                        >
                          <Icon
                            name="info"
                            style={{
                              cursor: "pointer",
                              marginLeft: "4px",
                              color: "#a94442",
                            }}
                            aria-label="Approval status details"
                          />
                        </Popover>
                      </div>
                    )}
                </div>

                {isApprover && (
                  <div
                    style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
                  >
                    {/* <ApproveAccess moduleName="Tasks"> */}
                    <Button
                      style={{
                        backgroundColor: "#d1f5ec",
                        color: "#107154",
                        borderColor: "#d1f5ec",
                        fontWeight: 500,
                      }}
                      onClick={() => {
                        handleApproveReject("approve");
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#38d39f";
                        e.currentTarget.style.color = "#fff";
                        e.currentTarget.style.borderColor = "#38d39f";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#d1f5ec";
                        e.currentTarget.style.color = "#107154";
                        e.currentTarget.style.borderColor = "#d1f5ec";
                      }}
                      loading={isProcessing}
                      size="middle"
                    >
                      Approve
                    </Button>
                    {/* </ApproveAccess> */}
                    <CancelAccess moduleName="Tasks">
                      <Button
                        style={{
                          backgroundColor: "#fbe3e4",
                          color: "#a94442",
                          borderColor: "#fbe3e4",
                          fontWeight: 500,
                        }}
                        onClick={() => setRejectModalVisible(true)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#e74c3c";
                          e.currentTarget.style.color = "#fff";
                          e.currentTarget.style.borderColor = "#e74c3c";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#fbe3e4";
                          e.currentTarget.style.color = "#a94442";
                          e.currentTarget.style.borderColor = "#fbe3e4";
                        }}
                        loading={isProcessing}
                        size="middle"
                      >
                        Reject
                      </Button>
                    </CancelAccess>
                  </div>
                )}

                {/* Show edit menu if user has module edit permission OR is owner/admin/hierarchy, and base conditions are met */}
                {(() => {
                  const isTaskEditable = isTaskStatusEditable(
                    selectedTaskDetails?.taskStatus,
                  );

                  // Recalculate baseConditions from task detail data
                  const projectStatus =
                    selectedTaskDetails?.projectStatus ||
                    projectResponse?.status ||
                    "";
                  const phaseStatus =
                    selectedTaskDetails?.phaseStatus ||
                    phaseResponse?.status ||
                    "";
                  const activityStatus =
                    selectedTaskDetails?.activityStatus || "";
                  const isLocked = (projectResponse as any)?.isLocked || false;
                  const lockedBy = (projectResponse as any)?.lockedBy;

                  const baseConditionsMet =
                    !["onhold", "cancelled", "archived", "closed"].includes(
                      projectStatus?.toLowerCase(),
                    ) &&
                    !["onhold", "cancelled", "archived", "closed"].includes(
                      phaseStatus?.toLowerCase(),
                    ) &&
                    ![
                      "onhold",
                      "cancelled",
                      "archived",
                      "closed",
                      "deleted",
                    ].includes(activityStatus?.toLowerCase()) &&
                    (isLocked === false || lockedBy == currentUser?.id);
                  const isInHierarchy =
                    selectedTaskDetails?.hierarchyUserIds &&
                    Array.isArray(selectedTaskDetails.hierarchyUserIds) &&
                    currentUser?.id &&
                    selectedTaskDetails.hierarchyUserIds
                      .filter((id: number) => id > 0)
                      .includes(currentUser?.id);
                  const isAdmin =
                    currentUser?.genericRole?.toLowerCase() === "admin";
                  const isTaskOwner =
                    selectedTaskDetails?.taskOwnerId === currentUser?.id;

                  // Check module-level edit permission
                  const hasModulePermission = hasModuleEditPermission("Tasks");

                  // Show if: task is editable AND base conditions met AND
                  // - Owner: Module permission AND owner check
                  // - Admin: Module permission only (hierarchy not needed)
                  // - Others: Module permission AND hierarchy (both required)
                  return (
                    !isFromWorkUI &&
                    isTaskEditable &&
                    baseConditionsMet &&
                    ((isTaskOwner && hasModulePermission) ||
                      (isAdmin && hasModulePermission) ||
                      (hasModulePermission && isInHierarchy))
                  );
                })() && (
                  <EditAccess moduleName="Tasks">
                    <UncontrolledDropdown>
                      <DropdownToggle
                        tag="a"
                        className="dropdown-toggle btn btn-icon btn-trigger"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "32px",
                          width: "32px",
                          padding: 0,
                          margin: 0,
                        }}
                      >
                        <Icon name="more-v"></Icon>
                      </DropdownToggle>
                      <DropdownMenu end>
                        <ul className="link-list-opt">
                          <li>
                            <DropdownItem
                              tag="a"
                              href="#edit"
                              onClick={async (ev: any) => {
                                ev.preventDefault();
                                await loadDropdownData();

                                // Load existing tools data for edit mode
                                if (
                                  selectedTaskDetails?.taskTools &&
                                  selectedTaskDetails.taskTools.length > 0
                                ) {
                                  setTaskTools(
                                    selectedTaskDetails.taskTools.map(
                                      mapExistingTaskTool,
                                    ),
                                  );
                                } else {
                                  setTaskTools([]);
                                }

                                // Load existing hyperlinks data for edit mode
                                if (
                                  selectedTaskDetails?.hyperlinks &&
                                  selectedTaskDetails.hyperlinks.length > 0
                                ) {
                                  const existingHyperlinks =
                                    selectedTaskDetails.hyperlinks.map(
                                      (hyperlink: any) => ({
                                        hyperlinkId: hyperlink.hyperlinkId,
                                        name: hyperlink.name,
                                        link: hyperlink.link,
                                      }),
                                    );
                                  setHyperlinks(existingHyperlinks);
                                } else {
                                  setHyperlinks([]);
                                }

                                setIsEditMode(true);
                                setDisableSubmit(false);
                              }}
                            >
                              <span>Edit</span>
                            </DropdownItem>
                          </li>
                        </ul>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </EditAccess>
                )}
              </>
            )}
          </div>
          {!hideInternalTabs && (
            <div
              style={{
                position: "relative",
                top: 0,
                zIndex: 10,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
                paddingRight: "20px",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <div className="d-flex align-items-center" style={{ gap: "12px" }}>
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "10px",
                    background:
                      "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <InfoCircleOutlined
                    style={{ fontSize: "18px", color: "#2563eb" }}
                  />
                </div>
                <h5
                  style={{
                    fontFamily: '"Roboto", sans-serif',
                    margin: 0,
                    lineHeight: "1.2",
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#1e293b",
                  }}
                >
                  Task Information
                </h5>
              </div>
            </div>
          )}

          {contextHolder}
          {(() => {
            return null;
          })()}
          {(() => {
            return null;
          })()}

          <Alert
            isOpen={
              selectedTaskDetails?.taskStatus?.toLowerCase() === "onhold" &&
              selectedTaskDetails?.taskOnholdReason
            }
            color="warning"
            className="start-0 w-100 py-1 mt-2"
            style={{
              top: 0,
              textAlign: "left",
              fontSize: "14px",
              cursor: "default",
            }}
          >
            Task is On-Hold on{" "}
            <span className="fw-bold">
              {dayjs(selectedTaskDetails?.taskOnholdDate)?.format(
                "DD-MMM-YYYY",
              )}
            </span>{" "}
            till{" "}
            <span className="fw-bold">
              {dayjs(selectedTaskDetails?.taskETAResolution)?.format(
                "DD-MMM-YYYY",
              )}
            </span>
            .{" "}
            <ReactTyped
              strings={[
                "Because of, " + (selectedTaskDetails?.taskOnholdReason || ""),
              ]}
              typeSpeed={10}
              showCursor={false}
            />
          </Alert>
          <Alert
            isOpen={errorData != undefined}
            color="danger"
            className="start-0 w-100 py-1 mt-2"
            style={{
              top: 0,
              textAlign: "left",
              fontSize: "14px",
              cursor: "default",
            }}
          >
            <span className="fw-bold">{errorData?.error}: </span>
            <ReactTyped
              strings={[errorData?.message || ""]}
              typeSpeed={10}
              showCursor={false}
            />
          </Alert>

          <FormProvider {...methods}>
            <form
              className="content clearfix"
              style={{
                position: "relative",
                zIndex: 1,
                maxWidth: "100%",
              }}
            >
              <div className="row g-3">
                <div className="col-12 col-md-4">
                  {isEditMode ? (
                    <FormInput
                      name="taskName"
                      label={undefined}
                      optionLabel="Task Name"
                      optionLabelWidth="130px"
                      type="text"
                      placeholder="Enter Task Name"
                      disabled={!isEditMode || isFromWorkUI}
                      required={true}
                    />
                  ) : (
                    <div
                      style={responsiveLabelValueStyle}
                      className="labelValuesStyle"
                    >
                      <label
                        style={responsiveLabelStyle}
                        className="labelStyle"
                      >
                        Task Name
                      </label>
                      <div style={responsiveValueStyle} className="valueStyle">
                        {selectedTaskDetails?.taskName || "N/A"}
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-12 col-md-4">
                  {isEditMode ? (
                    <FormInput
                      name="internalTaskNumber"
                      label={undefined}
                      optionLabel="Internal Task Number"
                      optionLabelWidth="130px"
                      type="text"
                      placeholder="Enter Internal Task Number"
                      disabled={!isEditMode || isFromWorkUI}
                      required={true}
                    />
                  ) : (
                    <div
                      style={responsiveLabelValueStyle}
                      className="labelValuesStyle"
                    >
                      <label
                        style={responsiveLabelStyle}
                        className="labelStyle"
                      >
                        Internal Task Number
                      </label>
                      <div style={responsiveValueStyle} className="valueStyle">
                        {selectedTaskDetails?.internalTaskNumber || "N/A"}
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-12 col-md-4">
                  {isEditMode ? (
                    <FormSelect
                      disabled={!isEditMode || isFromWorkUI}
                      name="workCategoryId"
                      label={undefined}
                      optionLabel="Work Category"
                      optionLabelWidth="130px"
                      placeholder="Select Work Category"
                      options={workCatgoriesList}
                      required={true}
                    />
                  ) : (
                    <div
                      style={responsiveLabelValueStyle}
                      className="labelValuesStyle"
                    >
                      <label
                        style={responsiveLabelStyle}
                        className="labelStyle"
                      >
                        Work Category
                      </label>
                      <div style={responsiveValueStyle} className="valueStyle">
                        {selectedTaskDetails?.workCategoryName || "N/A"}
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-12 col-md-4">
                  {isEditMode ? (
                    <FormSelect
                      disabled={!isEditMode || isFromWorkUI}
                      name="workType"
                      label={undefined}
                      optionLabel="Work Type"
                      optionLabelWidth="130px"
                      placeholder="Select Work Type"
                      options={[
                        { label: "Open", value: "Open" },
                        { label: "Quantity", value: "Quantity" },
                      ]}
                      defaultValue="Open"
                      required={true}
                    />
                  ) : (
                    <div
                      style={responsiveLabelValueStyle}
                      className="labelValuesStyle"
                    >
                      <label
                        style={responsiveLabelStyle}
                        className="labelStyle"
                      >
                        Work Type
                      </label>
                      <div style={responsiveValueStyle} className="valueStyle">
                        {selectedTaskDetails?.workType || "N/A"}
                      </div>
                    </div>
                  )}
                </div>

                {/* Work Quantity fields (for Quantity work type) */}
                {workTypeWatch === "Quantity" && (
                  <>
                    <div className="col-12 col-md-4">
                      {isEditMode ? (
                        <FormInput
                          disabled={!isEditMode || isFromWorkUI}
                          name="workQuantity"
                          label={undefined}
                          optionLabel="Work Quantity"
                          optionLabelWidth="130px"
                          pattern={/^\d+$/}
                          placeholder="Enter Work Quantity"
                          required={true}
                        />
                      ) : (
                        <div style={responsiveLabelValueStyle}>
                          <label style={responsiveLabelStyle}>
                            Work Quantity
                          </label>
                          <div style={responsiveValueStyle}>
                            {selectedTaskDetails?.workQuantity || "N/A"}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="col-12 col-md-4">
                      {isEditMode ? (
                        <FormSelect
                          disabled={!isEditMode || isFromWorkUI}
                          name="workUomId"
                          label={undefined}
                          optionLabel="Work Uom"
                          optionLabelWidth="130px"
                          placeholder="Select Work Uom"
                          options={workUomsList}
                          required={true}
                        />
                      ) : (
                        <div style={responsiveLabelValueStyle}>
                          <label style={responsiveLabelStyle}>Work Uom</label>
                          <div style={responsiveValueStyle}>
                            {workUomsList.find(
                              (uom: any) =>
                                uom.value === selectedTaskDetails?.workUomId,
                            )?.label || "N/A"}
                          </div>
                        </div>
                      )}
                    </div>
                    {isEditMode ? (
                      <>
                        <div className="col-12 col-md-4">
                          <FormInput
                            disabled={!isEditMode}
                            name="outputQuantity"
                            label={undefined}
                            optionLabel="Output / Prod. Qty"
                            optionLabelWidth="130px"
                            type="number"
                            placeholder="e.g. output per basis"
                            pattern={/^\d+(\.\d{1,2})?$/}
                            patternErrorMessage="Enter a valid output quantity"
                          />
                        </div>
                        <div className="col-12 col-md-4">
                          <FormSelect
                            disabled={!isEditMode}
                            name="outputBasisUomId"
                            label={undefined}
                            optionLabel="Output Basis"
                            optionLabelWidth="130px"
                            placeholder="e.g. Day / Shift / Blast"
                            options={workUomsList}
                          />
                        </div>
                      </>
                    ) : (
                      (selectedTaskDetails?.outputQuantity != null ||
                        selectedTaskDetails?.outputBasisUomId != null) && (
                        <div className="col-12 col-md-4">
                          <div style={responsiveLabelValueStyle}>
                            <label style={responsiveLabelStyle}>
                              Output / Productivity
                            </label>
                            <div style={responsiveValueStyle}>
                              {selectedTaskDetails?.outputQuantity != null
                                ? `${selectedTaskDetails.outputQuantity} ${
                                    workUomsList.find(
                                      (uom: any) =>
                                        uom.value ===
                                        selectedTaskDetails?.workUomId,
                                    )?.label || ""
                                  }${
                                    selectedTaskDetails?.outputBasisUomName
                                      ? ` per ${selectedTaskDetails.outputBasisUomName}`
                                      : ""
                                  }`
                                : "N/A"}
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </>
                )}
                {/* (Removed temporary extra Tags+Description block; Tags already exist below) */}

                <div className="col-12 col-md-4">
                  {isEditMode ? (
                    <FormSelect
                      disabled={!isEditMode || isFromWorkUI}
                      name="taskOwnerId"
                      label={undefined}
                      optionLabel="Owner"
                      optionLabelWidth="130px"
                      placeholder="Select Owner"
                      options={ownerOptions}
                      required={true}
                    />
                  ) : (
                    <div
                      style={responsiveLabelValueStyle}
                      className="labelValuesStyle"
                    >
                      <label
                        style={responsiveLabelStyle}
                        className="labelStyle"
                      >
                        Owner
                        {selectedTaskDetails?.originalTaskOwnerId &&
                        selectedTaskDetails?.originalTaskOwnerId !=
                          selectedTaskDetails?.ownerId ? (
                          <Popconfirm
                            title={<></>}
                            icon={<></>}
                            showCancel={false}
                            trigger="hover"
                            okButtonProps={{
                              style: {
                                display: "none",
                              },
                            }}
                            description={
                              <Descriptions
                                title=""
                                bordered
                                column={1}
                                size="small"
                                className="custom-description-title"
                              >
                                <Descriptions.Item label="Original Owner">
                                  {selectedTaskDetails?.originalTaskOwnerName ||
                                    "N/A"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Reason">
                                  {selectedTaskDetails?.reassignedReason ||
                                    "N/A"}
                                </Descriptions.Item>
                              </Descriptions>
                            }
                          >
                            <Icon
                              name="info"
                              style={{
                                cursor: "pointer",
                                fontSize: "13px",
                              }}
                            />
                          </Popconfirm>
                        ) : (
                          <></>
                        )}
                      </label>
                      <div style={responsiveValueStyle} className="valueStyle">
                        {selectedTaskDetails?.taskOwnerName || "N/A"}
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-12 col-md-4">
                  {isEditMode ? (
                    <FormDatePicker
                      disabled={!isEditMode || isFromWorkUI}
                      name="taskPlannedStartDate"
                      label={undefined}
                      optionLabel="Planned Start Date"
                      optionLabelWidth="130px"
                      minDate={
                        projectStartDate && dayjs(projectStartDate).isValid()
                          ? new Date(projectStartDate)
                          : undefined
                      }
                      maxDate={
                        projectEndDate && dayjs(projectEndDate).isValid()
                          ? new Date(projectEndDate)
                          : undefined
                      }
                      placeholder={!isEditMode ? "N/A" : "Select Start Date"}
                    />
                  ) : (
                    <div
                      style={responsiveLabelValueStyle}
                      className="labelValuesStyle"
                    >
                      <label
                        style={responsiveLabelStyle}
                        className="labelStyle"
                      >
                        Planned Start Date
                      </label>
                      <div style={responsiveValueStyle} className="valueStyle">
                        {selectedTaskDetails?.taskPlannedStartDate
                          ? dayjs(
                              selectedTaskDetails?.taskPlannedStartDate,
                            ).format("DD-MMM-YYYY")
                          : "N/A"}
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-12 col-md-4">
                  {isEditMode ? (
                    <FormDatePicker
                      name="taskPlannedEndDate"
                      label={undefined}
                      optionLabel="Planned End Date"
                      optionLabelWidth="130px"
                      disabled={!startDateWatch || !isEditMode || isFromWorkUI}
                      minDate={startDateWatch}
                      maxDate={
                        projectEndDate && dayjs(projectEndDate).isValid()
                          ? new Date(projectEndDate)
                          : undefined
                      }
                      placeholder={!isEditMode ? "N/A" : "Select End Date"}
                    />
                  ) : (
                    <div
                      style={responsiveLabelValueStyle}
                      className="labelValuesStyle"
                    >
                      <label
                        style={responsiveLabelStyle}
                        className="labelStyle"
                      >
                        Planned End Date
                      </label>
                      <div style={responsiveValueStyle} className="valueStyle">
                        {selectedTaskDetails?.taskPlannedEndDate
                          ? dayjs(
                              selectedTaskDetails?.taskPlannedEndDate,
                            ).format("DD-MMM-YYYY")
                          : "N/A"}
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-12 col-md-4">
                  {isEditMode ? (
                    <FormSelect
                      disabled={!isEditMode || isFromWorkUI}
                      name="priorityId"
                      label={undefined}
                      optionLabel="Priority"
                      optionLabelWidth="130px"
                      placeholder="Select Priority"
                      options={priorityList}
                      defaultValue={selectedTaskDetails?.priorityName}
                    />
                  ) : (
                    <div
                      style={responsiveLabelValueStyle}
                      className="labelValuesStyle"
                    >
                      <label
                        style={responsiveLabelStyle}
                        className="labelStyle"
                      >
                        Priority
                      </label>
                      <div style={responsiveValueStyle} className="valueStyle">
                        {selectedTaskDetails?.priorityName || "N/A"}
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-12 col-md-4">
                  {isEditMode ? (
                    <FormSelect
                      name="isBillable"
                      label={undefined}
                      optionLabel="Billing Type"
                      optionLabelWidth="130px"
                      placeholder="Select Billing Type"
                      disabled={
                        !isEditMode ||
                        isFromWorkUI ||
                        workTypeWatch === "Quantity"
                      }
                      options={BILLABLE_OPTIONS}
                    />
                  ) : (
                    <div
                      style={responsiveLabelValueStyle}
                      className="labelValuesStyle"
                    >
                      <label
                        style={responsiveLabelStyle}
                        className="labelStyle"
                      >
                        Billing Type
                      </label>
                      <div style={responsiveValueStyle} className="valueStyle">
                        {selectedTaskDetails?.isBillable !== undefined
                          ? selectedTaskDetails.isBillable === true
                            ? "Billable"
                            : "Non Billable"
                          : "N/A"}
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-12 col-md-4">
                  {isEditMode ? (
                    <FormInput
                      name="taskWeightagePercentage"
                      label={undefined}
                      optionLabel={
                        <span>
                          Weightage
                          {remainingWeightage !== null && (
                            <span
                              style={{
                                color: "#52c41a",
                                marginLeft: "8px",
                                fontWeight: "normal",
                              }}
                            >
                              (Avl: {remainingWeightage}%)
                            </span>
                          )}
                        </span>
                      }
                      optionLabelWidth="130px"
                      placeholder="Enter Weightage %"
                      type="text"
                      disabled={!isEditMode || isFromWorkUI}
                      pattern={/^(?:100(?:\.0{1,2})?|\d{1,2}(?:\.\d{1,2})?)$/}
                      patternErrorMessage={`Enter a value between 0.01 and ${
                        remainingWeightage ?? 100
                      } (up to 2 decimals)`}
                      customValidation={{
                        validate: (value: string) => {
                          if (
                            value === undefined ||
                            value === null ||
                            value === ""
                          )
                            return true;
                          const n = Number(value);
                          if (Number.isNaN(n)) return true;
                          if (n <= 0)
                            return "Weightage must be greater than 0";
                          if (remainingWeightage === null) return true;
                          return (
                            n <= remainingWeightage ||
                            `Cannot exceed available (${remainingWeightage}%)`
                          );
                        },
                      }}
                    />
                  ) : (
                    <div
                      style={responsiveLabelValueStyle}
                      className="labelValuesStyle"
                    >
                      <label
                        style={responsiveLabelStyle}
                        className="labelStyle"
                      >
                        Weightage
                      </label>
                      <div style={responsiveValueStyle} className="valueStyle">
                        {selectedTaskDetails?.taskWeightagePercentage
                          ? `${
                              Number(
                                selectedTaskDetails.taskWeightagePercentage,
                              ) %
                                1 ===
                              0
                                ? parseInt(
                                    selectedTaskDetails.taskWeightagePercentage,
                                  )
                                : selectedTaskDetails.taskWeightagePercentage
                            }%`
                          : "N/A"}
                      </div>
                    </div>
                  )}
                </div>

                <div className="col-12 col-md-4">
                  {isEditMode ? (
                    <FormInput
                      name="rate"
                      label={undefined}
                      optionLabel="Rate incl. GST"
                      optionLabelWidth="130px"
                      placeholder="Enter Rate"
                      type="number"
                      disabled={!isEditMode || isFromWorkUI}
                      required={true}
                      pattern={/^\d+(\.\d{1,2})?$/}
                      patternErrorMessage="Enter a valid rate"
                      customValidation={{
                        validate: (value: string) => {
                          const amount = parseFloat(value);
                          if (isNaN(amount))
                            return "Please enter a valid number";
                          if (amount <= 0) return "Rate must be greater than 0";
                          return true;
                        },
                      }}
                    />
                  ) : (
                    <div
                      style={responsiveLabelValueStyle}
                      className="labelValuesStyle"
                    >
                      <label
                        style={responsiveLabelStyle}
                        className="labelStyle"
                      >
                        Rate incl. GST
                      </label>
                      {/* Hover shows how the quoted rate resolves to the Basic Rate. */}
                      <Tooltip
                        title={rateInclGstTooltip(
                          taskPriceBreakdown,
                          taskGstPercent,
                          taskCessPercent,
                        )}
                      >
                        <div
                          style={{
                            ...responsiveValueStyle,
                            cursor: "help",
                            borderBottom: "1px dotted #8094ae",
                            display: "inline-block",
                          }}
                          className="valueStyle"
                        >
                          {selectedTaskDetails?.rate ?? "N/A"}
                        </div>
                      </Tooltip>
                    </div>
                  )}
                </div>

                {isEditMode && !isFromWorkUI && (
                  <div className="col-12 col-md-4">
                    <FormInput
                      name="gstPercent"
                      label={undefined}
                      optionLabel="GST %"
                      optionLabelWidth="130px"
                      placeholder="18"
                      type="number"
                      customValidation={{
                        validate: (value: string) => {
                          if (value === "" || value === null || value === undefined)
                            return true;
                          const pct = parseFloat(value);
                          if (isNaN(pct)) return "Please enter a valid number";
                          if (pct < 0 || pct > 100) return "GST % must be 0-100";
                          return true;
                        },
                      }}
                    />
                  </div>
                )}

                {isEditMode && !isFromWorkUI && (
                  <div className="col-12 col-md-4">
                    <FormInput
                      name="cessPercent"
                      label={undefined}
                      optionLabel="Labour Cess %"
                      optionLabelWidth="130px"
                      placeholder="1"
                      type="number"
                      customValidation={{
                        validate: (value: string) => {
                          if (value === "" || value === null || value === undefined)
                            return true;
                          const pct = parseFloat(value);
                          if (isNaN(pct)) return "Please enter a valid number";
                          if (pct < 0 || pct > 100) return "Cess % must be 0-100";
                          return true;
                        },
                      }}
                    />
                  </div>
                )}

                {/* Base / GST / Labour Cess / Basic Rate — all derived from the
                    GST-inclusive rate above. While editing this previews the pending
                    values; otherwise it shows what the server stored. */}
                <div className="col-12">
                  <TaskPriceBreakdownPanel
                    breakdown={taskPriceBreakdown}
                    gstPercent={taskGstPercent}
                    cessPercent={taskCessPercent}
                  />
                </div>

                {/* Total Amount = Work Quantity × Basic Rate. Costed on the working
                    rate: GST is a pass-through and labour cess is withheld by the
                    client, so neither is value the project earns. */}
                {workTypeWatch === "Quantity" &&
                  selectedTaskDetails?.workQuantity != null &&
                  taskPriceBreakdown.uploadRate > 0 && (
                    <div className="col-12 col-md-4">
                      <div
                        style={responsiveLabelValueStyle}
                        className="labelValuesStyle"
                      >
                        <label
                          style={responsiveLabelStyle}
                          className="labelStyle"
                        >
                          Total Amount
                        </label>
                        <div
                          style={responsiveValueStyle}
                          className="valueStyle"
                        >
                          {`₹${(
                            Number(selectedTaskDetails.workQuantity) *
                            taskPriceBreakdown.uploadRate
                          ).toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`}
                        </div>
                      </div>
                    </div>
                  )}

                {/* Executed Rate Field - Only show if executedRate exists */}
                {selectedTaskDetails?.executedRate !== null &&
                  selectedTaskDetails?.executedRate !== undefined && (
                    <div className="col-12 col-md-4">
                      <div
                        style={responsiveLabelValueStyle}
                        className="labelValuesStyle"
                      >
                        <label
                          style={responsiveLabelStyle}
                          className="labelStyle"
                        >
                          Executed Rate
                          {selectedTaskDetails?.rateRemarks && (
                            <Tooltip title={selectedTaskDetails.rateRemarks}>
                              <InfoCircleOutlined
                                style={{
                                  marginLeft: 8,
                                  color: "#004293",
                                  cursor: "pointer",
                                }}
                              />
                            </Tooltip>
                          )}
                        </label>
                        <div
                          style={responsiveValueStyle}
                          className="valueStyle"
                        >
                          {selectedTaskDetails.executedRate}
                        </div>
                      </div>
                    </div>
                  )}

                <div className="col-12 col-md-4">
                  {isEditMode ? (
                    <FormSelect
                      disabled={true}
                      name="contractorId"
                      label={undefined}
                      optionLabel="Contractor"
                      optionLabelWidth="130px"
                      placeholder="Select Contractor"
                      options={contractorOptions}
                      required={true}
                    />
                  ) : (
                    <div
                      style={responsiveLabelValueStyle}
                      className="labelValuesStyle"
                    >
                      <label
                        style={responsiveLabelStyle}
                        className="labelStyle"
                      >
                        Contractor
                      </label>
                      <div style={responsiveValueStyle} className="valueStyle">
                        {selectedTaskDetails?.taskContractorName || "N/A"}
                      </div>
                    </div>
                  )}
                </div>

                {/* Budget Line Section - Only show for tasks and when project has budget */}
                {hasProjectBudget && (
                  <>
                    <div className="col-12 col-md-4">
                      {isEditMode ? (
                        <FormSelect
                          name="budgetLineId"
                          label={undefined}
                          optionLabel={
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <span>Budget Line</span>
                              <span style={{ color: "red" }}>*</span>
                              {selectedBudgetLine && (
                                <Popconfirm
                                  placement="right"
                                  title={<></>}
                                  icon={<></>}
                                  showCancel={false}
                                  trigger="hover"
                                  okButtonProps={{
                                    style: {
                                      display: "none",
                                    },
                                  }}
                                  description={
                                    <div className="d-flex flex-column">
                                      <Descriptions
                                        title=""
                                        bordered
                                        column={1}
                                        size="small"
                                        className="custom-description-title"
                                      >
                                        <Descriptions.Item label="Budget Value">
                                          ₹
                                          {selectedBudgetLine.budgetValue?.toLocaleString(
                                            "en-IN",
                                          )}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Allocated Value">
                                          ₹
                                          {selectedBudgetLine.allocatedLineValue?.toLocaleString(
                                            "en-IN",
                                          )}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Remaining">
                                          ₹
                                          {getRealTimeRemainingBudget()?.toLocaleString(
                                            "en-IN",
                                          )}
                                        </Descriptions.Item>
                                      </Descriptions>
                                    </div>
                                  }
                                >
                                  <Icon
                                    name="info"
                                    className="ms-1"
                                    style={{
                                      color: "#004293",
                                      cursor: "pointer",
                                      fontSize: "16px",
                                    }}
                                  />
                                </Popconfirm>
                              )}
                            </div>
                          }
                          optionLabelWidth="130px"
                          placeholder="Select Budget Line"
                          disabled={true}
                          options={budgetLines}
                          required={true}
                          hideRequiredMark={true}
                        />
                      ) : (
                        <div
                          style={responsiveLabelValueStyle}
                          className="labelValuesStyle"
                        >
                          <label
                            style={responsiveLabelStyle}
                            className="labelStyle"
                          >
                            Budget Line
                          </label>
                          <div
                            style={responsiveValueStyle}
                            className="valueStyle"
                          >
                            {budgetLines.find(
                              (budget) =>
                                budget.value ===
                                selectedTaskDetails?.budgetLineId,
                            )?.label || "N/A"}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="col-12 col-md-4">
                      {isEditMode ? (
                        <FormInput
                          name="allocatedBudgetAmount"
                          label={undefined}
                          optionLabel="Allocation Amount"
                          optionLabelWidth="130px"
                          placeholder="Enter amount to allocate for this task"
                          type="number"
                          disabled={
                            !isEditMode || !budgetLineIdWatch || isFromWorkUI
                          }
                          required={true}
                          pattern={
                            new RegExp(`^(?:[1-9]\\d*)(?:\\.\\d{1,2})?$`)
                          }
                          patternErrorMessage="Amount must be greater than 0"
                          customValidation={{
                            validate: (value: string) => {
                              const amount = parseFloat(value);
                              if (isNaN(amount))
                                return "Please enter a valid number";

                              // Only validate against remaining budget if the field is dirty (user has changed it)
                              // and the value is actually different from the original value
                              if (
                                dirtyFields &&
                                dirtyFields.allocatedBudgetAmount
                              ) {
                                const originalAmount =
                                  selectedTaskDetails?.budgetValue ||
                                  selectedTaskDetails?.allocatedBudgetAmount ||
                                  0;

                                // Only validate if the value has actually changed from the original
                                if (amount !== originalAmount) {
                                  const remainingBudget = selectedBudgetLine
                                    ? selectedBudgetLine.budgetValue -
                                      selectedBudgetLine.allocatedLineValue
                                    : selectedBudgetValue || 0;

                                  // Calculate the maximum allowed allocation
                                  const maxAllowedAllocation =
                                    originalAmount +
                                    (selectedBudgetLine
                                      ? selectedBudgetLine.budgetValue -
                                        selectedBudgetLine.allocatedLineValue
                                      : selectedBudgetValue || 0);

                                  // Check if the new amount exceeds the maximum allowed allocation
                                  if (amount > maxAllowedAllocation) {
                                    const remainingBudget = selectedBudgetLine
                                      ? selectedBudgetLine.budgetValue -
                                        selectedBudgetLine.allocatedLineValue
                                      : selectedBudgetValue || 0;

                                    return `Limit: ₹${maxAllowedAllocation?.toLocaleString(
                                      "en-IN",
                                    )}`;
                                  }
                                }
                              }

                              return true;
                            },
                          }}
                        />
                      ) : (
                        <div
                          style={responsiveLabelValueStyle}
                          className="labelValuesStyle"
                        >
                          <label
                            style={responsiveLabelStyle}
                            className="labelStyle"
                          >
                            Allocation Amount
                          </label>
                          <div
                            style={responsiveValueStyle}
                            className="valueStyle"
                          >
                            {selectedTaskDetails?.budgetValue ||
                            selectedTaskDetails?.allocatedBudgetAmount
                              ? `₹${(
                                  selectedTaskDetails?.budgetValue ||
                                  selectedTaskDetails?.allocatedBudgetAmount
                                )?.toLocaleString("en-IN")}`
                              : "N/A"}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Site Hierarchy Section - Only show if workLevelSetting or segmentLevel exists and not a subtask */}
                {!isSubtask &&
                  (selectedTaskDetails?.workLevelSetting ||
                    selectedTaskDetails?.segmentLevel) && (
                    <>
                      {shouldShowHierarchy && (
                        <div className="col-12 col-md-4">
                          {isEditMode ? (
                            <FormSelect
                              name="siteStructureUnitId"
                              label={undefined}
                              optionLabel={hierarchyFieldLabel}
                              optionLabelWidth="130px"
                              disabled={
                                !isEditMode ||
                                isFromWorkUI ||
                                isTowerBlockDisabledFromApi
                              }
                              options={mergedTowerOptions}
                              placeholder={hierarchyFieldPlaceholder}
                              required={true}
                            />
                          ) : (
                            <div
                              style={responsiveLabelValueStyle}
                              className="labelValuesStyle"
                            >
                              <label
                                style={responsiveLabelStyle}
                                className="labelStyle"
                              >
                                {hierarchyFieldLabel}
                              </label>
                              <div
                                style={responsiveValueStyle}
                                className="valueStyle"
                              >
                                {selectedTaskDetails?.siteStructureUnitName ||
                                  towerOptions.find(
                                    (opt) =>
                                      opt.value ===
                                      selectedTaskDetails?.siteStructureUnitId?.toString(),
                                  )?.label ||
                                  "N/A"}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {shouldShowHierarchy &&
                        (workLevelSetting === "TOWER_BLOCK" ||
                          workLevelSetting === "VILLA") && (
                          <div className="col-12 col-md-4">
                            {isEditMode ? (
                              <FormSelect
                                name="locationId"
                                label={undefined}
                                optionLabel="Location"
                                optionLabelWidth="130px"
                                disabled={
                                  !isEditMode ||
                                  isFromWorkUI ||
                                  isLocationDisabledFromApi
                                }
                                options={mergedLocationOptions}
                                placeholder="Select Location"
                              />
                            ) : (
                              <div
                                style={responsiveLabelValueStyle}
                                className="labelValuesStyle"
                              >
                                <label
                                  style={responsiveLabelStyle}
                                  className="labelStyle"
                                >
                                  Location
                                </label>
                                <div
                                  style={responsiveValueStyle}
                                  className="valueStyle"
                                >
                                  {selectedTaskDetails?.locationName ||
                                    locationOptions.find(
                                      (opt) =>
                                        opt.value ===
                                        selectedTaskDetails?.locationId?.toString(),
                                    )?.label ||
                                    "N/A"}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      {shouldShowHierarchy &&
                        (workLevelSetting === "FLOOR" ||
                          workLevelSetting === "FLAT") && (
                          <div className="col-12 col-md-4">
                            {isEditMode ? (
                              <FormSelect
                                name="floorNo"
                                label={undefined}
                                optionLabel="Floor"
                                optionLabelWidth="130px"
                                disabled={
                                  !isEditMode ||
                                  isFromWorkUI ||
                                  isFloorDisabledFromApi
                                }
                                options={mergedFloorOptions}
                                placeholder="Select Floor"
                                required={true}
                              />
                            ) : (
                              <div
                                style={responsiveLabelValueStyle}
                                className="labelValuesStyle"
                              >
                                <label
                                  style={responsiveLabelStyle}
                                  className="labelStyle"
                                >
                                  Floor
                                </label>
                                <div
                                  style={responsiveValueStyle}
                                  className="valueStyle"
                                >
                                  {selectedTaskDetails?.floorNo || "N/A"}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      {shouldShowHierarchy && workLevelSetting === "FLOOR" && (
                        <div className="col-12 col-md-4">
                          {isEditMode ? (
                            <FormSelect
                              name="locationId"
                              label={undefined}
                              optionLabel="Location"
                              optionLabelWidth="130px"
                              disabled={
                                !isEditMode ||
                                isFromWorkUI ||
                                isLocationDisabledFromApi
                              }
                              options={mergedLocationOptions}
                              placeholder="Select Location"
                            />
                          ) : (
                            <div
                              style={responsiveLabelValueStyle}
                              className="labelValuesStyle"
                            >
                              <label
                                style={responsiveLabelStyle}
                                className="labelStyle"
                              >
                                Location
                              </label>
                              <div
                                style={responsiveValueStyle}
                                className="valueStyle"
                              >
                                {locationOptions.find(
                                  (opt) =>
                                    opt.value ===
                                    selectedTaskDetails?.locationId?.toString(),
                                )?.label || "N/A"}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {shouldShowHierarchy && workLevelSetting === "FLAT" && (
                        <>
                          <div className="col-12 col-md-4">
                            {isEditMode ? (
                              <FormSelect
                                name="siteStructureSubUnitId"
                                label={undefined}
                                optionLabel="Flat"
                                optionLabelWidth="130px"
                                disabled={
                                  !isEditMode ||
                                  isFromWorkUI ||
                                  isFlatDisabledFromApi
                                }
                                options={mergedFlatOptions}
                                placeholder="Select Flat"
                                required={true}
                              />
                            ) : (
                              <div
                                style={responsiveLabelValueStyle}
                                className="labelValuesStyle"
                              >
                                <label
                                  style={responsiveLabelStyle}
                                  className="labelStyle"
                                >
                                  Flat
                                </label>
                                <div
                                  style={responsiveValueStyle}
                                  className="valueStyle"
                                >
                                  {selectedTaskDetails?.siteStructureSubUnitName ||
                                    flatOptions.find(
                                      (opt) =>
                                        opt.value ===
                                        selectedTaskDetails?.siteStructureSubUnitId?.toString(),
                                    )?.label ||
                                    "N/A"}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="col-12 col-md-4">
                            {isEditMode ? (
                              <FormSelect
                                name="locationId"
                                label={undefined}
                                optionLabel="Location"
                                optionLabelWidth="130px"
                                disabled={
                                  !isEditMode ||
                                  isFromWorkUI ||
                                  isLocationDisabledFromApi
                                }
                                options={mergedLocationOptions}
                                placeholder="Select Location"
                              />
                            ) : (
                              <div
                                style={responsiveLabelValueStyle}
                                className="labelValuesStyle"
                              >
                                <label
                                  style={responsiveLabelStyle}
                                  className="labelStyle"
                                >
                                  Location
                                </label>
                                <div
                                  style={responsiveValueStyle}
                                  className="valueStyle"
                                >
                                  {selectedTaskDetails?.locationName ||
                                    locationOptions.find(
                                      (opt) =>
                                        opt.value ===
                                        selectedTaskDetails?.locationId?.toString(),
                                    )?.label ||
                                    "N/A"}
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}

                <div className="col-12 col-md-4">
                  {isEditMode ? (
                    <FormMultiSelect
                      disabled={!isEditMode || isFromWorkUI}
                      name="projectUserId"
                      label={undefined}
                      optionLabel="Associated Team"
                      optionLabelWidth="130px"
                      placeholder="Select"
                      options={teamOptions}
                      tagRender={teamTagRender}
                    />
                  ) : (
                    <div
                      style={responsiveLabelValueStyle}
                      className="labelValuesStyle"
                    >
                      <label
                        style={responsiveLabelStyle}
                        className="labelStyle"
                      >
                        Associated Team
                      </label>
                      <div style={responsiveValueStyle} className="valueStyle">
                        {(() => {
                          // Mirror edit mode: drop the Owner and fall back to
                          // userName, since the API sometimes returns
                          // taskUsers without taskUserName populated.
                          const teamNames = (
                            selectedTaskDetails?.taskUsers || []
                          )
                            .filter(
                              (user: any) => user?.taskUserType !== "Owner",
                            )
                            .map(
                              (user: any) =>
                                user.taskUserName || user.userName,
                            )
                            .filter(Boolean);
                          return teamNames.length > 0
                            ? teamNames.join(", ")
                            : "N/A";
                        })()}
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-12 col-md-4">
                  {isEditMode ? (
                    <div className="form-group mb-1">
                      <div className="form-control-wrap">
                        <div className="form-control-select d-flex align-items-center">
                          <label
                            className="form-label"
                            style={{ minWidth: "130px" }}
                          >
                            Reminder
                          </label>
                          <div
                            className="reminder-popper-wrapper"
                            style={{ flex: 1 }}
                          >
                            <ReminderPopper
                              setRemainders={setRemainders}
                              remainders={remainders}
                              users={projectUsers.map((i: any) => {
                                return { ...i, value: Number(i?.userId) };
                              })}
                              disabled={!isEditMode || isFromWorkUI}
                              selectedTaskDetails={selectedTaskDetails}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={responsiveLabelValueStyle}
                      className="labelValuesStyle"
                    >
                      <label
                        style={responsiveLabelStyle}
                        className="labelStyle"
                      >
                        Reminder
                      </label>
                      <div style={responsiveValueStyle} className="valueStyle">
                        {(() => {
                          const reminder = selectedTaskDetails?.reminders;
                          if (
                            !reminder ||
                            !reminder.reminderType ||
                            !reminder.specificTime
                          ) {
                            return "N/A";
                          }

                          let text = reminder.reminderType;

                          if (
                            reminder.reminderType === "On" &&
                            reminder.specificDate
                          ) {
                            text += ` ${dayjs(reminder.specificDate).format(
                              "MMM DD, YYYY",
                            )}`;
                          } else if (
                            reminder.reminderType !== "Daily" &&
                            reminder.offsetFrom
                          ) {
                            text += ` ${reminder.offsetFrom}`;
                          }

                          text += ` ${dayjs(
                            reminder.specificTime,
                            "HH:mm",
                          ).format("HH:mm")}`;
                          return text;
                        })()}
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-12 col-md-6">
                  {isEditMode ? (
                    <div className="form-group mb-1">
                      <div className="form-control-wrap">
                        <div className="form-control-select d-flex align-items-center">
                          <label
                            className="form-label"
                            style={{ minWidth: "130px" }}
                          >
                            Recurrence
                          </label>
                          <div
                            className="recurring-frequency-popper-wrapper"
                            style={{ flex: 1 }}
                            title={
                              !(
                                remainders?.reminderType &&
                                remainders?.specificTime
                              )
                                ? "Please set a Reminder first to enable Recurrence"
                                : undefined
                            }
                          >
                            <RecurringFrequencyPopper
                              setRemainders={setRemainders}
                              disabled={
                                !isEditMode ||
                                isFromWorkUI ||
                                !(
                                  remainders?.reminderType &&
                                  remainders?.specificTime
                                )
                              }
                              selectedTaskDetails={selectedTaskDetails}
                              remainders={remainders}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={responsiveLabelValueStyle}
                      className="labelValuesStyle"
                    >
                      <label
                        style={responsiveLabelStyle}
                        className="labelStyle"
                      >
                        Recurrence
                      </label>
                      <div style={responsiveValueStyle} className="valueStyle">
                        {selectedTaskDetails?.reminders?.recurrenceType
                          ? formatRecurrence(selectedTaskDetails?.reminders)
                          : "N/A"}
                      </div>
                    </div>
                  )}
                </div>

                <div className="col-12 col-md-6">
                  <div style={{ width: "100%" }}>
                    {isEditMode ? (
                      <div className="form-group mb-1">
                        <div className="form-control-wrap">
                          <div className="form-control-select d-flex align-items-center">
                            <label
                              className="form-label"
                              style={{ minWidth: "130px" }}
                            >
                              Approval Required
                            </label>
                            <div style={{ flex: 1 }}>
                              <Select
                                style={{ width: "100%" }}
                                value={isApprovalRequired ? "yes" : "no"}
                                onChange={(value) => {
                                  const newValue = value === "yes";
                                  setIsApprovalRequired(newValue);
                                  if (!newValue) {
                                    // Clear approval hierarchy if approval is not required
                                    setApprovals({
                                      open: false,
                                      data: [],
                                    });
                                  }
                                }}
                                disabled={isFromWorkUI}
                                options={[
                                  { label: "Yes", value: "yes" },
                                  { label: "No", value: "no" },
                                ]}
                              />
                            </div>
                          </div>
                          {isApprovalRequired && (
                            <div
                              style={{ marginTop: "8px", marginLeft: "130px" }}
                            >
                              <Popover
                                content="Click here to set required approvals"
                                title="Approval Required"
                              >
                                <a
                                  href="#"
                                  className="success"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setApprovals((pre) => ({
                                      ...pre,
                                      open: true,
                                    }));
                                  }}
                                >
                                  <EditOutlined /> Set Approvals{" "}
                                  {approvals?.data?.length === 0 ? (
                                    <span className="mandatory">
                                      Please set approvals
                                    </span>
                                  ) : (
                                    <span
                                      style={{
                                        color: "#38d39f",
                                        fontWeight: 500,
                                      }}
                                    >
                                      ({approvals.data.length} level
                                      {approvals.data.length > 1
                                        ? "s"
                                        : ""}{" "}
                                      configured)
                                    </span>
                                  )}
                                </a>
                              </Popover>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div
                        style={responsiveLabelValueStyle}
                        className="labelValuesStyle"
                      >
                        <label
                          style={responsiveLabelStyle}
                          className="labelStyle"
                        >
                          Approval Required
                        </label>
                        <div
                          style={responsiveValueStyle}
                          className="valueStyle"
                        >
                          {selectedTaskDetails?.approvalRequired ? (
                            <ApprovalTimelineContent />
                          ) : (
                            <div>
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "2px 8px",
                                  borderRadius: "4px",
                                  backgroundColor: "#fff",
                                  border: "1px solid #e85347",
                                  color: "#e85347",
                                  fontSize: "10.5px",
                                  fontWeight: "500",
                                }}
                              >
                                No
                              </span>
                              {selectedTaskDetails?.autoApprovalRemarks && (
                                <div
                                  style={{
                                    marginTop: "4px",
                                    fontSize: "12px",
                                    color: "#666",
                                  }}
                                >
                                  Remarks:{" "}
                                  {selectedTaskDetails.autoApprovalRemarks}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {isEditMode && !isApprovalRequired && (
                <div className="row g-3 mt-2">
                  <div className="col-12">
                    <div className="form-group mb-1">
                      <div className="form-control-wrap">
                        <div className="d-flex align-items-start">
                          <label
                            className="form-label"
                            style={{ minWidth: "130px", paddingTop: 4 }}
                          >
                            Remarks
                            <span className="mandatory">*</span>
                          </label>
                          <Input.TextArea
                            value={autoApprovalRemarks}
                            onChange={(e) =>
                              setAutoApprovalRemarks(e.target.value)
                            }
                            maxLength={500}
                            placeholder="Enter reason for not requiring approvals"
                            disabled={isFromWorkUI}
                            style={{ flex: 1, resize: "vertical" }}
                            autoSize={{ minRows: 2, maxRows: 4 }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="row g-3 mt-2">
                <div className="col-12">
                  {isEditMode ? (
                    <div className="form-group mb-1">
                      <div className="form-control-wrap">
                        <div className="form-control-select d-flex align-items-center">
                          <label
                            className="form-label"
                            style={{ minWidth: "130px" }}
                          >
                            Tags
                          </label>
                          <Select
                            mode="tags"
                            style={{ width: "100%", flex: 1 }}
                            disabled={!isEditMode || isFromWorkUI}
                            placeholder="Type and press enter to create or select a tag"
                            labelInValue
                            value={tags}
                            onChange={(value) => setTags(value)}
                            options={projectTagsList}
                            tokenSeparators={[","]}
                            tagRender={tagRender}
                            filterOption={false}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={responsiveLabelValueStyle}
                      className="labelValuesStyle"
                    >
                      <label
                        style={responsiveLabelStyle}
                        className="labelStyle"
                      >
                        Tags
                      </label>
                      <div style={responsiveValueStyle} className="valueStyle">
                        {(selectedTaskDetails?.taskTags || []).length > 0 ? (
                          selectedTaskDetails?.taskTags.map((tag: any) => {
                            const color = getColorByLabel(
                              (tag.tagName || "").toString(),
                            );
                            return (
                              <span
                                key={tag.tagId}
                                className="custom-tag-wrapper"
                              >
                                <Tag
                                  className="custom-tag"
                                  style={{ backgroundColor: color }}
                                >
                                  {tag.tagName}
                                </Tag>
                                <span
                                  className="custom-tag-tail"
                                  style={{ borderLeftColor: color }}
                                />
                              </span>
                            );
                          })
                        ) : (
                          <div
                            style={responsiveValueStyle}
                            className="valueStyle"
                          >
                            N/A
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3"></div>
              {/* Description row in first column below Tags */}
              <div
                className={isEditMode ? "row g-3" : "row"}
                style={{ marginTop: isEditMode ? "16px" : "0px" }}
              >
                <div className={isEditMode ? "col-12" : "col-12 col-md-4"}>
                  {isEditMode ? (
                    <FormInput
                      disabled={!isEditMode || isFromWorkUI}
                      name="taskDescription"
                      label={undefined}
                      optionLabel="Add Description"
                      optionLabelWidth="130px"
                      type="text"
                      minLength={2}
                      maxLength={200}
                      placeholder="Enter Description"
                    />
                  ) : (
                    <div
                      style={responsiveLabelValueStyle}
                      className="labelValuesStyle"
                    >
                      <label
                        style={responsiveLabelStyle}
                        className="labelStyle"
                      >
                        Description
                      </label>
                      <div style={responsiveValueStyle} className="valueStyle">
                        {selectedTaskDetails?.taskDescription || "N/A"}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Removed extra Description row as requested */}
              {/* Rendered as a function call (not <CollapsePanel/>) so it is NOT
                  remounted on every parent re-render — otherwise setTaskTools
                  would remount the tool materials drawer, closing/reopening it.
                  Safe because CollapsePanel uses no hooks. */}
              <div className="col-12">{CollapsePanel({ isEditMode })}</div>
            </form>
          </FormProvider>

          {/* Hyperlinks Section - Edit Mode */}
          {isEditMode && (
            <div
              className="card"
              style={{
                marginBottom: "24px",
                border: "1px solid #e8e8e8",
                borderRadius: "8px",
              }}
            >
              <div className="card-body" style={{ padding: "20px" }}>
                <div className="d-flex align-items-center mb-3">
                  <Icon
                    name="link"
                    style={{
                      fontSize: "20px",
                      color: "#004293",
                      marginRight: "8px",
                    }}
                  />
                  <h6
                    className="section-header mb-0"
                    style={{ color: "#004293", fontWeight: "600" }}
                  >
                    Hyperlinks
                  </h6>
                </div>
                <div className="row g-3 mt-2">
                  <div className="col-4">
                    <div className="form-group">
                      <label className="form-label">Link Name</label>
                      <div className="form-control-wrap">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter link name"
                          value={currentHyperlinkName}
                          onChange={(e) =>
                            setCurrentHyperlinkName(e.target.value)
                          }
                          style={{ height: "38px" }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="form-group">
                      <label className="form-label">URL</label>
                      <div className="form-control-wrap">
                        <input
                          type="url"
                          className="form-control"
                          placeholder="Enter URL"
                          value={currentHyperlinkUrl}
                          onChange={(e) =>
                            setCurrentHyperlinkUrl(e.target.value)
                          }
                          style={{ height: "38px" }}
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className="col-2 d-flex align-items-center justify-content-start"
                    style={{ marginTop: "45px" }}
                  >
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => {
                        if (
                          !currentHyperlinkName ||
                          !currentHyperlinkUrl ||
                          !currentHyperlinkName.trim() ||
                          !currentHyperlinkUrl.trim()
                        ) {
                          messageApi.error(
                            "Please enter both link name and URL",
                          );
                          return;
                        }

                        // Check if hyperlink already exists
                        if (
                          hyperlinks.some(
                            (h) => h.name === currentHyperlinkName.trim(),
                          )
                        ) {
                          messageApi.error("Link name already exists");
                          return;
                        }

                        setHyperlinks([
                          ...hyperlinks,
                          {
                            name: currentHyperlinkName.trim(),
                            link: currentHyperlinkUrl.trim(),
                          },
                        ]);
                        setCurrentHyperlinkName("");
                        setCurrentHyperlinkUrl("");
                      }}
                      disabled={!isEditMode}
                    >
                      Add Link
                    </Button>
                  </div>
                </div>

                {/* Display added hyperlinks */}
                {hyperlinks.length > 0 && (
                  <div className="mt-3">
                    <Table
                      dataSource={hyperlinks}
                      rowKey="name"
                      pagination={false}
                      size="small"
                      bordered
                      columns={[
                        {
                          title: "Link Name",
                          dataIndex: "name",
                          key: "name",
                          width: 300,
                          align: "center" as const,
                        },
                        {
                          title: "URL",
                          dataIndex: "link",
                          key: "link",
                          width: 400,
                          align: "center" as const,
                          render: (text: string) => (
                            <a
                              href={text}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#004293" }}
                            >
                              {text}
                            </a>
                          ),
                        },
                        {
                          title: "Action",
                          key: "action",
                          width: 100,
                          align: "center" as const,
                          render: (_: any, record: any) => (
                            <em
                              className="icon ni ni-trash"
                              onClick={() => {
                                setHyperlinks(
                                  hyperlinks.filter(
                                    (h) => h.name !== record.name,
                                  ),
                                );
                              }}
                              style={{
                                cursor: "pointer",
                                fontSize: "16px",
                                color: "#ff4d4f",
                                padding: "4px",
                              }}
                            ></em>
                          ),
                        },
                      ]}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rate Analysis & Cost Breakdown (read-only) */}
          {!isEditMode && (
            <RateAnalysisBreakdown task={selectedTaskDetails} />
          )}

          {/* Tools & Machinery View Section */}
          {!isEditMode && (
            <div
              className="card"
              style={{
                marginBottom: "24px",
                border: "1px solid #e8e8e8",
                borderRadius: "8px",
              }}
            >
              <div className="card-body" style={{ padding: "20px" }}>
                <div className="d-flex align-items-center mb-3">
                  <Icon
                    name="building"
                    style={{
                      fontSize: "20px",
                      color: "#004293",
                      marginRight: "8px",
                    }}
                  />
                  <h6
                    className="section-header mb-0"
                    style={{ color: "#004293", fontWeight: "600" }}
                  >
                    Machinery
                  </h6>
                </div>
                <div className="table-responsive poTable">
                  <Table
                    className="customTable"
                    dataSource={selectedTaskDetails?.taskTools || []}
                    rowKey="toolId"
                    pagination={false}
                    size="middle"
                    bordered
                    columns={[
                      {
                        title: "Tool Type",
                        dataIndex: "toolTypeName",
                        key: "toolTypeName",
                        width: 200,
                        align: "center",
                      },
                      {
                        title: "Machinery",
                        dataIndex: "toolName",
                        key: "toolName",
                        width: 200,
                        align: "center",
                      },
                      {
                        title: "Serial Number",
                        dataIndex: "serialNumber",
                        key: "serialNumber",
                        width: 150,
                        align: "center",
                        render: (text: any) => text || "",
                      },
                      {
                        title: "Quantity",
                        dataIndex: "quantity",
                        key: "quantity",
                        width: 120,
                        align: "center",
                        render: (text: any) =>
                          text !== undefined && text !== null ? text : "",
                      },
                      {
                        title: "UOM",
                        dataIndex: "workUomName",
                        key: "workUomName",
                        width: 150,
                        align: "center",
                        render: (text: any) => text || "",
                      },
                      {
                        title: "Rate",
                        dataIndex: "rate",
                        key: "rate",
                        width: 120,
                        align: "center",
                        render: (text: any) =>
                          text !== undefined && text !== null ? text : "",
                      },
                      {
                        title: "Total Amount",
                        dataIndex: "totalAmount",
                        key: "totalAmount",
                        width: 140,
                        align: "center",
                        render: (text: any) =>
                          text !== undefined && text !== null ? text : "",
                      },
                    ]}
                    scroll={{ x: "max-content" }}
                    style={{ minWidth: "600px" }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Hyperlinks View Section */}
          {!isEditMode && (
            <div
              className="card"
              style={{
                marginBottom: "24px",
                border: "1px solid #e8e8e8",
                borderRadius: "8px",
              }}
            >
              <div className="card-body" style={{ padding: "20px" }}>
                <div className="d-flex align-items-center mb-3">
                  <Icon
                    name="link"
                    style={{
                      fontSize: "20px",
                      color: "#004293",
                      marginRight: "8px",
                    }}
                  />
                  <h6
                    className="section-header mb-0"
                    style={{ color: "#004293", fontWeight: "600" }}
                  >
                    Hyperlinks
                  </h6>
                </div>
                <div className="table-responsive poTable">
                  <Table
                    className="customTable"
                    dataSource={selectedTaskDetails?.hyperlinks || []}
                    rowKey="hyperlinkId"
                    pagination={false}
                    size="middle"
                    bordered
                    columns={[
                      {
                        title: "Link Name",
                        dataIndex: "name",
                        key: "name",
                        width: 200,
                        align: "center",
                      },
                      {
                        title: "URL",
                        dataIndex: "link",
                        key: "link",
                        width: 400,
                        align: "center",
                        render: (text: string) => (
                          <a
                            href={text}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#004293" }}
                          >
                            {text}
                          </a>
                        ),
                      },
                    ]}
                    scroll={{ x: "max-content" }}
                    style={{ minWidth: "600px" }}
                  />
                </div>
              </div>
            </div>
          )}

          {!hideInternalTabs && (
            <div className="d-flex align-items-center flex-wrap mb-2 mt-2">
              <ul
                className="nav nav-tabs nav-tabs-card nav-tabs-xs w-100 p-0 m-0 position-relative"
                style={{ flexWrap: "nowrap" }}
              >
                {TabsList?.map((_tab: any) => {
                  // Helper function to render tab with access control
                  const renderTabWithAccess = () => {
                    const AccessComponent =
                      _tab.accessControl === "ViewAccess"
                        ? ViewAccess
                        : _tab.accessControl === "EditAccess"
                          ? EditAccess
                          : _tab.accessControl === "AddAccess"
                            ? AddAccess
                            : _tab.accessControl === "ApproveAccess"
                              ? ApproveAccess
                              : _tab.accessControl === "RejectAccess"
                                ? RejectAccess
                                : _tab.accessControl === "CancelAccess"
                                  ? CancelAccess
                                  : ViewAccess; // Default to ViewAccess

                    return (
                      <AccessComponent moduleName={_tab.moduleName || "Tasks"}>
                        <li
                          className="nav-item"
                          key={_tab?.tabKey}
                          onClick={() => handleTabChange(_tab?.tabKey)}
                        >
                          <a
                            onClick={(ev) => {
                              ev.preventDefault();
                            }}
                            className={`nav-link${
                              activeTab === _tab?.tabKey ? " active" : ""
                            }`}
                            href={"#" + _tab?.tabKey}
                            style={{ cursor: "pointer" }}
                          >
                            {_tab?.label}
                          </a>
                        </li>
                      </AccessComponent>
                    );
                  };

                  return renderTabWithAccess();
                })}
              </ul>

              <div className="tab-content mt-2 w-100">
                <div
                  key={"comments"}
                  className={`tab-pane${
                    activeTab === "comments" ? " active" : " "
                  }`}
                  style={{
                    display: activeTab === "comments" ? "block" : "none",
                    transition: "opacity 0.3s ease-in-out",
                    opacity: activeTab === "comments" ? 1 : 0,
                  }}
                >
                  {loadedTabs.has("comments") && (
                    <CommentsTab
                      module="Task"
                      moduleId={selectedTaskDetails?.taskId}
                      projectId={selectedTaskDetails?.projectId}
                      projectName={selectedTaskDetails?.projectName}
                      name={selectedTaskDetails?.taskName}
                      showTaskModal={false}
                      isFromWorkUI={isFromWorkUI}
                    />
                  )}
                </div>
                <div
                  key={"SubTasks"}
                  className={`tab-pane${
                    activeTab === "SubTasks" ? " active" : " "
                  }`}
                  style={{
                    display: activeTab === "SubTasks" ? "block" : "none",
                    transition: "opacity 0.3s ease-in-out",
                    opacity: activeTab === "SubTasks" ? 1 : 0,
                  }}
                >
                  {loadedTabs.has("SubTasks") && (
                    <SubTaskList
                      isActive={activeTab === "SubTasks"}
                      isFromWorkUI={isFromWorkUI}
                    />
                  )}
                </div>
                <div
                  key={"Loghours"}
                  className={`tab-pane${
                    activeTab === "Loghours" ? " active" : " "
                  }`}
                  style={{ width: "100%" }}
                >
                  {loadedTabs.has("Loghours") && (
                    <TimeLogTable
                      projectId={selectedTaskDetails?.projectId}
                      taskId={selectedTaskDetails?.taskId || null}
                      // isApprover={isApprover}
                      activeTab={activeTab}
                      isFromWorkUI={isFromWorkUI}
                      isFromSpecificLevel={true}
                      taskOwnerId={selectedTaskDetails?.taskOwnerId}
                      taskOwnerName={selectedTaskDetails?.taskOwnerName}
                      onTaskCompletionUpdate={fetchTaskById}
                      refreshParentTask={fetchTaskById}
                    />
                  )}
                </div>
                <div
                  key={"issues"}
                  className={`tab-pane${
                    activeTab === "issues" ? " active" : " "
                  }`}
                  style={{ width: "100%", padding: "0", margin: "0" }}
                >
                  {loadedTabs.has("issues") && (
                    <IssuesTable
                      projectId={selectedTaskDetails?.projectId}
                      taskId={selectedTaskDetails?.taskId}
                      activeTab={activeTab}
                      isFromWorkUI={isFromWorkUI}
                      isFromSpecificLevel={true}
                    />
                  )}
                </div>
                <div
                  key={"Dependencies"}
                  className={`tab-pane${
                    activeTab === "Dependencies" ? " active" : " "
                  }`}
                  style={{
                    display: activeTab === "Dependencies" ? "block" : "none",
                    transition: "opacity 0.3s ease-in-out",
                    opacity: activeTab === "Dependencies" ? 1 : 0,
                  }}
                >
                  {loadedTabs.has("Dependencies") && (
                    <TaskDependencies
                      loadOnRefresh={activeTab}
                      taskId={selectedTaskDetails?.taskId}
                      isFromWorkUI={isFromWorkUI}
                      projectId={projectId ?? undefined}
                      derivedProjectStatus={derivedProjectStatus}
                    />
                  )}
                </div>
                <div
                  key={"ChecklistResponse"}
                  className={`tab-pane${
                    activeTab === "ChecklistResponse" ? " active" : " "
                  }`}
                  style={{
                    display:
                      activeTab === "ChecklistResponse" ? "block" : "none",
                    transition: "opacity 0.3s ease-in-out",
                    opacity: activeTab === "ChecklistResponse" ? 1 : 0,
                  }}
                >
                  {loadedTabs.has("ChecklistResponse") && (
                    <TaskCheckListView
                      checklistResponses={
                        selectedTaskDetails?.checklistLinks || []
                      }
                      task={selectedTaskDetails}
                      refreshData={fetchTaskById}
                      // isApprover={isApprover}
                      isFromWorkUI={isFromWorkUI}
                    />
                  )}
                </div>
                <div
                  key={"Documents"}
                  className={`tab-pane${
                    activeTab === "Documents" ? " active" : " "
                  }`}
                  style={{
                    display: activeTab === "Documents" ? "block" : "none",
                    transition: "opacity 0.3s ease-in-out",
                    opacity: activeTab === "Documents" ? 1 : 0,
                  }}
                >
                  {loadedTabs.has("Documents") && (
                    <DocumentsTabWrapper
                      linkType="task"
                      shouldLoadData={activeTab === "Documents"}
                      isFromWorkUI={isFromWorkUI}
                    />
                  )}
                </div>
                <div
                  key={"StatusTimeLine"}
                  className={`tab-pane${
                    activeTab === "StatusTimeLine" ? " active" : ""
                  }`}
                  style={{
                    display: activeTab === "StatusTimeLine" ? "block" : "none",
                    transition: "opacity 0.3s ease-in-out",
                    opacity: activeTab === "StatusTimeLine" ? 1 : 0,
                  }}
                >
                  {loadedTabs.has("StatusTimeLine") && (
                    <TaskStatusTimeLine
                      isActive={activeTab === "StatusTimeLine"}
                    />
                  )}
                </div>

                <div
                  key={"ActivityStream"}
                  className={`tab-pane${
                    activeTab === "ActivityStream" ? " active" : ""
                  }`}
                  style={{
                    display: activeTab === "ActivityStream" ? "block" : "none",
                    transition: "opacity 0.3s ease-in-out",
                    opacity: activeTab === "ActivityStream" ? 1 : 0,
                  }}
                >
                  {loadedTabs.has("ActivityStream") && (
                    <TaskActivityStreamLine activeTab={activeTab} />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Checklist Modal for status change */}
          <TaskCheckListCreateModal
            open={showChecklistModal}
            checklistId={selectedChecklistForModal?.checklistId}
            checklistLinkId={selectedChecklistForModal?.checklistLinkId}
            onClose={() => setShowChecklistModal(false)}
            onSave={(success: boolean | undefined) =>
              handleChecklistModalClose("save", success)
            }
          />
        </div>

        {/* Checklist Modal for status change */}
        <TaskCheckListCreateModal
          open={showChecklistModal}
          checklistId={selectedChecklistForModal?.checklistId}
          checklistLinkId={selectedChecklistForModal?.checklistLinkId}
          onClose={() => setShowChecklistModal(false)}
          onSave={(success: boolean | undefined) =>
            handleChecklistModalClose("save", success)
          }
        />

        {/* // Add the RejectModal component */}
        <RejectModal
          isModalVisible={rejectModalVisible}
          setIsModalVisible={setRejectModalVisible}
          onReject={(reason) => handleApproveReject("reject", reason)}
          title={`Reject Approval: ${selectedTaskDetails?.entityName || ""}`}
          label="Reason for rejection"
          isLoading={isRejecting}
        />

        {/* Executed Rate Modal for task closure */}
        <Modal
          title="Rate Confirmation"
          open={executedRateModalVisible}
          onOk={handleExecutedRateSubmit}
          onCancel={async () => {
            setExecutedRateModalVisible(false);
            setPendingClosureStatus(null);
            setExecutedRate(null);
            setRateRemarks("");
            setExecutedRateModalCancelled(true);
            // Refresh task data to get updated checklist status
            await fetchTaskById();
            // Don't change status when user cancels the executed rate modal
            // The checklist submission should not trigger status change
          }}
          okText="Submit"
          cancelText="Cancel"
          okButtonProps={{ type: "primary" }}
          width={500}
        >
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <label
                style={{ display: "block", marginBottom: 8, fontWeight: 500 }}
              >
                <span style={{ color: "#666" }}>Planned Task Rate:</span>{" "}
                <span style={{ color: "#2557d6", fontWeight: 600 }}>
                  {selectedTaskDetails?.rate || 0}{" "}
                  {(() => {
                    const selectedUom = workUomsList.find(
                      (uom: any) =>
                        uom.value === selectedTaskDetails?.workUomId,
                    );
                    return selectedUom ? `per ${selectedUom.label}` : "";
                  })()}
                </span>
              </label>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label
                style={{ display: "block", marginBottom: 8, fontWeight: 500 }}
              >
                <span style={{ color: "#666" }}>Executed Rate</span>{" "}
                <span style={{ color: "red" }}>*</span>
              </label>
              <Input
                style={{ width: "100%" }}
                placeholder="Enter the actual rate at which task was executed"
                type="number"
                min={0}
                step={0.01}
                value={executedRate !== null ? executedRate.toString() : ""}
                onChange={(e) => {
                  const value = e.target.value ? Number(e.target.value) : null;
                  setExecutedRate(value);
                  // Clear remarks if rate becomes same as task rate
                  if (
                    value !== null &&
                    Number(value) === (selectedTaskDetails?.rate || 0)
                  ) {
                    setRateRemarks("");
                  }
                }}
              />
            </div>
            {executedRate !== null &&
              Number(executedRate) !== (selectedTaskDetails?.rate || 0) && (
                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 8,
                      fontWeight: 500,
                    }}
                  >
                    <span style={{ color: "#666" }}>Remarks</span>{" "}
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <Input.TextArea
                    placeholder="Please explain why the executed rate differs from the planned task rate"
                    rows={3}
                    style={{ width: "100%" }}
                    value={rateRemarks}
                    onChange={(e) => setRateRemarks(e.target.value)}
                  />
                </div>
              )}
          </div>
        </Modal>

        {/* Approval Hierarchy Drawer */}
        {approvals.open && (
          <IssueApprovalDrawer
            open={approvals.open}
            projectId={projectId || 0}
            excludeUserId={
              selectedOwnerId ? Number(selectedOwnerId) : undefined
            }
            onClose={() => {
              setApprovals((pre) => ({
                ...pre,
                open: false,
              }));
            }}
            onSubmit={(data: any) => {
              setApprovals({
                open: false,
                data: data,
              });
            }}
            setApprovalSubmissionStatus={(status: boolean) => {}}
            approvalData={approvals.data}
          />
        )}
      </div>
    </>
  );
};

export default TaskInformationById;
